// Central Zustand store for the dashboard.
//
// Owns: companies list, active company, per-company message history,
// per-company queue, session lifecycle status, composer draft +
// attachments, modal open-state, thinking accordion open-state per msg.
//
// All state mutations are local; backend writes happen via store/api.ts.
// Streaming responses come in via store/sse.ts events that this store
// reduces into messages.

import { create } from "zustand";
import { parseNativeInputSnapshot, mergeNativeInputSnapshot, nativeInputMessages, type NativeInputSnapshot } from "./nativeInputs";
import { markStopped } from "../lib/stopMarks";
import { reconcileApprovalProgress, type ApprovalTransport } from "../lib/revisionApprovalProgress";
import { chatNavigationBusy, confirmChatSwitch, readChatLink, writeChatLink, type ChatHistoryMode } from "../lib/chatLinks";

// A late history response must not reconnect a chat the user has left (A→B→A too).
let chatSelectionVersion = 0;

/* Sidebar flags (9 Oct 2026). A company object from an older backend (or a route
   that does not send the flags) keeps the pin / archive the list already knows. */
let _activitySeq = 0;
let _flagOpSeq = 0;
const _flagPending = new Map<string, {
  tail: Promise<unknown>; latest: number; confirmed: { archivedAt?: string | null; pinnedAt?: string | null };
}>();
/** Mark as unread sent from this device, until a poll shows it (id → when). */
const _manualUnread = new Map<string, number>();
/** unreadByCompany value for a chat marked unread by hand (C9, 9 Oct, as Build add4b7c): truthy
 *  (the dot, the blue time) but older than any activity, so lastActivityMs ignores it and the
 *  row keeps its place, its section and its time. A real unread reply keeps its reply time. */
export const MANUAL_UNREAD = 1;
function withFlags(chat: Company, archivedAt: string | null | undefined, pinnedAt: string | null | undefined): Company {
  if ((chat.archivedAt || null) === (archivedAt || null) && (chat.pinnedAt || null) === (pinnedAt || null)) return chat;
  return { ...chat, archivedAt: archivedAt || null, pinnedAt: pinnedAt || null };
}
/** A copy of `map` without `key` (the same object when the key is absent). */
function withoutKey<T>(map: Record<string, T>, key: string): Record<string, T> {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
}
/** Replace a company with the server's copy, keeping flags it does not carry (or that are mid-write). */
export function keepFlags(prev: Company, next: Company): Company {
  if (!next || typeof next !== "object") return next;
  if (_flagPending.has(prev.id)) return { ...next, archivedAt: prev.archivedAt ?? null, pinnedAt: prev.pinnedAt ?? null };
  const out = { ...next };
  if (!("archivedAt" in next)) out.archivedAt = prev.archivedAt ?? null;
  if (!("pinnedAt" in next)) out.pinnedAt = prev.pinnedAt ?? null;
  return out;
}
import type {
  AttachItem,
  AttentionSummary,
  Company,
  Followup,
  LightboxItem,
  SessionStatus,
  UIMessage,
  CompactionState,
} from "../lib/types";
import {
  __mock,
  addCompany as apiAddCompany,
  addSample as apiAddSample,
  clearChatHistory as apiClearChatHistory,
  deleteCompany as apiDeleteCompany,
  getAllSessionStatuses,
  lastPolledUnread,
  lastPolledManualUnread,
  markChatRead as apiMarkChatRead,
  markChatUnread as apiMarkChatUnread,
  getAttention,
  getCompanies,
  getMessageHistory,
  getMetrics,
  getSessionStatus,
  interruptCurrentTurn,
  cancelQueuedMessage,
  editQueuedMessage,
  holdQueuedMessage,
  sendNativeInputNow,
  compactSession as apiCompactSession,
  getCompactionState,
  CompactRefusedError,
  killActiveSession,
  postMessage,
  renameCompany as apiRenameCompany,
  restartCompanySession,
  revealCompanyKey,
  rotateKey as apiRotateKey,
  changeDomain as apiChangeDomain,
  runRevision as apiRunRevision,
  dismissRevision as apiDismissRevision,
  setRevisionsEnabled as apiSetRevisionsEnabled,
  setAutoRevision as apiSetAutoRevision,
  setAutomationFreeze as apiSetAutomationFreeze,
  setEffort as apiSetEffort,
  promoteToClient as apiPromoteToClient,
  getGlobalSettings as apiGetGlobalSettings,
  setSampleDefaultEffort as apiSetSampleDefaultEffort,
  setClientDefaultEffort as apiSetClientDefaultEffort,
  setAutoRevisionDefaultEffort as apiSetAutoRevisionDefaultEffort,
  setAutoRevisionEffort as apiSetAutoRevisionEffort,
  spawnSession,
  uploadFile,
  getFollowups as apiGetFollowups,
  createFollowup as apiCreateFollowup,
  clearFinishedFollowups as apiClearFinishedFollowups,
  cancelFollowup as apiCancelFollowup,
  runFollowupNow as apiRunFollowupNow,
  getMessageStatus,
  getChatActivity as apiGetChatActivity,
  setChatFlag as apiSetChatFlag,
} from "./api";
import type { ChatActivity } from "../lib/sidebarModel";
import {
  LIVE_CONNECTING_MESSAGE,
  LIVE_DISCONNECTED_MESSAGE,
  createDisconnectNotice,
  isSendUnconfirmed,
  isStartRefused,
  DELIVERY_FAILED_REASON,
  droppedServerQueued,
  hideQueuedDuplicates,
  QUEUE_LOST_REASON,
  TOO_LONG_REASON,
  UNCONFIRMED_REASON,
  MAX_AUTO_ATTEMPTS,
  OUTBOX_MAX_TEXT,
  outboxBubbles,
  planRecovery,
  readOutbox,
  reconcileQueue,
  removeFromOutbox,
  upsertOutbox,
  writeOutbox,
  type OutboxEntry,
  type ServerQueuedItem,
  type StorageLike,
} from "./delivery";
import {
  abortMockStream,
  connectStream,
  emitMockReply,
  type StreamEvent,
  type StreamSubscription,
} from "./sse";
import { admitScheduledTurn } from "./scheduledTurnAdmission";
import { compressImage } from "../lib/compression";
import { getCurrentIdToken } from "../auth-context";
import { markFirstViewReady } from "../lib/bootLoader";
import {
  copyPromisedTextToClipboard,
  copyTextToClipboard,
} from "../lib/clipboard";

/** Keep failed, never-accepted user bubbles local until the operator retries.
 * History proves acceptance by immutable user ID, never by matching text.
 * Preserve server order and the original failed message/file objects; this
 * display-only merge never sends, persists an outbox or changes the composer. */
function mergeFailedLocalMessages(
  history: UIMessage[],
  local: UIMessage[],
  failed: SessionState["failedUserMsgIds"],
  /** Also keep these local user bubbles (sends not confirmed yet). */
  keep: Record<string, unknown> = {},
): { messages: UIMessage[]; failedUserMsgIds: SessionState["failedUserMsgIds"] } {
  let nextFailed = failed;
  for (const message of history) {
    if (message.role !== "user") continue;
    if (message.meta?.deliveryFailed === true) {
      // Saved, but the agent never received it: it stays visibly failed with a
      // same-id retry, never a normal "sent" bubble. A retry in progress wins.
      if (!Object.hasOwn(nextFailed, message.id) && !Object.hasOwn(keep, message.id)) {
        if (nextFailed === failed) nextFailed = { ...failed };
        nextFailed[message.id] = { reason: DELIVERY_FAILED_REASON, failedAt: Date.parse(message.meta.deliveryFailedAt ?? "") || Date.now(), retry: "resend" };
      }
      continue;
    }
    if (Object.hasOwn(nextFailed, message.id)) {
      if (nextFailed === failed) nextFailed = { ...failed };
      delete nextFailed[message.id];
    }
  }
  const present = new Set(history.map((message) => message.id));
  // A following message already known in both views is the strongest ordering
  // anchor. For a newly discovered reply, fall back to creation time.
  const nextAnchors: (string | null)[] = new Array(local.length);
  let nextAnchor: string | null = null;
  for (let i = local.length - 1; i >= 0; i--) {
    nextAnchors[i] = nextAnchor;
    if (present.has(local[i]!.id)) nextAnchor = local[i]!.id;
  }
  let messages = history;
  let afterPreviousFailed = 0;
  for (let i = 0; i < local.length; i++) {
    const message = local[i]!;
    if (message.role !== "user" || present.has(message.id) ||
        (!Object.hasOwn(nextFailed, message.id) && !Object.hasOwn(keep, message.id))) continue;
    if (messages === history) messages = [...history];
    let at = nextAnchors[i] ? messages.findIndex((item) => item.id === nextAnchors[i]) : -1;
    if (at < 0) {
      const createdAt = Date.parse(message.createdAt);
      if (Number.isFinite(createdAt)) at = messages.findIndex((item) => Date.parse(item.createdAt) > createdAt);
    }
    if (at < 0) at = messages.length;
    // Retain local order for equal/missing timestamps without reordering any
    // durable server messages or inserting a duplicate on the next refresh.
    at = Math.max(at, afterPreviousFailed);
    messages.splice(at, 0, message);
    afterPreviousFailed = at + 1;
    present.add(message.id);
  }
  return { messages, failedUserMsgIds: nextFailed };
}

export { DELIVERY_FAILED_REASON, UNCONFIRMED_REASON, QUEUE_LOST_REASON, TOO_LONG_REASON };

/* ----- Delivery: per-chat send guard, outbox, confirmation -----
 * sendAdmissionByCompany (ported from Build): one send per chat at a time, so a
 * double tap during a cold start cannot POST the same draft twice. The outbox
 * keeps every unconfirmed send (localStorage, 24 h) so a reload or app kill
 * mid-send is recovered with the SAME userMsgId; the server dedupes on it. */
const sendAdmissionByCompany = new Set<string>();
// Stop/Clear can finish while session preparation has no message ID yet.
// Fence that preparation without consuming its draft or attachments.
const sendAdmissionEpoch = new Map<string, number>();

/** Admission is synchronous; transport waits for exact predecessor receipts.
 * The dependency lives in the existing outbox so reload cannot bypass it. */
const orderedSendOwners = new Set<string>();

/** Clear cannot know an HTTP request that has not reached the server yet.
 * Fence every captured ID before asking it to erase history. Keep barriers
 * and receipts on any uncertainty so an explicit Clear retry can finish safely. */
async function confirmClearSendOrder(cid: string): Promise<void> {
  const entries = sendOrderEntries(cid);
  for (const entry of entries) outboxSave({ ...entry, attempts: MAX_AUTO_ATTEMPTS, orderStopPending: true, orderReleased: false });
  const proofs = await Promise.all(entries.map(async entry => {
    try {
      const result = await cancelQueuedMessage(cid, entry.userMsgId);
      if (result.cancelled === true || result.removed === true) return true;
      // Already accepted is safe only BEFORE the following atomic Clear.
      const status = await getMessageStatus(cid, entry.userMsgId);
      return ["delivered", "queued", "removed"].includes(status);
    } catch { return false; }
  }));
  if (proofs.some(proven => !proven))
    throw new Error("Earlier sends could not be cancelled or confirmed. History was not cleared. Try Clear again when connected.");
}

/** Tombstone captured IDs before interrupt, so a delayed initial POST cannot
 * become an old request in a fresh post-Stop turn. Unproved IDs remain barriers. */
async function retireStoppedSendOrder(cid: string, entries: OutboxEntry[], queued: Set<string>): Promise<boolean> {
  for (const entry of entries) outboxSave({ ...entry,
    attempts: MAX_AUTO_ATTEMPTS, orderStopPending: true, orderReleased: false });
  let pending = false;
  await Promise.all(entries.map(async entry => {
    let proven = false;
    try {
      const result = await cancelQueuedMessage(cid, entry.userMsgId);
      proven = result.cancelled === true || result.removed === true;
      if (!proven) {
        const status = await getMessageStatus(cid, entry.userMsgId);
        proven = ["delivered", "queued", "removed"].includes(status);
      }
    } catch { /* No proof: keep the existing ID manual-only and ordered. */ }
    if (!proven) { pending = true; return; }
    settleSendOrder(cid, entry.userMsgId);
    if (queued.has(entry.userMsgId)) outboxDrop(entry.userMsgId);
    else patchSendOrder(cid, entry.userMsgId, { orderStopPending: false, orderReleased: true, predecessorId: undefined });
  }));
  return pending;
}

const orderedSendPauses = new Map<string, { promise: Promise<void>; resolve: () => void; count: number }>();
function pauseOrderedSends(cid: string): () => void {
  let pause = orderedSendPauses.get(cid);
  if (!pause) {
    let resolve!: () => void;
    const promise = new Promise<void>(done => { resolve = done; });
    pause = { promise, resolve, count: 0 }; orderedSendPauses.set(cid, pause);
  }
  pause.count++;
  const owned = pause;
  return () => { if (--owned.count === 0) { orderedSendPauses.delete(cid); owned.resolve(); } };
}

async function canRecoverSendOrder(cid: string, id: string): Promise<boolean> {
  if (orderedSendPauses.has(cid)) return false;
  const entry = sendOrderEntry(cid, id);
  if (entry?.orderStopPending) {
    try {
      const status = await getMessageStatus(cid, id);
      if (status === "removed") {
        settleSendOrder(cid, id);
        patchSendOrder(cid, id, { orderStopPending: false, orderReleased: true, predecessorId: undefined });
      }
    } catch { /* Retain Stop's unresolved delivery barrier. */ }
    return false;
  }
  if (!entry?.predecessorId) return !orderedSendOwners.has(id);
  if (sendOrderEntry(cid, entry.predecessorId)?.orderStopPending) return false;
  try {
    const status = await getMessageStatus(cid, entry.predecessorId);
    if (!["delivered", "queued", "removed"].includes(status)) return false;
    settleSendOrder(cid, entry.predecessorId);
    return !orderedSendOwners.has(id) && !sendOrderEntry(cid, id)?.predecessorId;
  } catch { return false; }
}

const orderedSendSignals = new Map<string, { promise: Promise<void>; resolve: () => void }>();
function sendOrderEntries(cid: string): OutboxEntry[] {
  return outboxAll().filter(entry => entry.cid === cid);
}
function sendOrderEntry(cid: string, id: string): OutboxEntry | undefined {
  const entry = outboxFind(id); return entry?.cid === cid ? entry : undefined;
}
function patchSendOrder(cid: string, id: string, patch: Partial<OutboxEntry>): void {
  const entry = outboxFind(id); if (entry?.cid === cid) outboxSave({ ...entry, ...patch });
}
function captureSendOrder(cid: string): Pick<OutboxEntry, "predecessorId" | "waitingForSend"> {
  const preceding = sendOrderEntries(cid).filter(entry => entry.state !== "queued" && !entry.orderReleased).at(-1);
  return { predecessorId: preceding?.userMsgId, waitingForSend: !!preceding };
}
function settleSendOrder(cid: string, id: string): void {
  // Removing a not-yet-posted middle entry splices, not bypasses, its predecessor.
  const prior = sendOrderEntry(cid, id)?.predecessorId;
  for (const entry of sendOrderEntries(cid)) if (entry.predecessorId === id)
    patchSendOrder(cid, entry.userMsgId, { predecessorId: prior });
  const key = cid + ":" + id;
  orderedSendSignals.get(key)?.resolve();
  orderedSendSignals.delete(key);
}
async function awaitSendOrder(cid: string, id: string): Promise<boolean> {
  orderedSendOwners.add(id);
  for (;;) {
    const pause = orderedSendPauses.get(cid);
    if (pause) { await pause.promise; continue; }
    const entry = sendOrderEntry(cid, id);
    if (!entry || entry.orderStopPending) { orderedSendOwners.delete(id); return false; }
    if (!entry.predecessorId) {
      markSendOrderPosting(cid, id);
      return true;
    }
    const key = cid + ":" + entry.predecessorId;
    let signal = orderedSendSignals.get(key);
    if (!signal) {
      let resolve!: () => void;
      const promise = new Promise<void>(done => { resolve = done; });
      signal = { promise, resolve }; orderedSendSignals.set(key, signal);
    }
    const ownKey = cid + ":" + id;
    let own = orderedSendSignals.get(ownKey);
    if (!own) {
      let resolve!: () => void;
      const promise = new Promise<void>(done => { resolve = done; });
      own = { promise, resolve }; orderedSendSignals.set(ownKey, own);
    }
    await Promise.race([signal.promise, own.promise]);
  }
}

function markSendOrderPosting(cid: string, id: string): void {
  patchSendOrder(cid, id, { waitingForSend: false });
  useSession.setState(s => ({ queueByCompany: { ...s.queueByCompany,
    [cid]: (s.queueByCompany[cid] ?? []).map(q => q.userMsgId === id ? { ...q, waitingForSend: false } : q) } }));
}

const resendInFlight = new Set<string>();
/** First POSTs still awaiting their answer: confirmation leaves them alone. */
const postingIds = new Set<string>();
let confirmTimer: ReturnType<typeof setTimeout> | null = null;
let confirmRunning = false;
let confirmAgain = false;
let confirmBackoffMs = 2_000;
function outboxStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
/** Full copies of this tab's entries that storage could not hold (too long, or
 *  storage refused the write). Read-only overlay: never written back over
 *  storage, so it cannot overwrite other tabs' entries. */
const memOutbox = new Map<string, OutboxEntry>();
/** Ids this tab removed on purpose (cancel, Stop, Clear/delete chat): never
 *  resent, and their pending checks are dropped silently. */
const locallyRemoved = new Set<string>();
function storedOutbox(): OutboxEntry[] {
  return readOutbox(outboxStorage(), Date.now());
}
function outboxAll(): OutboxEntry[] {
  const stored = storedOutbox();
  if (memOutbox.size === 0) return stored;
  const ids = new Set(stored.map((e) => e.userMsgId));
  return [...stored.map((e) => memOutbox.get(e.userMsgId) ?? e),
    ...[...memOutbox.values()].filter((e) => !ids.has(e.userMsgId))];
}
function outboxFind(userMsgId: string): OutboxEntry | undefined {
  return memOutbox.get(userMsgId) ?? storedOutbox().find((e) => e.userMsgId === userMsgId);
}
/** Read-modify-write against storage on every change (other tabs' entries are
 *  kept). A message storage cannot hold in full stays in memory for this tab. */
function outboxSave(entry: OutboxEntry): void {
  const ok = writeOutbox(outboxStorage(), upsertOutbox(storedOutbox(), entry));
  if (!ok || entry.text.length > OUTBOX_MAX_TEXT) memOutbox.set(entry.userMsgId, entry);
  else memOutbox.delete(entry.userMsgId);
}
function outboxDrop(userMsgId: string): void {
  const entry = outboxFind(userMsgId);
  if (entry) settleSendOrder(entry.cid, userMsgId);
  memOutbox.delete(userMsgId);
  const all = storedOutbox();
  const next = removeFromOutbox(all, userMsgId);
  if (next !== all) writeOutbox(outboxStorage(), next);
}
/** Julian removed these sends (cancel, Stop, Clear/delete chat): forget them
 *  for good on this device — no outbox entry, no pending check, no resend. */
function forgetRemovedSends(set: DeliverySet, userMsgIds: Iterable<string>): void {
  const ids = [...userMsgIds];
  if (ids.length === 0) return;
  for (const id of ids) { locallyRemoved.add(id); outboxDrop(id); }
  set((s) => {
    const unconfirmed = { ...s.unconfirmedUserMsgIds };
    const failed = { ...s.failedUserMsgIds };
    for (const id of ids) { delete unconfirmed[id]; delete failed[id]; }
    return { unconfirmedUserMsgIds: unconfirmed, failedUserMsgIds: failed };
  });
}
/** Clear chat / delete chat: forget every send of this chat on this device. */
function forgetChatSends(get: DeliveryGet, set: DeliverySet, cid: string, capturedIds?: ReadonlySet<string>): void {
  if (capturedIds) { forgetRemovedSends(set, capturedIds); return; }
  const s = get();
  const ids = new Set<string>();
  for (const e of outboxAll()) if (e.cid === cid) ids.add(e.userMsgId);
  for (const [id, u] of Object.entries(s.unconfirmedUserMsgIds)) if (u.cid === cid) ids.add(id);
  for (const m of s.messagesByCompany[cid] ?? []) if (m.role === "user" && (Object.hasOwn(s.failedUserMsgIds, m.id) || Object.hasOwn(s.unconfirmedUserMsgIds, m.id))) ids.add(m.id);
  for (const q of s.queueByCompany[cid] ?? []) if (q.userMsgId) ids.add(q.userMsgId);
  forgetRemovedSends(set, ids);
}
/** The server says Julian removed this send: drop its bubble, chip, markers
 *  and outbox entry silently (never delivered, never resent). */
function dropRemovedSend(set: DeliverySet, cid: string, userMsgId: string): void {
  forgetRemovedSends(set, [userMsgId]);
  set((s) => {
    const msgs = s.messagesByCompany[cid] ?? [];
    const queue = s.queueByCompany[cid] ?? [];
    const next: Partial<SessionState> = {};
    if (msgs.some((m) => m.id === userMsgId && m.role === "user"))
      next.messagesByCompany = { ...s.messagesByCompany, [cid]: msgs.filter((m) => !(m.id === userMsgId && m.role === "user")) };
    if (queue.some((q) => q.userMsgId === userMsgId && !q.dispatching))
      next.queueByCompany = { ...s.queueByCompany, [cid]: queue.filter((q) => !(q.userMsgId === userMsgId && !q.dispatching)) };
    return next;
  });
}
/** Chips the server had confirmed queued that a rebuild or an error dropped:
 *  keep an outbox "queued" entry so the status check can tell a drained
 *  message (delivered) from one the server lost ("Not delivered — tap to
 *  resend"). Never resent automatically. */
function rememberDroppedQueued(get: DeliveryGet, cid: string, chips: QueuedMessage[]): void {
  let any = false;
  for (const q of chips) {
    if (!q.userMsgId || locallyRemoved.has(q.userMsgId)) continue;
    const existing = outboxFind(q.userMsgId);
    if (existing && existing.state !== "sending") continue;
    outboxSave({ cid, userMsgId: q.userMsgId, text: existing?.text ?? q.text, attachmentPaths: q.attachmentPaths,
      userAttachments: q.userAttachments, notify: existing?.notify ?? false,
      createdAt: existing?.createdAt ?? Date.now(), attempts: existing?.attempts ?? 1, state: "queued" });
    any = true;
  }
  if (any) scheduleConfirm(get, 800);
}
/** The server confirmed this send queued: remember it until delivered. */
function outboxMarkQueued(userMsgId: string, fallback: Omit<OutboxEntry, "state" | "attempts" | "createdAt">): void {
  const existing = outboxFind(userMsgId);
  outboxSave({ ...fallback, ...(existing ?? {}), createdAt: existing?.createdAt ?? Date.now(),
    attempts: existing?.attempts ?? 1, state: "queued" });
}
function attachmentsFromPaths(paths: string[]): UIMessage["userAttachments"] {
  return paths.map((p) => {
    const filename = p.split("/").pop() ?? p;
    const ext = filename.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? "";
    const kind: "image" | "pdf" | "file" =
      ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif"].includes(ext) ? "image" : ext === "pdf" ? "pdf" : "file";
    return { serverPath: p, filename, kind };
  });
}
function queuedFromServer(item: ServerQueuedItem): QueuedMessage {
  return {
    id: `queued-${item.userMsgId}`,
    text: item.text,
    attachmentPaths: item.files,
    serverQueued: true,
    userMsgId: item.userMsgId,
    userAttachments: item.files.length ? attachmentsFromPaths(item.files) : undefined,
  };
}
type DeliveryGet = () => SessionState;
type DeliverySet = (fn: (s: SessionState) => Partial<SessionState> | SessionState) => void;
/** Ask the server about unconfirmed sends after `ms` (one pending timer). */
function scheduleConfirm(get: DeliveryGet, ms = 2_000): void {
  if (confirmTimer) clearTimeout(confirmTimer);
  confirmTimer = setTimeout(() => {
    confirmTimer = null;
    void get().confirmPendingSends();
  }, ms);
}
/** Show one send as failed: a visible user bubble with a red marker. With
 *  retry "resend", tapping it re-POSTs the SAME id. */
function markSendFailed(
  set: DeliverySet,
  cid: string,
  userMsgId: string,
  text: string,
  userAttachments: UIMessage["userAttachments"] | undefined,
  reason: string,
  retry: "resend" | undefined,
): void {
  set((s) => {
    const unconfirmed = { ...s.unconfirmedUserMsgIds }; delete unconfirmed[userMsgId];
    const next: Partial<SessionState> = {
      unconfirmedUserMsgIds: unconfirmed,
      failedUserMsgIds: { ...s.failedUserMsgIds, [userMsgId]: { reason, failedAt: Date.now(), ...(retry ? { retry } : {}) } },
    };
    // A queued send that never reached the server: its chip becomes a failed
    // bubble (at the end), so it can be retried instead of silently vanishing.
    const localQueue = s.queueByCompany[cid] ?? [];
    const chip = localQueue.find((q) => q.userMsgId === userMsgId && !q.dispatching);
    if (chip) next.queueByCompany = { ...s.queueByCompany, [cid]: localQueue.filter((q) => q !== chip) };
    const msgs = s.messagesByCompany[cid] ?? [];
    if (!msgs.some((m) => m.id === userMsgId)) {
      next.messagesByCompany = { ...s.messagesByCompany, [cid]: [...msgs,
        { id: userMsgId, role: "user" as const, content: text, createdAt: nowIso(), userAttachments: userAttachments ?? chip?.userAttachments }] };
    }
    return next;
  });
}
// "Live updates disconnected" only after 8 s down while visible; cleared on
// ANY (re)connect, including a new stream opened by a resync.
let disconnectNotice: ReturnType<typeof createDisconnectNotice> | null = null;
function liveNotice(get: DeliveryGet) {
  if (disconnectNotice) return disconnectNotice;
  const isVisible = () => typeof document === "undefined" || document.visibilityState === "visible";
  disconnectNotice = createDisconnectNotice({
    isVisible,
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
    show: (message) => get().setToast(message, "info"),
    clear: () => {
      const text = get().toast?.text;
      if (text === LIVE_DISCONNECTED_MESSAGE || text === LIVE_CONNECTING_MESSAGE) get().clearToast();
    },
  });
  const notice = disconnectNotice;
  try {
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", () => notice.visibilityChanged());
  } catch { /* no DOM (tests) */ }
  return notice;
}

interface QueuedMessage {
  waitingForSend?: boolean;
  id: string;
  text: string;
  attachmentPaths: string[];
  /**
   * True when this message has already been POSTed to the backend (which
   * server-side-enqueues when claude is busy). Means the message is
   * DURABLE — survives PWA close, WiFi drop, phone off. Drain logic
   * skips the re-POST and just synthesizes the next-turn placeholder
   * so SSE events from the backend's auto-drain land somewhere visible.
   */
  serverQueued?: boolean;
  /**
   * Stable link back to the optimistically-inserted user bubble in
   * messagesByCompany. Used by the queued-badge in UserBubble to mark
   * the EXACT bubble that's still waiting (matching by text alone
   * misattributes when the user sends the same word twice — "ok" twice
   * would light up both bubbles as queued).
   */
  userMsgId?: string;
  /**
   * True once the previous turn's `done` event fired and we synthesized a
   * placeholder for this message's response — i.e. backend is dispatching
   * it to claude RIGHT NOW. The chip stays visible (with "sending…" text
   * instead of "queued") until claude emits its first content event for
   * the new placeholder. Without this, the chip vanished on `done` and
   * the user saw the queue indicator disappear while claude hadn't
   * actually responded yet — confusing during the few-second gap between
   * done and first thinking/tool event for the next turn.
   */
  dispatching?: boolean;
  /**
   * Display attachments (UIMessage.userAttachments shape), carried so the
   * user bubble can be BUILT and inserted into the transcript at DRAIN time.
   * We deliberately do NOT insert the queued bubble optimistically while it
   * waits — it must appear AFTER the previous reply, then its own reply right
   * below it (basic chat order). The bubble is materialized in the `done`
   * drain handler from `text` + this field.
   */
  userAttachments?: UIMessage["userAttachments"];
}

/** Per-chat reasoning effort. The composer chip sets THIS value for the ACTIVE
 *  company; it's persisted server-side (clients.json `effort_level`) and applied
 *  as CLAUDE_CODE_EFFORT_LEVEL when that chat's session spawns — normal sends AND
 *  auto-revisions. NOT a message-text magic phrase: it's a real spawn-time flag
 *  on the box's claude, where low < medium < high < xhigh < max. Applies to the
 *  chat's NEXT session spawn (a running session keeps the effort it spawned with).
 *  "low" is accepted/stored but never OFFERED by a picker (shown as "Low" if set). */
export type ThinkingLevel = "low" | "medium" | "high" | "xhigh" | "max" | "ultra";
/** Display labels for every effort level (incl. the hidden "low"). */
export const THINKING_LABEL: Record<ThinkingLevel, string> = {
  low: "Low", medium: "Medium", high: "High", xhigh: "xHigh", max: "Max", ultra: "Max", // Ultracode retired 9 Oct 2026: a legacy "ultra" reads as Max
};
/** Backend default when a company has no explicit effort_level. */
export const DEFAULT_THINKING: ThinkingLevel = "xhigh";
/** Order the chip cycles through on each click (medium → max → wrap). */
export const THINKING_ORDER: ThinkingLevel[] = ["medium", "high", "xhigh", "max"];
/** ULTRA sits above Max and is offered ONLY on samples — it is the ultracode tier
 *  (xhigh + standing dynamic-workflow orchestration, i.e. real parallel Opus agents).
 *  Clients never see it, so a client chip cycles the Medium→Max order above. */
export const THINKING_ORDER_SAMPLE: ThinkingLevel[] = ["medium", "high", "xhigh", "max", "ultra"];

/* ----- Per-company unread/pending persistence -----
 *  Survives tab close: if you fire-and-forget a task on company A and
 *  close the tab, the dot stays on A's row when you come back. */
/** Mapping for an older backend's 409 compact_unavailable_in_operations. */
export const COMPACT_UNAVAILABLE_TOAST =
  "Compact isn't available in Operations yet — Operations chats start fresh after idle, and the CLI auto-compacts near the 1M limit.";

const PENDING_STORAGE_KEY = "reputifly_pending_by_company";
function loadPendingByCompany(): Record<string, boolean> {
  try {
    const raw = typeof localStorage !== "undefined"
      ? localStorage.getItem(PENDING_STORAGE_KEY)
      : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch { /* ignore */ }
  return {};
}
function persistPendingByCompany(state: Record<string, boolean>): void {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(state));
  } catch { /* private-mode safe */ }
}

/* ----- Persisted active company -----
 *  Without this, every PWA cold start lands on a blank "Pick a company"
 *  screen — even though the user was mid-conversation when iOS killed
 *  the JS context. Restoring activeCompanyId on bootstrap reopens the
 *  same chat and triggers selectCompany → history hydrate + SSE connect.
 *  Validated against the loaded companies list at restore time so a
 *  deleted-elsewhere id doesn't 404 the dashboard.
 */
const ACTIVE_COMPANY_STORAGE_KEY = "reputifly_active_company";
function loadActiveCompanyId(): string | null {
  try {
    const raw = typeof localStorage !== "undefined"
      ? localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY)
      : null;
    return raw || null;
  } catch { return null; }
}
function persistActiveCompanyId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, id);
    else localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
  } catch { /* private-mode safe */ }
}

interface SessionState {
  // ===== Data =====
  companies: Company[];
  activeCompanyId: string | null;
  messagesByCompany: Record<string, UIMessage[]>;
  queueByCompany: Record<string, QueuedMessage[]>;
  nativeInputsByCompany: Record<string, NativeInputSnapshot>;
  applyNativeInputs: (companyId: string, snapshot: unknown, speculativeAssistantId?: string, nativeHistory?: UIMessage[]) => boolean;
  sendNativeInputNow: (companyId: string, inputId: string) => Promise<void>;
  cancelNativeInput: (companyId: string, inputId: string) => Promise<void>;
  sessionStatus: SessionStatus;
  /**
   * Multi-session map: every companyId currently running/spawning/killing/errored
   * on the backend. Populated by a 6s poll of /api/sessions/all-status.
   * Drives the sidebar "active session" pulse next to each company name.
   * Idle companies are omitted from this map.
   */
  sessionStatusByCompany: Record<string, SessionStatus>;
  /**
   * Server-side metrics for the topbar RAM pie chart chip. Polled every
   * 8s from /api/metrics. Null until first poll lands.
   */
  metrics: {
    ramUsedMB: number;
    ramTotalMB: number;
    ramUsedPct: number;
    load1: number;
    sessionsActive: number;
  } | null;
  /**
   * Per-company pending-revision summary (opt-in clients only). Polled ~6s from
   * /api/revision/attention. Drives the "Attention" sidebar section + the
   * approve banner above the composer. Empty for clients with nothing pending.
   */
  attentionByCompany: Record<string, AttentionSummary>;

  // ===== Streaming =====
  /** ID of the assistant message currently receiving deltas. */
  streamingMessageId: string | null;
  /** Per-message thinking accordion override (true=open, false=closed). */
  thinkingExpandedByMsgId: Record<string, boolean>;
  /**
   * Per-company seq dedup for Mode-2 SSE replay (Wave 4 P0-3).
   *
   * When `resyncActive` tears down + reopens SSE (focus regain, visibility
   * change, online event), the new connection loses Last-Event-ID. Backend
   * does Mode-2 inclusive replay from lastDoneSeq — re-delivers every event
   * since the last `done`. Without dedup, applyStreamEvent appends each
   * delta TWICE (replayed + original-in-memory), doubling content/thinking.
   *
   * Resets per-cid on selectCompany (new SSE epoch). Within a session,
   * seqs are monotonic and trustworthy.
   */
  lastAppliedSeqByCompany: Record<string, number>;
  /**
   * Per-company epoch id last seen from the backend (see broadcaster
   * SequencedEvent.epoch). When an incoming event's epoch exceeds this, a new
   * backend epoch has begun (spawn / session respawn / backend restart) → reset
   * lastAppliedSeqByCompany so the fresh epoch's events aren't dropped as dups.
   */
  currentEpochByCompany: Record<string, number>;
  /** Fence replay of a scheduled turn already present in loaded history. */
  completedScheduledReplayByCompany: Record<string, boolean>;
  /**
   * Per-user-msg-id failure marker. Set when a send fails AFTER the user
   * message bubble was optimistically inserted (sendInbound throws → 502,
   * or queue POST throws). Drives the red border + "⚠ Send failed · tap
   * to retry" hint on UserBubble. Without this, the toast fades after 4s
   * and the user bubble looks identical to a successful send — easy to
   * lose track of which message failed in a long chat (Julian's report
   * on the polish round before NS).
   *
   * Lifetime: in-memory only (not persisted to localStorage or SQLite).
   * Cleared when:
   *   - User taps the failed bubble → text copied to composer + marker removed
   *   - Explicit `clearFailedMarker(msgId)` call
   *   - PWA reload (state resets to {})
   * NOT cleared on company switch — marker persists across switches so
   * the user can come back to a failed bubble and still see it. Keyed by
   * UIMessage.id which is per-send-unique (never collides across cids).
   */
  failedUserMsgIds: Record<string, { reason: string; failedAt: number; retry?: "resend" }>;
  /**
   * Sends whose POST got no answer from the app (network drop, backgrounded
   * phone, proxy 499/502/503/504/524). Shown as a neutral "Not confirmed yet",
   * never red: the server is asked about the id (confirmPendingSends) and the
   * bubble becomes normal (found), is resent once with the SAME id (not found),
   * or only then turns failed.
   */
  unconfirmedUserMsgIds: Record<string, { cid: string; since: number }>;

  // ===== Composer =====
  composerDraft: string;
  attachments: AttachItem[];
  /** When true, the NEXT send POSTs `notifyOnDone: true` so the backend
   *  pings Julian on Telegram (@reputifly_agent_bot) when that turn's
   *  `done` event fires. One-shot — cleared after send. Set by picking
   *  `/notify` from the slash menu; cleared by clicking the chip. */
  notifyOnDoneArmed: boolean;
  // (Per-chat reasoning effort now lives on the active Company as
  //  `effortLevel`; the composer chip reads/sets it via setThinkingLevel.)

  // ===== UI =====
  sidebarOpen: boolean;
  addCompanyModalOpen: boolean;
  settingsCompanyId: string | null;
  guideOpen: boolean;
  /** Global-settings popup (⋯ → Global settings). */
  globalSettingsOpen: boolean;
  /** Scheduled-tasks screen (⋯ → Scheduled tasks). */
  scheduledOpen: boolean;
  /** "chat" = only the open client's rows; "all" = every client's. */
  scheduledScope: "chat" | "all";
  /** Rows for the CURRENT scope. Replaced wholesale on each fetch — this is a
   *  list view, not a cache, and a stale row here is a wrong countdown. */
  followups: Followup[];
  /** companyId → display name, so "All chats" can label a row. */
  followupNames: Record<string, string>;
  followupsLoading: boolean;
  followupsError: string | null;
  /** Row counts for BOTH scopes, so the switch can label each half. */
  followupTotals: { chat: number; all: number };
  /** Default reasoning effort baked into NEW samples. Loaded on bootstrap. */
  /** May be "ultra" (ultracode) — samples only. */
  sampleDefaultEffort: "low" | "medium" | "high" | "xhigh" | "max" | "ultra";
  /** LIVE default reasoning effort for clients (your interactive sends). */
  clientDefaultEffort: "low" | "medium" | "high" | "xhigh" | "max";
  /** LIVE default reasoning effort for auto-approved revisions (separate). */
  autoRevisionDefaultEffort: "low" | "medium" | "high" | "xhigh" | "max";
  toast: { id: number; text: string; kind: "info" | "error" | "success"; action?: ToastAction } | null;
  /** Per-company unread/pending indicator. Set true when the user sends
   *  a message to a company; cleared when the user (re-)activates that
   *  company in the sidebar. Drives the oscillating three-dot pulse on
   *  sidebar rows — mirrors the Claude.ai "active task" indicator. */
  pendingByCompany: Record<string, boolean>;
  /** Unread replies (server-side, every device): companyId → reply time. Opening the
   *  chat clears it. Drives the Unread group at the top of the sidebar. */
  unreadByCompany: Record<string, number>;
  markChatSeen(id: string): void;
  /** "Mark as unread" (row menu, swipe, a pinned card's sheet): optimistic, shared by every device,
   *  cleared when the chat is opened. Marking the open chat keeps it unread while it stays open. */
  markChatUnread(id: string, source?: string): Promise<boolean>;
  /** The open chat that was marked unread by hand: polls do not mark it read until it is opened again. */
  unreadHoldId: string | null;
  /** Sidebar recency + previews (GET /api/companies/activity, 9 Oct 2026). */
  chatActivity: Record<string, ChatActivity>;
  refreshChatActivity: () => Promise<void>;
  /** Pin / archive a chat: optimistic, written in click order, rolled back on error. */
  setChatFlag: (id: string, flag: "archived" | "pinned", on: boolean, source?: string, pinnedAt?: string | null) => Promise<boolean>;
  /** Hover-preview state for AttachmentChip. */
  hoverPreview: AttachItem | null;
  /** Lightbox state for full-screen attachment preview. `lightboxItem` is the
   *  currently-shown image; `lightboxItems` is the gallery it belongs to (the
   *  images of the message that was clicked) so ←/→ can page through them. */
  lightboxItem: LightboxItem | null;
  lightboxItems: LightboxItem[];
  lightboxIndex: number;
  bootstrapped: boolean;
  chatNavigationReady: boolean;
  chatLinkError: string | null;
  companiesLoaded: boolean;

  // ===== Actions =====
  bootstrap: () => Promise<void>;
  setSidebarOpen: (b: boolean) => void;
  setToast: (text: string, kind?: "info" | "error" | "success", action?: ToastAction) => void;
  clearToast: () => void;

  openAddCompany: () => void;
  closeAddCompany: () => void;
  openSettings: (id: string) => void;
  closeSettings: () => void;
  openGuide: () => void;
  closeGuide: () => void;
  openGlobalSettings: () => void;
  closeGlobalSettings: () => void;
  openScheduled: () => void;
  closeScheduled: () => void;
  setScheduledScope: (scope: "chat" | "all") => void;
  fetchFollowups: () => Promise<void>;
  createFollowup: (input: {
    companyId: string; prompt: string; delayMs: number;
    everyMs?: number | null; maxRuns?: number | null;
  }) => Promise<boolean>;
  clearFinishedFollowups: () => Promise<void>;
  cancelFollowup: (id: string) => Promise<void>;
  runFollowupNow: (id: string) => Promise<void>;
  saveSampleDefaultEffort: (level: "low" | "medium" | "high" | "xhigh" | "max" | "ultra") => Promise<void>;
  saveClientDefaultEffort: (level: "low" | "medium" | "high" | "xhigh" | "max") => Promise<void>;
  saveAutoRevisionDefaultEffort: (level: "low" | "medium" | "high" | "xhigh" | "max") => Promise<void>;
  /** Per-client auto-approval effort (⋯ → Auto-approve thinking). */
  setAutoRevisionEffort: (id: string, level: "low" | "medium" | "high" | "xhigh" | "max") => void;

  setHoverPreview: (item: AttachItem | null) => void;
  setLightbox: (item: LightboxItem | null) => void;
  /** Open the lightbox on a GALLERY (the clicked message's images) at `index`. */
  openLightbox: (items: LightboxItem[], index: number) => void;
  /** Page the open gallery by `delta` (wraps around). No-op for a lone image. */
  stepLightbox: (delta: number) => void;

  // Companies
  loadCompanies: () => Promise<void>;
  addCompany: (name: string, curlOrJson: string) => Promise<void>;
  addSample: (name: string) => Promise<void>;
  renameCompany: (id: string, name: string) => Promise<void>;
  convertToClient: (id: string) => Promise<void>;
  rotateKey: (id: string, curlOrJson: string) => Promise<void>;
  changeDomain: (id: string, newApiBase: string) => Promise<void>;
  deleteCompany: (id: string) => Promise<void>;

  // Sessions
  selectCompany: (id: string, history?: ChatHistoryMode) => Promise<void>;
  navigateToChat: (id: string) => Promise<boolean>;
  resolveChatLink: (refresh?: boolean) => Promise<void>;
  dismissChatLinkError: () => void;
  startSession: () => Promise<void>;
  stopSession: () => Promise<void>;
  /**
   * Pull the server's per-company session map AND reconcile local in-flight
   * state against it: if the server says nothing is running for a company
   * where we locally have a streaming placeholder or pending flag set, sweep
   * the orphan. This is the SOLE authority that clears `streamingMessageId`
   * and `isStreaming` flags based on server state — guarantees the UI is
   * never a stale liar after a missed `done` event. Called on a 6s timer
   * AND from `resyncActive` (on PWA resume / window focus).
   */
  pollAllStatuses: () => Promise<void>;
  /** Recover durable completed replies without replacing the SSE connection. */
  reconcileSettledHistory: (companyId: string) => Promise<boolean>;
  /**
   * On PWA resume / tab focus / network reconnect: tear down + re-establish
   * the SSE subscription (fresh `lastDoneSeq` replay + fresh auth token),
   * re-fetch message history for the active company, and trigger an
   * immediate `pollAllStatuses`. Debounced + cool-down'd at the call site
   * since iOS fires visibilitychange + pageshow + focus in rapid succession
   * on resume.
   */
  resyncActive: () => Promise<void>;
  /** Soft reset: wipes UI history + claude jsonl, preserves agent notes. */
  clearChatHistory: (id: string) => Promise<void>;
  /** Cancel ONE still-waiting queued message (the ✕ on a pending bubble).
   *  Optimistically removes it locally, calls the backend, and restores its
   *  text to the composer (if empty) so the user can edit + resend. */
  cancelQueued: (companyId: string, queuedId: string) => Promise<void>;
  /** Change the words of a message that has not been sent yet. "already_sent" /
   *  "removed" = lost the race (nothing changed); the caller says so plainly. */
  editQueued: (companyId: string, inputId: string, text: string) => Promise<QueuedEditResult>;
  /** Keep a native input from being handed over while its editor is open. */
  holdQueuedForEdit: (companyId: string, inputId: string, editing: boolean) => void;
  /** Undo for Remove: send the same words and files again (joins the end of the line). */
  undoRemoveQueued: (companyId: string, text: string, files: string[]) => Promise<void>;
  /** Put a taken-back queued message (words + files) into the message box, stacked in queue order. */
  restoreToComposer: (companyId: string, text: string, files: string[], order: number) => boolean;
  /** Native Compact (COMPACT UX SPEC v2): POST /compact returns at once; the
   *  verdict comes from server state (SSE "compact", status, GET /context
   *  polled every 3 s while compacting). Never starts a process. */
  compactCompany: (id: string) => Promise<void>;
  /** Server-owned native Compact state per chat. */
  compactionByCompany: Record<string, CompactionState>;
  /** Merge a server Compact state; on a compacting → done/failed transition
   *  add the chat line, refresh the meter and release the local queue. */
  applyCompaction: (cid: string, next: CompactionState | null | undefined) => void;
  /** Fetches the bearer key for one company and copies a ready-to-paste
   *  curl command to the clipboard. Useful for wiring the same client
   *  credentials into the Telegram bot, ad-hoc scripts, etc. */
  copyCompanyCurl: (id: string) => Promise<void>;
  /** Opens the client's WordPress site root in a new tab (derived from
   *  the company's api_base origin). */
  visitCompanyWebsite: (id: string) => void;

  // Revisions — "Attention → Approve"
  /** Poll the per-company attention map (opt-in clients with pending revisions). */
  fetchAttention: () => Promise<void>;
  /** Julian-approved one-click dispatch: work ONE pending thread for a client.
   *  Retains the item with transport feedback until exact server status arrives.
   *  opts.note ⇒ Julian's typed instructions passed to the agent alongside the approval. */
  runRevisionForThread: (companyId: string, threadId: number, opts?: { note?: string }) => Promise<void>;
  /** Terminal "skip it": decline a gated build + resolve the thread so it leaves
   *  Attention for good (never re-nags). Optimistically drops it from the dock. */
  dismissRevision: (companyId: string, threadId: number) => Promise<void>;
  /** Approve ALL pending threads for a client in one click (one session works the
   *  queue). opts.excludeGated ⇒ dispatch only in-scope items and LEAVE gated builds
   *  in the Attention dock for Julian (used by the Auto-approve ON sweep).
   *  opts.note ⇒ Julian's typed instructions passed to the agent alongside the approval. */
  runAllRevisions: (companyId: string, opts?: { excludeGated?: boolean; note?: string }) => Promise<void>;
  /** Opt a client in/out of the Attention watch (updates the company row). */
  setRevisionsEnabled: (companyId: string, enabled: boolean) => Promise<void>;
  /** Opt a client in/out of auto-approve (updates the company row). */
  setAutoRevision: (companyId: string, enabled: boolean) => Promise<void>;
  setAutomationFreeze: (companyId: string, frozen: boolean, reviewedPending?: boolean) => Promise<void>;
  /** Topbar ⋯ one-click: flip auto-approve for a client. Turning ON also ensures
   *  the Attention watch is on and immediately approves everything currently
   *  pending (one session works the queue); turning OFF just disarms auto (the
   *  watch stays on, so pings + the Attention dock keep working). */
  toggleAutoApprove: (companyId: string) => Promise<void>;

  // Composer
  setComposerDraft: (s: string) => void;
  addAttachmentFromFile: (file: File) => Promise<void>;
  removeAttachment: (id: string) => void;
  /** Swap a pending image for an edited copy (the annotate view). Same compress +
   * upload path as addAttachmentFromFile; the chip keeps its id and place. On
   * failure the original stays untouched. */
  replaceAttachmentImage: (id: string, file: File) => Promise<void>;
  clearAttachments: () => void;
  setThinkingLevel: (l: ThinkingLevel) => void;
  cycleThinkingLevel: () => void;
  setNotifyOnDoneArmed: (b: boolean) => void;
  /** Remove a failed-send marker (called when the user taps the bubble
   *  to retry — text copies into composer, marker disappears). No-op if
   *  the id isn't in the map. */
  clearFailedMarker: (msgId: string) => void;
  sendMessage: () => Promise<void>;
  /** Re-POST one message with its SAME userMsgId (server dedupes). */
  resendMessage: (cid: string, userMsgId: string, opts?: { manual?: boolean }) => Promise<void>;
  /** Ask the server about every unconfirmed/outbox send and settle each one. */
  confirmPendingSends: () => Promise<void>;
  /** Per-message stop — aborts current stream but leaves the session alive. */
  interruptStream: () => Promise<void>;

  // Thinking accordion
  toggleThinking: (msgId: string) => void;
  setThinkingExpanded: (msgId: string, expanded: boolean) => void;

  // SSE event reducer
  applyStreamEvent: (e: StreamEvent) => void;
  /** Show the tab's next queued message as a bubble + reply placeholder (the
   *  server drains it on its own). Used at every turn boundary: a reply's
   *  `done`, and the end of a native Compact. */
  materializeQueuedHead: (cid: string, options?: { skipIfDispatching?: boolean }) => void;
}

let _streamSub: StreamSubscription | null = null;
const historyRecoveryInFlight = new Set<string>();
// Chats whose reply was dropped because it arrived with no placeholder while
// the server was busy/queued (a server-admitted turn this tab never staged).
// Their next placeholder-less `done` re-reads durable history instead.
const settleHistoryAfterDone = new Set<string>();
/** The newest assistant row after the last operator row closed with no text and no files. */
function lastReplyEmpty(messages: UIMessage[] | undefined): boolean {
  const last = messages?.at(-1);
  return !!last && last.role === "assistant" && !last.isStreaming && !last.content?.trim() && !(last.files?.length) &&
    !(last.tools?.length);
}
let _toastCounter = 0;
/** Local HTTP feedback, never invented running status. Terminal dismissals use
 * a separate short suppression while their server readback catches up. */
const revisionApprovalTransport = new Map<string, ApprovalTransport>();
const recentlyDismissed = new Map<string, number>();
/** Companies with a toggleAutoApprove flow in flight — a cheap re-entrancy guard so
 *  reopening the ⋯ menu and re-clicking mid-flight can't double-run the backlog sweep. */
const autoToggleInFlight = new Set<string>();
/**
 * Generation counter for /all-status polls. Each poll bumps it on entry;
 * the resulting set() is conditional on `myGen === _resyncGen` so a slow
 * poll can't clobber a newer one. Prevents B1↔B2 races where a
 * visibility-triggered resync and the 6s tick land within ~100ms.
 */
let _resyncGen = 0;
/** Cool-down + debounce for visibility/pageshow/online listeners. */
let _lastResyncAtMs = 0;
let _resyncDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const RESYNC_DEBOUNCE_MS = 300;   // iOS fires 3-4 events in rapid succession
const RESYNC_COOLDOWN_MS = 2_000; // ignore re-triggers within this window

/** GET /context every 3 s per chat while its native Compact runs (backs up a
 *  missed SSE event, e.g. on a phone that slept). Stops on any other state. */
const compactionPolls = new Map<string, ReturnType<typeof setInterval>>();
function ensureCompactionPoll(cid: string): void {
  if (compactionPolls.has(cid)) return;
  const timer = setInterval(() => {
    if (useSession.getState().compactionByCompany[cid]?.state !== "compacting") {
      clearInterval(timer);
      compactionPolls.delete(cid);
      return;
    }
    try {
      const requestedRun = useSession.getState().compactionByCompany[cid];
      void getCompactionState(cid).then((state) => {
        if (useSession.getState().compactionByCompany[cid] !== requestedRun) return;
        useSession.getState().applyCompaction(cid, state);
      }).catch(() => {});
    } catch { /* next tick retries */ }
  }, 3_000);
  compactionPolls.set(cid, timer);
}

export const COMPACT_UNCONFIRMED_TEXT =
  "Compaction not confirmed — the server restarted while it ran. If it finished, the context meter shows the smaller size; otherwise compact again.";

/** Close a run the server lost: a local "not confirmed" line, state cleared,
 *  the poll stopped and Stop no longer hidden behind the compacting mirror. */
function closeLostCompaction(cid: string, prev: CompactionState): void {
  const finishedAt = nowIso();
  const timer = compactionPolls.get(cid);
  if (timer) { clearInterval(timer); compactionPolls.delete(cid); }
  useSession.setState((s) => {
    const lost: CompactionState = { ...prev, state: "failed", finishedAt, errorCode: "compact_unconfirmed", error: COMPACT_UNCONFIRMED_TEXT };
    const msgs = s.messagesByCompany[cid];
    const lineId = `sys-compact-unconfirmed-${prev.startedAt ?? finishedAt}`;
    const withLine = msgs && !msgs.some((m) => m.id === lineId)
      ? { messagesByCompany: { ...s.messagesByCompany, [cid]: [...msgs, { id: lineId, role: "system" as const, content: COMPACT_UNCONFIRMED_TEXT, createdAt: finishedAt }] } }
      : {};
    // Clear the compacting mirror; clear busy only when no turn is streaming here
    // (a message sent after the restart may be running for real).
    const streamingHere = s.activeCompanyId === cid && !!s.streamingMessageId;
    const status = s.sessionStatusByCompany[cid];
    const statusPatch = status
      ? { sessionStatusByCompany: { ...s.sessionStatusByCompany, [cid]: { ...status, compacting: false, ...(streamingHere ? {} : { busy: false }) } },
          ...(s.sessionStatus.companyId === cid && !streamingHere ? { sessionStatus: { ...s.sessionStatus, busy: false } } : {}) }
      : {};
    return { compactionByCompany: { ...s.compactionByCompany, [cid]: lost }, ...withLine, ...statusPatch };
  });
}

/** Re-read server Compact state for a chat we show as compacting (after an SSE epoch change). */
function refreshCompaction(cid: string): void {
  if (useSession.getState().compactionByCompany[cid]?.state !== "compacting") return;
  try {
    void getCompactionState(cid).then((state) => useSession.getState().applyCompaction(cid, state ?? { state: "idle" })).catch(() => {});
  } catch { /* the 3 s poll retries */ }
}

export interface ToastAction { label: string; run: () => void }
export type QueuedEditResult = "edited" | "already_sent" | "removed" | "refused" | "error";
const REMOVED_TOAST = "Removed — it won't be sent.";
const TAKEN_BACK_TOAST = "Back in your message box. It won't be sent.";
/** X on a queued message puts it back in the message box, like Claude Code (12 Oct 2026, Julian lost
 * text when X only deleted). Several take-backs stack in queue order: the block of taken-back
 * messages stays sorted by when each was queued, and anything typed after it stays after it. If the
 * draft no longer starts with that block (edited), a later take-back is simply appended. */
const takenBack = new Map<string, { chunks: { order: number; text: string }[]; block: string }>();
function stackTakenBack(companyId: string, draft: string, text: string, order: number): string {
  const prev = takenBack.get(companyId);
  let chunks: { order: number; text: string }[] | null = null; let rest = "";
  if (prev && prev.block && draft.startsWith(prev.block)) { chunks = [...prev.chunks]; rest = draft.slice(prev.block.length); }
  else if (!draft.trim()) chunks = [];
  if (!chunks) {
    takenBack.delete(companyId);
    return text.trim() ? draft.replace(/\s+$/, "") + "\n\n" + text : draft;
  }
  const at = chunks.findIndex(c => c.order > order);
  chunks.splice(at === -1 ? chunks.length : at, 0, { order, text });
  const block = chunks.map(c => c.text).filter(t => t.trim()).join("\n\n");
  takenBack.set(companyId, { chunks, block });
  return block + rest;
}
const MAY_HAVE_READ_TOAST = "Removed. It was already typed into the agent's queue, so it may still read it. Send a correction if needed.";
function attachKindFor(path: string): AttachItem["kind"] {
  return /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(path) ? "image" : /\.pdf$/i.test(path) ? "pdf" : "file";
}
function newId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * A stream status event carries only live lifecycle fields; the helper counts come
 * from the /all-status poll. Keep the polled ones until the next poll replaces them,
 * so the chip and the "N helpers working" note never blink off between polls.
 */
function keepPolledHelperFields(next: SessionStatus, previous: SessionStatus | undefined): SessionStatus {
  if (!previous) return next;
  return {
    ...next,
    ...(next.helpers === undefined && previous.helpers !== undefined ? { helpers: previous.helpers } : {}),
    ...(next.subagents === undefined && previous.subagents !== undefined ? { subagents: previous.subagents } : {}),
    // The stream's opening snapshot has no autoTurn: keep the polled one while running
    // (otherwise "Working" / "reading results" blinked off on every reconnect).
    ...(next.autoTurn === undefined && previous.autoTurn !== undefined && next.state === "running"
      ? { autoTurn: previous.autoTurn } : {}),
  };
}

function confirmedNativeResponse(raw: unknown): NativeInputSnapshot {
  const snapshot = parseNativeInputSnapshot(raw);
  if (!snapshot) {
    const error = new Error("Native input status was not confirmed");
    error.name = "SendUnconfirmedError";
    throw error;
  }
  return snapshot;
}

/** Native input receipts never create a turn, dequeue a legacy control, or infer reading. */
function nativeSnapshotPatch(s: SessionState, cid: string, raw: unknown, base: Partial<SessionState> = {}, speculativeAssistantId?: string, nativeHistory: UIMessage[] = []): Partial<SessionState> {
  const incoming = parseNativeInputSnapshot(raw);
  const previous = s.nativeInputsByCompany[cid];
  const staleHistory = previous?.revision !== undefined && incoming?.revision !== undefined && incoming.revision < previous.revision;
  const snapshot = incoming ? mergeNativeInputSnapshot(previous, incoming) : previous;
  if (staleHistory) {
    // A stale GET carries stale saved rows as well as a stale snapshot. Keep
    // current native receipts, never resurrect pre-Clear rows from that GET.
    const currentNative = (s.messagesByCompany[cid] ?? []).filter(message => message.meta?.nativeInput);
    const currentById = new Map(currentNative.map(message => [message.id, message]));
    nativeHistory = currentNative;
    if (base.messagesByCompany?.[cid]) base = { ...base, messagesByCompany: { ...base.messagesByCompany,
      [cid]: base.messagesByCompany[cid].flatMap(message => message.meta?.nativeInput
        ? (currentById.has(message.id) ? [currentById.get(message.id)!] : []) : [message]) } };
  }
  const effective = { ...s, ...base };
  const visibleIds = new Set(snapshot?.inputs.map(input => input.userMsgId) ?? []);
  // Older unresolved inputs leave the bounded active rail, not the visible audit history.
  if (snapshot?.rootId) nativeHistory = [...nativeHistory, ...(s.nativeInputsByCompany[cid]?.inputs ?? [])
    .filter(input => !visibleIds.has(input.userMsgId) && (input.state === "uncertain" || input.state === "failed"))
    .map((input): UIMessage => ({ id: input.userMsgId, role: "user", content: input.text, files: input.files,
      createdAt: new Date(input.queuedAt).toISOString(), meta: { type: "Message", nativeInput: { rootId: input.rootId, state: input.state } } }))];
  const ids = new Set([...(snapshot?.inputs.map(input => input.userMsgId) ?? []),
    ...[...(effective.messagesByCompany[cid] ?? []), ...nativeHistory].filter(message => message.meta?.nativeInput).map(message => message.id)]);
  for (const id of ids) if (!outboxFind(id)?.orderStopPending) outboxDrop(id);
  const failed = { ...effective.failedUserMsgIds };
  const unconfirmed = { ...effective.unconfirmedUserMsgIds };
  for (const id of ids) { delete failed[id]; delete unconfirmed[id]; }
  const messages = (effective.messagesByCompany[cid] ?? []).filter(message => message.id !== speculativeAssistantId);

  const waiting = sendOrderEntries(cid).filter(entry => entry.waitingForSend && !entry.truncated);
  const waitingIds = new Set(waiting.map(entry => entry.userMsgId));
  const queue = (effective.queueByCompany[cid] ?? []).filter(input => !ids.has(input.userMsgId ?? input.id));
  for (const entry of waiting) if (!queue.some(input => input.userMsgId === entry.userMsgId))
    queue.push({ id: "waiting-" + entry.userMsgId, userMsgId: entry.userMsgId, text: entry.text,
      attachmentPaths: entry.attachmentPaths, waitingForSend: true, serverQueued: false });

  return { ...base,
    nativeInputsByCompany: snapshot ? { ...effective.nativeInputsByCompany, [cid]: snapshot } : effective.nativeInputsByCompany,
    queueByCompany: { ...effective.queueByCompany, [cid]: queue },
    messagesByCompany: { ...effective.messagesByCompany, [cid]: nativeInputMessages(messages.filter(message => !waitingIds.has(message.id)), snapshot, nativeHistory) },
    failedUserMsgIds: failed, unconfirmedUserMsgIds: unconfirmed,
    ...(speculativeAssistantId && effective.streamingMessageId === speculativeAssistantId ? { streamingMessageId: null } : {}),
  };
}

/** Every session slot was busy: the send is saved and waits in line (WP-5). Kept here (not a new
 *  import) so the store's test harnesses, which mock each import, keep loading it unchanged. */
function slotWaitToast(notify: boolean): string {
  return notify
    ? "Saved. Waiting for a slot — it sends by itself, and Telegram pings when it's done."
    : "Saved. Waiting for a slot — it sends by itself when one frees up.";
}

export const useSession = create<SessionState>((set, get) => ({
  /* ---- initial data ---- */
  companies: [],
  // Restored from localStorage so cold PWA start lands on the right chat
  // (bootstrap validates this against the loaded companies and calls
  // selectCompany if still valid). Without this Julian gets a "Pick a
  // company" splash every time iOS evicts the JS context.
  activeCompanyId: loadActiveCompanyId(),
  messagesByCompany: {},
  queueByCompany: {},
  nativeInputsByCompany: {},
  sessionStatus: { companyId: null, pid: null, state: "idle" },
  sessionStatusByCompany: {},
  compactionByCompany: {},
  attentionByCompany: {},
  metrics: null,

  streamingMessageId: null,
  thinkingExpandedByMsgId: {},
  lastAppliedSeqByCompany: {},
  currentEpochByCompany: {},
  completedScheduledReplayByCompany: {},
  failedUserMsgIds: {},
  unconfirmedUserMsgIds: {},

  composerDraft: "",
  attachments: [],
  notifyOnDoneArmed: false,

  // Sidebar closed by default — open it via the hamburger when you
  // want to switch companies. Eliminates the "drawer covers chat on
  // load" flash on mobile.
  sidebarOpen: false,
  addCompanyModalOpen: false,
  settingsCompanyId: null,
  guideOpen: false,
  globalSettingsOpen: false,
  scheduledOpen: false,
  scheduledScope: "chat",
  followups: [],
  followupNames: {},
  followupsLoading: false,
  followupsError: null,
  followupTotals: { chat: 0, all: 0 },
  sampleDefaultEffort: "high",
  clientDefaultEffort: "xhigh",
  autoRevisionDefaultEffort: "xhigh",
  toast: null,
  pendingByCompany: loadPendingByCompany(),
  unreadByCompany: {},
  chatActivity: {},
  async refreshChatActivity() {
    if (typeof apiGetChatActivity !== "function") return;
    const seq = ++_activitySeq;
    const opsAtStart = _flagOpSeq;
    let activity: Record<string, ChatActivity>;
    try {
      activity = await apiGetChatActivity();
    } catch {
      return; // previews are decoration; the list still works without them
    }
    if (seq !== _activitySeq) return;
    set((s) => {
      // Another device pinned / archived a chat: follow it, unless this tab changed a flag meanwhile.
      let changed = false;
      const companies = s.companies.map((c) => {
        const row = activity[c.id];
        if (!row || _flagPending.has(c.id) || opsAtStart !== _flagOpSeq) return c;
        const next = withFlags(c, row.archivedAt, row.pinnedAt);
        if (next !== c) changed = true;
        return next;
      });
      return changed ? { chatActivity: activity, companies } : { chatActivity: activity };
    });
  },
  async setChatFlag(id, flag, on, source, pinnedAt) {
    const before = get().companies.find((c) => c.id === id);
    if (!before || typeof apiSetChatFlag !== "function") return false;
    const apply = (archivedAt: string | null | undefined, pinnedAt: string | null | undefined) => set((s) => ({
      companies: s.companies.map((c) => (c.id === id ? withFlags(c, archivedAt, pinnedAt) : c)),
    }));
    // Per chat, writes go out one after another in click order; only the newest click owns the row.
    const track = _flagPending.get(id) ?? { tail: Promise.resolve(), latest: 0,
      confirmed: { archivedAt: before.archivedAt ?? null, pinnedAt: before.pinnedAt ?? null } };
    const mine = ++track.latest;
    _flagPending.set(id, track);
    _flagOpSeq++;
    const now = new Date().toISOString();
    if (flag === "archived") apply(on ? (before.archivedAt || now) : null, on ? null : before.pinnedAt);
    else apply(on ? null : before.archivedAt, on ? (before.pinnedAt || pinnedAt || now) : null);
    const run = track.tail.then(async () => {
      try {
        const next = await apiSetChatFlag(id, flag, on, source, pinnedAt);
        track.confirmed = { archivedAt: next?.archivedAt ?? null, pinnedAt: next?.pinnedAt ?? null };
        if (track.latest === mine) apply(track.confirmed.archivedAt, track.confirmed.pinnedAt);
        return true;
      } catch {
        if (track.latest === mine) {
          apply(track.confirmed.archivedAt, track.confirmed.pinnedAt);
          const verb = flag === "archived" ? (on ? "archive" : "unarchive") : (on ? "pin" : "unpin");
          get().setToast(`Couldn't ${verb} that chat. Please try again.`, "error");
        }
        return false;
      } finally {
        if (track.latest === mine && _flagPending.get(id) === track) _flagPending.delete(id);
      }
    });
    track.tail = run.catch(() => undefined);
    return run;
  },
  unreadHoldId: null,
  async markChatUnread(id, source) {
    if (!get().companies.some((c) => c.id === id)) return false;
    const at = Date.now();
    _manualUnread.set(id, at);
    set((s) => ({
      unreadByCompany: { ...s.unreadByCompany, [id]: s.unreadByCompany[id] || MANUAL_UNREAD },
      unreadHoldId: s.activeCompanyId === id ? id : s.unreadHoldId,
    }));
    try {
      if (typeof apiMarkChatUnread === "function") await apiMarkChatUnread(id, source);
      return true;
    } catch {
      if (_manualUnread.get(id) === at) _manualUnread.delete(id);
      set((s) => {
        if (s.unreadByCompany[id] !== MANUAL_UNREAD) return {};
        const next = { ...s.unreadByCompany };
        delete next[id];
        return { unreadByCompany: next, unreadHoldId: s.unreadHoldId === id ? null : s.unreadHoldId };
      });
      get().setToast("Couldn't mark that chat unread. Please try again.", "error");
      return false;
    }
  },
  markChatSeen(id: string) {
    _manualUnread.delete(id);
    if (get().unreadHoldId) set({ unreadHoldId: null });
    const cur = get().unreadByCompany;
    if (cur[id]) {
      const next = { ...cur };
      delete next[id];
      set({ unreadByCompany: next });
    }
    // Always tell the server: another device may still show it as unread.
    void Promise.resolve(typeof apiMarkChatRead === "function" ? apiMarkChatRead(id) : undefined).catch(() => undefined);
  },
  hoverPreview: null,
  lightboxItem: null,
  lightboxItems: [],
  lightboxIndex: 0,
  bootstrapped: false,
  chatNavigationReady: false,
  chatLinkError: null,
  companiesLoaded: false,

  /* ---- UI ---- */
  setSidebarOpen: (b) => set({ sidebarOpen: b }),
  setToast: (text, kind = "info", action) =>
    set({ toast: { id: ++_toastCounter, text, kind: kind as "info" | "error" | "success", ...(action ? { action } : {}) } }),
  clearToast: () => set({ toast: null }),
  openAddCompany: () => set({ addCompanyModalOpen: true }),
  closeAddCompany: () => set({ addCompanyModalOpen: false }),
  openSettings: (id) => set({ settingsCompanyId: id }),
  closeSettings: () => set({ settingsCompanyId: null }),
  openGuide: () => set({ guideOpen: true }),
  closeGuide: () => set({ guideOpen: false }),
  openGlobalSettings: () => set({ globalSettingsOpen: true }),
  closeGlobalSettings: () => set({ globalSettingsOpen: false }),

  /* ----- Scheduled tasks (⋯ → Scheduled tasks) -----
     Unlike the global settings, these are NOT loaded at bootstrap: they are a
     countdown list nobody is looking at until they open the screen, and a stale
     one is worse than an absent one. Fetched on open, then polled by the
     component while it is mounted. */
  openScheduled: () => {
    // followupsLoading must be true on the FIRST paint. Setting it inside
    // fetchFollowups() is one render too late, and that render showed
    // "Nothing scheduled in this chat yet" — a confident lie — before the rows
    // arrived. An empty list and an unloaded list look identical; only this
    // flag tells them apart, so it has to be set in the same tick as the open.
    set({ scheduledOpen: true, followupsError: null, followupsLoading: true });
    void get().fetchFollowups();
  },
  closeScheduled: () => set({ scheduledOpen: false }),
  setScheduledScope: (scope) => {
    if (get().scheduledScope === scope) return;
    // Blank the rows immediately: showing this chat's list under an "All chats"
    // heading for the length of a round-trip is a lie the user would act on.
    set({ scheduledScope: scope, followups: [], followupsError: null, followupsLoading: true });
    void get().fetchFollowups();
  },

  fetchFollowups: async () => {
    const scope = get().scheduledScope;
    const cid = get().activeCompanyId;
    if (scope === "chat" && !cid) {
      set({ followups: [], followupNames: {}, followupsLoading: false });
      return;
    }
    set({ followupsLoading: true });
    try {
      const r = await apiGetFollowups(scope, cid ?? undefined);
      // The scope may have been switched during the await; a late response for
      // the other scope would silently replace the right list with the wrong one.
      if (get().scheduledScope !== scope) return;
      set({
        followups: r.followups,
        followupNames: r.names ?? {},
        followupTotals: r.totals ?? { chat: 0, all: 0 },
        followupsLoading: false,
        followupsError: null,
      });
    } catch (err: any) {
      set({ followupsLoading: false, followupsError: err?.message ?? "Could not load schedules" });
    }
  },

  createFollowup: async (input) => {
    try {
      await apiCreateFollowup(input);
      // Re-read rather than splicing the returned row in: the list is scoped and
      // sorted server-side, and a locally inserted row would sit in the wrong
      // group until the next poll.
      await get().fetchFollowups();
      get().setToast("Scheduled", "success");
      return true;
    } catch (err: any) {
      // validate() writes for a human — "delay_ms below floor (5 < 10000)" is
      // more use than "invalid input", so pass it straight through.
      get().setToast(err?.message ?? "Couldn't schedule that", "error");
      return false;
    }
  },

  clearFinishedFollowups: async () => {
    const scope = get().scheduledScope;
    const cid = get().activeCompanyId;
    try {
      const n = await apiClearFinishedFollowups(scope === "chat" ? cid ?? undefined : undefined);
      await get().fetchFollowups();
      get().setToast(n ? `Cleared ${n} finished` : "Nothing to clear", "success");
    } catch (err: any) {
      get().setToast(err?.message ?? "Couldn't clear", "error");
    }
  },

  cancelFollowup: async (id) => {
    const prev = get().followups;
    const row = prev.find((f) => f.id === id);
    // Optimistic: mark it cancelled in place rather than removing it, so the row
    // does not vanish and reappear under "Finished" a beat later.
    set({ followups: prev.map((f) => (f.id === id ? { ...f, status: "cancelled", canCancel: false } : f)) });
    try {
      await apiCancelFollowup(id);
      get().setToast(`"${row?.prompt.slice(0, 40) ?? "Schedule"}…" cancelled`, "success");
      void get().fetchFollowups();
    } catch (err: any) {
      set({ followups: prev }); // revert
      // The backend's refusal text is written for a human — show it verbatim.
      get().setToast(err?.message ?? "Couldn't cancel", "error");
    }
  },

  runFollowupNow: async (id) => {
    try {
      await apiRunFollowupNow(id);
      // Deliberately not "sent": the server only moved the due time. The
      // scheduler's tick still applies the pause, busy and headroom gates.
      get().setToast("Queued — it will run within 30 seconds", "success");
      void get().fetchFollowups();
    } catch (err: any) {
      get().setToast(err?.message ?? "Couldn't run it now", "error");
    }
  },
  saveSampleDefaultEffort: async (level) => {
    const label = THINKING_LABEL[level];
    const prev = get().sampleDefaultEffort;
    set({ sampleDefaultEffort: level }); // optimistic; modal stays open (two settings)
    try {
      const s = await apiSetSampleDefaultEffort(level);
      set({ sampleDefaultEffort: s.sampleDefaultEffort, clientDefaultEffort: s.clientDefaultEffort });
      get().setToast(`New samples will default to ${label} thinking`, "success");
    } catch (e) {
      set({ sampleDefaultEffort: prev }); // revert
      get().setToast(`Couldn't save: ${(e as Error).message}`, "error");
    }
  },
  saveClientDefaultEffort: async (level) => {
    const label = THINKING_LABEL[level];
    const prev = get().clientDefaultEffort;
    set({ clientDefaultEffort: level }); // optimistic
    try {
      const s = await apiSetClientDefaultEffort(level);
      set({ clientDefaultEffort: s.clientDefaultEffort });
      get().setToast(`Clients will default to ${label} thinking`, "success");
    } catch (e) {
      set({ clientDefaultEffort: prev }); // revert
      get().setToast(`Couldn't save: ${(e as Error).message}`, "error");
    }
  },
  saveAutoRevisionDefaultEffort: async (level) => {
    const label = THINKING_LABEL[level];
    const prev = get().autoRevisionDefaultEffort;
    set({ autoRevisionDefaultEffort: level }); // optimistic
    try {
      const s = await apiSetAutoRevisionDefaultEffort(level);
      set({ autoRevisionDefaultEffort: s.autoRevisionDefaultEffort });
      get().setToast(`Auto-approvals will default to ${label} thinking`, "success");
    } catch (e) {
      set({ autoRevisionDefaultEffort: prev }); // revert
      get().setToast(`Couldn't save: ${(e as Error).message}`, "error");
    }
  },
  setAutoRevisionEffort: (id, level) => {
    const s = get();
    const company = s.companies.find((c) => c.id === id);
    const prev = company?.autoRevisionEffort;
    if (prev === level) return;
    set({ companies: s.companies.map((c) => (c.id === id ? { ...c, autoRevisionEffort: level } : c)) });
    apiSetAutoRevisionEffort(id, level)
      .then((updated) => {
        set({ companies: get().companies.map((c) => (c.id === id ? { ...c, ...updated } : c)) });
        const label = THINKING_LABEL[level];
        get().setToast(`Auto-approve thinking → ${label}`, "success");
      })
      .catch((err: any) => {
        set({ companies: get().companies.map((c) => (c.id === id ? { ...c, autoRevisionEffort: prev } : c)) });
        get().setToast(`Couldn't set auto-approve thinking: ${err?.message ?? err}`, "error");
      });
  },
  setHoverPreview: (item) => set({ hoverPreview: item }),
  // Single-image open (or close on null). Kept for callers that have no gallery.
  setLightbox: (item) =>
    set(
      item
        ? { lightboxItem: item, lightboxItems: [item], lightboxIndex: 0 }
        : { lightboxItem: null, lightboxItems: [], lightboxIndex: 0 },
    ),
  openLightbox: (items, index) => {
    if (items.length === 0) return;
    const i = Math.min(Math.max(index, 0), items.length - 1);
    set({ lightboxItems: items, lightboxIndex: i, lightboxItem: items[i]! });
  },
  stepLightbox: (delta) => {
    const { lightboxItems: items, lightboxIndex: i } = get();
    if (items.length < 2) return;
    // Wrap, so ← from the first lands on the last (and vice versa).
    const n = (i + delta + items.length) % items.length;
    set({ lightboxIndex: n, lightboxItem: items[n]! });
  },

  /* ---- bootstrap on mount ---- */
  async bootstrap() {
    if (get().bootstrapped) {
      if (get().chatNavigationReady) await get().resolveChatLink(true);
      return;
    }
    const storedChat = get().activeCompanyId;
    set({ bootstrapped: true, activeCompanyId: null });
    // The roster, session status and global settings don't depend on each other:
    // load them together instead of one after another (27 Sep: ~0.6 s off first paint).
    await Promise.all([
      get().loadCompanies(),
      getSessionStatus().then((status) => set({ sessionStatus: status }), () => {
        // Backend not reachable in Phase 1A is fine.
      }),
      // Global settings (sample-default effort) — non-fatal if unreachable.
      apiGetGlobalSettings().then((s) => set({
        sampleDefaultEffort: s.sampleDefaultEffort,
        clientDefaultEffort: s.clientDefaultEffort,
        autoRevisionDefaultEffort: s.autoRevisionDefaultEffort,
      }), () => { /* keep the defaults */ }),
    ]);

    // Restore the active company from localStorage (so cold PWA start lands
    // on the right chat with full state). Validate against the loaded
    // companies list — if the stored id was deleted from another session,
    // drop it cleanly. selectCompany short-circuits on cur === id so we
    // null it out first to force the hydrate + SSE-subscribe path.
    {
      const stored = storedChat;
      // Read only after authentication and the accessible roster have loaded.
      // An explicit link always wins; invalid links never open a different chat.
      if (readChatLink().kind !== "none") {
        await get().resolveChatLink();
      } else if (stored) {
        const valid = get().companies.find((c) => c.id === stored);
        if (valid) {
          set({ activeCompanyId: null });
          await get().selectCompany(stored, "replace");
        } else {
          persistActiveCompanyId(null);
          set({ activeCompanyId: null });
        }
      }
    }

    set({ chatNavigationReady: true });
    markFirstViewReady();

    // ----- Polling loops + resume listeners -----
    // /all-status: every 6s. Also does in-flight reconciliation —
    // sweeps orphan placeholders when server says nothing is running.
    // /metrics: every 8s. Silent on error.
    // visibility/pageshow/focus/online: debounced resyncActive.
    // Module-level guard so React hot-reload doesn't double-register.
    if (!(globalThis as any).__reputiflyPollersStarted) {
      (globalThis as any).__reputiflyPollersStarted = true;
      const pollMetrics = async () => {
        try {
          const m = await getMetrics();
          set({
            metrics: {
              ramUsedMB: m.ram.usedMB,
              ramTotalMB: m.ram.totalMB,
              ramUsedPct: m.ram.usedPct,
              load1: m.load.one,
              sessionsActive: m.sessions.active,
            },
          });
        } catch { /* silent */ }
      };
      void get().pollAllStatuses();
      void pollMetrics();
      void get().fetchAttention();
      setInterval(() => void get().pollAllStatuses(), 6_000);
      setInterval(pollMetrics, 8_000);
      // Attention poll (opt-in clients' pending revisions). Slightly slower than
      // status — 12s is plenty for a human-in-loop approve flow, and the backend
      // watcher itself only refreshes every ~45s.
      setInterval(() => void get().fetchAttention(), 12_000);

      // PWA resume reconciliation. iOS fires visibilitychange + pageshow +
      // focus in rapid succession on resume; the debounce coalesces them
      // into one resync. Cooldown prevents rapid app-switching from
      // hammering the backend.
      if (typeof document !== "undefined" && typeof window !== "undefined") {
        const onResumeSignal = () => {
          if (document.visibilityState !== "visible") return;
          if (Date.now() - _lastResyncAtMs < RESYNC_COOLDOWN_MS) return;
          if (_resyncDebounceTimer) clearTimeout(_resyncDebounceTimer);
          _resyncDebounceTimer = setTimeout(() => {
            _resyncDebounceTimer = null;
            _lastResyncAtMs = Date.now();
            void get().resyncActive();
          }, RESYNC_DEBOUNCE_MS);
        };
        document.addEventListener("visibilitychange", onResumeSignal);
        window.addEventListener("pageshow", onResumeSignal);
        // Not "focus": iOS fires it whenever the keyboard or a sheet closes, and each
        // resync rebuilt the stream and dropped replies. visibilitychange + pageshow
        // cover a real resume.
        window.addEventListener("online", onResumeSignal);
        // Back online: settle unconfirmed sends at once (the resync above is
        // rate-limited by its cooldown).
        window.addEventListener("online", () => {
          confirmBackoffMs = 2_000;
          scheduleConfirm(get, 300);
        });
      }
      // OUTBOX RECOVERY after a reload / app kill: every saved, unconfirmed
      // send is shown "Not confirmed yet" and settled against the server with
      // its SAME id (found → normal; absent → one resend; then failed).
      {
        const pending = outboxAll();
        if (pending.length > 0) {
          set((s) => {
            const unconfirmed = { ...s.unconfirmedUserMsgIds };
            let failed = s.failedUserMsgIds;
            for (const e of pending) {
              if (e.state === "failed") {
                if (!Object.hasOwn(failed, e.userMsgId)) failed = { ...failed, [e.userMsgId]: e.truncated
                  ? { reason: TOO_LONG_REASON, failedAt: e.createdAt }
                  : { reason: UNCONFIRMED_REASON, failedAt: e.createdAt, retry: "resend" } };
              } else if (e.state === "sending") unconfirmed[e.userMsgId] = { cid: e.cid, since: Date.now() };
              // "queued": confirmed by the server; the check below settles it.
            }
            return { unconfirmedUserMsgIds: unconfirmed, failedUserMsgIds: failed };
          });
          scheduleConfirm(get, 1_500);
        }
      }
    }
    // Default-open the sidebar on wider viewports.
    if (typeof window !== "undefined" && window.innerWidth < 800) {
      set({ sidebarOpen: false });
    }
  },

  /* ---- Companies ---- */
  async loadCompanies() {
    try {
      const cs = await getCompanies();
      const prev = new Map(get().companies.map((c) => [c.id, c]));
      set({ companies: cs.map((c) => (prev.has(c.id) ? keepFlags(prev.get(c.id)!, c) : c)), companiesLoaded: true });
    } catch (err: any) {
      set({ companiesLoaded: false });
      get().setToast(`Couldn't load companies: ${err.message}`, "error");
    }
  },

  async addCompany(name, curlOrJson) {
    const c = await apiAddCompany(name, curlOrJson);
    set((s) => ({
      companies: [...s.companies.filter((x) => x.id !== c.id), c],
      messagesByCompany: { ...s.messagesByCompany, [c.id]: [] },
      chatActivity: withoutKey(s.chatActivity, c.id),
    }));
    // Supersedes any /activity read that started before the add (it could carry a deleted chat's preview for this id).
    void get().refreshChatActivity();
    get().setToast(`Added "${c.name}"`);
  },

  async addSample(name) {
    // Sample/demo workspace — server creates it with kind:"sample" + a
    // placeholder key (no paste/probe). The agent re-keys + renames it after
    // /cloud-control spins up the demo (reflected via the pollAllStatuses gate).
    try {
      const c = await apiAddSample(name);
      set((s) => ({
        companies: [...s.companies.filter((x) => x.id !== c.id), c],
        messagesByCompany: { ...s.messagesByCompany, [c.id]: [] },
        chatActivity: withoutKey(s.chatActivity, c.id),
      }));
      void get().refreshChatActivity();
      // Open the new sample chat immediately (the "+ New sample" UX — no modal).
      await get().selectCompany(c.id);
    } catch (err: any) {
      get().setToast(`Couldn't create sample: ${err.message}`, "error");
    }
  },

  async renameCompany(id, name) {
    const next = await apiRenameCompany(id, name);
    set((s) => ({
      companies: s.companies.map((c) => (c.id === id ? keepFlags(c, next) : c)),
    }));
  },

  async convertToClient(id) {
    // Flip kind sample -> client on the SAME row (id, key, api_base, cloak,
    // chat history all preserved). It just moves from the Samples tab to Clients.
    const next = await apiPromoteToClient(id);
    set((s) => ({
      companies: s.companies.map((c) => (c.id === id ? keepFlags(c, next) : c)),
    }));
    get().setToast(next.automationFrozen
      ? `Converted "${next.name}" to a client. Review the safety hold before enabling automation.`
      : `Converted "${next.name}" to a client`);
    // Promotion must never leave an invisible automation hold behind.
    if (next.automationFrozen) get().openSettings(id);
  },

  async rotateKey(id, curlOrJson) {
    const next = await apiRotateKey(id, curlOrJson);
    set((s) => ({
      companies: s.companies.map((c) => (c.id === id ? keepFlags(c, next) : c)),
    }));
    get().setToast(`Rotated key for "${next.name}"`);
  },

  async changeDomain(id, newApiBase) {
    const next = await apiChangeDomain(id, newApiBase);
    set((s) => ({
      companies: s.companies.map((c) => (c.id === id ? keepFlags(c, next) : c)),
    }));
    let host = "";
    try {
      if (next.apiBase) host = new URL(next.apiBase).host;
    } catch {
      /* fall through to generic copy */
    }
    get().setToast(host ? `Moved "${next.name}" to ${host}` : `Moved "${next.name}" to the new domain`);
  },

  async deleteCompany(id) {
    const target = get().companies.find((c) => c.id === id);
    const wasActive = get().activeCompanyId === id;
    await apiDeleteCompany(id);
    forgetChatSends(get, set, id);
    set((s) => {
      const messages = { ...s.messagesByCompany };
      delete messages[id];
      const queue = { ...s.queueByCompany };
      delete queue[id];
      // A new chat can reuse this id (addSample takes the first free slug): never show it this one's preview.
      const activity = { ...s.chatActivity };
      delete activity[id];
      const next: Partial<SessionState> = {
        companies: s.companies.filter((c) => c.id !== id),
        messagesByCompany: messages,
        queueByCompany: queue,
        chatActivity: activity,
      };
      if (s.activeCompanyId === id) {
        next.activeCompanyId = null;
        next.sessionStatus = { companyId: null, pid: null, state: "idle" };
        next.streamingMessageId = null;
      }
      return next as SessionState;
    });
    if (wasActive) persistActiveCompanyId(null);
    // Same word as the sheet (deleteCopy / chatKind): a client is "Removed", a sample / plain chat "Deleted".
    get().setToast(`${target && target.kind !== "sample" ? "Removed" : "Deleted"} "${target?.name ?? id}"`);
  },

  /* ---- Sessions ---- */
  async selectCompany(id, history = "push") {
    const cur = get().activeCompanyId;
    if (!get().companies.some((company) => company.id === id)) {
      set({ chatLinkError: "This chat is unavailable. It may have been deleted, or this account may not have access." });
      return;
    }
    if (cur === id) {
      writeChatLink(id, history);
      set({ chatLinkError: null });
      // Same company — just make sure the sidebar collapses on mobile.
      if (typeof window !== "undefined" && window.innerWidth < 800) {
        set({ sidebarOpen: false });
      }
      // Even on re-select, clear the unread dot (user is checking in).
      get().markChatSeen(id);
      const pending = { ...get().pendingByCompany };
      if (pending[id]) {
        delete pending[id];
        persistPendingByCompany(pending);
        set({ pendingByCompany: pending });
      }
      return;
    }
    if (chatNavigationBusy()) {
      get().setToast("Wait for the schedule request to finish before switching chats.", "error");
      return;
    }
    // Confirm BEFORE clearing attachments, revoking object URLs or closing streams.
    if (cur && !confirmChatSwitch(get())) return;
    const selection = ++chatSelectionVersion;
    writeChatLink(id, history);
    set({ chatLinkError: null });
    // Tear down old stream subscription
    if (_streamSub) {
      _streamSub.close();
      _streamSub = null;
    }
    abortMockStream();

    // FRESH COMPOSER PER CHAT (context-corruption fix). Clear any STAGED (unsent)
    // attachment + draft so a zip/text from the previous chat can NEVER bleed into
    // this one. Root cause of a sample building the WRONG lead: composer state was
    // global + uncleared on switch, so a Hax Tiling zip staged in one chat could
    // ride into the Birthday Party SG sample. clearAttachments() also revokes the
    // object URLs. (Sending already clears; this covers the switch-without-send path.)
    get().clearAttachments();
    set({ composerDraft: "", notifyOnDoneArmed: false });

    // Clear pending/unread for the company we're switching TO.
    get().markChatSeen(id);
    const pending = { ...get().pendingByCompany };
    if (pending[id]) {
      delete pending[id];
      persistPendingByCompany(pending);
    }

    set({
      activeCompanyId: id,
      chatNavigationReady: true,
      streamingMessageId: null,
      pendingByCompany: pending,
    });
    // Persist so cold PWA start lands on this company.
    persistActiveCompanyId(id);

    // Auto-close sidebar on phone
    if (typeof window !== "undefined" && window.innerWidth < 800) {
      set({ sidebarOpen: false });
    }

    let droppedChips: QueuedMessage[] = [];
    // Hydrate history for this company.
    //
    // If Claude is mid-turn when we hydrate (page refresh during a streaming
    // reply, PWA relaunch after phone sleep, etc.), the most recent persisted
    // message is the USER's — the assistant reply hasn't been written to
    // SQLite yet (it persists on /reply, after the model finishes). In that
    // case we synthesize a placeholder streaming assistant bubble + set
    // streamingMessageId so the SSE replay events (thinking, message_delta,
    // tool_start) flow into it cleanly. Without this the chip + tool bubbles
    // vanish on refresh and the UI looks idle while the server is working.
    try {
      const { messages: rawHistory, inFlight } = await getMessageHistory(id);
      const history = nativeInputMessages(rawHistory, parseNativeInputSnapshot(inFlight?.nativeInput));
      if (selection !== chatSelectionVersion || get().activeCompanyId !== id) return;
      const last = history[history.length - 1];
      const shouldSynthesize =
        !!inFlight?.busy && last?.role === "user";
      let finalHistory: UIMessage[] = history;
      let placeholderId: string | null = null;
      if (shouldSynthesize) {
        placeholderId = `restored-${id}-${Date.now()}`;
        const placeholder: UIMessage = {
          id: placeholderId,
          role: "assistant",
          content: "",
          tools: [],
          isStreaming: true,
          createdAt: new Date().toISOString(),
        };
        finalHistory = [...history, placeholder];
      }
      // QUEUE RECONCILIATION: server is the truth about how many messages
      // are queued. If user was on this company, queued msg2 while msg1
      // streamed, then switched AWAY (SSE torn down), the done event for
      // msg1 never reached the frontend's dequeue logic — so q[0].dispatching
      // was never set, and even after switching back the queueByCompany[cid]
      // would stay populated forever, showing a stale "1 queued" chip.
      // Trim local queue to match server's actual queue length on every
      // history fetch (selectCompany + resyncActive). Server `inFlight.queueLength`
      // is computed from queue.length(cid). If 0, clear locally. If less than
      // local, trim from the front (the oldest ones already drained).
      //
      // EDGE CASE (P1-F4 audit fix 2026-05-27): if q[0] has dispatching=true,
      // the server has ALREADY popped it from queue.q (it's now being
      // processed by claude), so serverQueueLength will be 1 less than
      // localQueue.length. Naively trimming the head would silently drop
      // the dispatching entry → "⏳ sending…" badge vanishes during the
      // window where claude is spinning up the new turn but no content
      // events have fired yet. Preserve the dispatching head explicitly.
      const serverQueueLength = inFlight?.queueLength ?? 0;
      set((s) => {
        // Wave 4 P0-3: reset seq dedup for this cid — selectCompany opens
        // a fresh SSE subscription that will replay events; we want to
        // accept those events on this new "epoch."
        const nextSeq = { ...s.lastAppliedSeqByCompany };
        delete nextSeq[id];
        const localQueue = s.queueByCompany[id] ?? [];
        // Wave 4 P0-1 (frontend): the prior "P1-F5" fix looked in
        // finalHistory for a streaming placeholder. That was DEAD CODE —
        // finalHistory comes from server, server never persists
        // isStreaming=true. Correct lookup is the LOCAL in-memory
        // messagesByCompany[id] (before this set() overwrites it). We
        // then merge: keep the live placeholder spliced onto fresh
        // server history so SSE events continue to route to its id.
        const oldLocalMsgs = s.messagesByCompany[id] ?? [];
        const existingLocalPlaceholder = oldLocalMsgs.find(
          (m) => m.role === "assistant" && m.isStreaming,
        );
        let mergedHistory = finalHistory;
        let resolvedStreamingId = placeholderId ?? s.streamingMessageId;
        if (!placeholderId && existingLocalPlaceholder) {
          // Wave 5 P0 audit fix: prior version checked `history.some(m =>
          // m.id === existingLocalPlaceholder.id)` — but local placeholder
          // ids (`restored-X`, `resync-X`, `recover-X`, optimistic `a-T-r`)
          // and server-persisted ids (`a-Date.now()-Math.random()`) come
          // from disjoint code paths and never collide. That check was
          // dead code, so the duplicate-bubble race still shipped.
          //
          // Correct detection: count of PERSISTED assistant replies (non-
          // streaming) in finalHistory vs in oldLocalMsgs (excluding our
          // placeholder). If server has MORE, turn(s) completed while we
          // were away on another company — discard our stale placeholder.
          const localPersistedReplies = oldLocalMsgs.filter(
            (m) => m.role === "assistant" && !m.isStreaming,
          ).length;
          const serverPersistedReplies = finalHistory.filter(
            (m) => m.role === "assistant",
          ).length;
          const turnCompletedWhileAway = serverPersistedReplies > localPersistedReplies;
          if (turnCompletedWhileAway) {
            // Server is truth: persisted reply is authoritative.
            // Discard the stale local placeholder (would otherwise render
            // its old streamed content as a duplicate bubble + future SSE
            // events would route to it instead of a new turn's placeholder).
            resolvedStreamingId = null;
          } else {
            // Turn genuinely still in-flight (server replies didn't grow).
            // Splice the live placeholder onto fresh history so SSE events
            // continue to route to its id.
            mergedHistory = [...finalHistory, existingLocalPlaceholder];
            resolvedStreamingId = existingLocalPlaceholder.id;
          }
        }
        const retained = mergeFailedLocalMessages(mergedHistory, oldLocalMsgs, s.failedUserMsgIds, s.unconfirmedUserMsgIds);
        // QUEUE: rebuilt from the server's queued list (survives a reload),
        // or trimmed to its count on an older backend. serverQueueLength
        // excludes a dispatching head (the server already moved it out).
        const rebuiltQueue = reconcileQueue(localQueue, inFlight?.queued, serverQueueLength, queuedFromServer);
        const queueNow = rebuiltQueue ?? localQueue;
        if (rebuiltQueue) droppedChips = droppedServerQueued(localQueue, rebuiltQueue);
        // One message never shows "Failed" and "queued" at once: the server's
        // queued list wins (a same-id retry of a failed row that got queued).
        const deduped = hideQueuedDuplicates(retained.messages, retained.failedUserMsgIds,
          new Set((inFlight?.queued ?? []).map((q) => q.userMsgId)));
        retained.messages = deduped.messages;
        retained.failedUserMsgIds = deduped.failed;
        // OUTBOX: a send made just before a reload/app kill that neither the
        // history nor the queue shows is rendered, never lost; it is marked
        // "Not confirmed yet" (or failed) and settled by confirmPendingSends.
        const present = new Set([...retained.messages.map((m) => m.id),
          ...queueNow.flatMap((q) => q.userMsgId ? [q.userMsgId] : [])]);
        const outbox = outboxAll();
        const recovered = outboxBubbles(outbox, id, present);
        let messages = retained.messages;
        let failedMap = retained.failedUserMsgIds;
        let unconfirmedMap = s.unconfirmedUserMsgIds;
        if (recovered.length > 0) {
          const streamingAt = messages.findIndex((m) => m.role === "assistant" && m.isStreaming);
          messages = streamingAt >= 0
            ? [...messages.slice(0, streamingAt), ...recovered, ...messages.slice(streamingAt)]
            : [...messages, ...recovered];
          for (const bubble of recovered) {
            const entry = outbox.find((e) => e.userMsgId === bubble.id)!;
            if (entry.state === "failed") {
              if (!Object.hasOwn(failedMap, bubble.id)) failedMap = { ...failedMap, [bubble.id]: entry.truncated
                ? { reason: TOO_LONG_REASON, failedAt: entry.createdAt }
                : { reason: UNCONFIRMED_REASON, failedAt: entry.createdAt, retry: "resend" } };
            } else if (!Object.hasOwn(unconfirmedMap, bubble.id)) {
              unconfirmedMap = { ...unconfirmedMap, [bubble.id]: { cid: id, since: Date.now() } };
            }
          }
        }
        const next: Partial<SessionState> = {
          messagesByCompany: { ...s.messagesByCompany, [id]: messages },
          failedUserMsgIds: failedMap,
          unconfirmedUserMsgIds: unconfirmedMap,
          streamingMessageId: resolvedStreamingId,
          lastAppliedSeqByCompany: nextSeq,
        };
        if (rebuiltQueue) next.queueByCompany = { ...s.queueByCompany, [id]: rebuiltQueue };
        return nativeSnapshotPatch(s, id, inFlight?.nativeInput, next);
      });
    } catch (err: any) {
      if (selection !== chatSelectionVersion || get().activeCompanyId !== id) return;
      get().setToast(`Couldn't load history: ${err.message}`, "error");
    }
    // A chip the server confirmed queued and no longer lists: drained, or lost
    // in a restart — the status check tells which (never a silent drop).
    if (droppedChips.length > 0) rememberDroppedQueued(get, id, droppedChips);
    // Settle any send the server has not confirmed yet (outbox after a reload).
    if (outboxAll().length > 0 || Object.keys(get().unconfirmedUserMsgIds).length > 0) scheduleConfirm(get, 800);

    // Subscribe to the streaming surface (real SSE in 1C+, no-op stub in mock mode)
    if (selection !== chatSelectionVersion || get().activeCompanyId !== id) return;
    _streamSub = connectStream(id, (e) => get().applyStreamEvent(e), getCurrentIdToken);
  },

  async navigateToChat(id) {
    if (chatNavigationBusy()) {
      get().setToast("Wait for the schedule request to finish before switching chats.", "error");
      return false;
    }
    if (!get().companies.some((company) => company.id === id)) {
      get().setToast("This chat is unavailable. Refresh the chat list and try again.", "error");
      return false;
    }
    // Opening the current chat also closes its sheet. Guard unsaved schedule
    // edits, but don't ask about composer text that stays mounted in that case.
    if (get().activeCompanyId === id && !confirmChatSwitch(get(), true)) return false;
    const navigation = get().selectCompany(id);
    const selection = chatSelectionVersion;
    const admitted = get().activeCompanyId === id;
    // Selection/confirmation is synchronous before history loading. Never close
    // a newer sheet from an old navigation's delayed completion.
    if (admitted) get().closeScheduled();
    await navigation;
    return admitted && selection === chatSelectionVersion && get().activeCompanyId === id;
  },

  async resolveChatLink(refresh = false) {
    if (refresh) await get().loadCompanies();
    const link = readChatLink();
    if (chatNavigationBusy()) {
      writeChatLink(get().activeCompanyId, "replace");
      get().setToast("Wait for the schedule request to finish before switching chats.", "error");
      return;
    }
    if (link.kind === "invalid") {
      set({ chatLinkError: "This chat link is invalid. Use a new link copied from the chat's menu." });
      return;
    }
    if (link.kind === "none") {
      const cur = get().activeCompanyId;
      if (!cur) { set({ chatLinkError: null }); return; }
      if (!confirmChatSwitch(get())) { writeChatLink(cur, "replace"); return; }
      ++chatSelectionVersion;
      if (_streamSub) { _streamSub.close(); _streamSub = null; }
      abortMockStream();
      get().clearAttachments();
      set({ activeCompanyId: null, composerDraft: "", notifyOnDoneArmed: false, streamingMessageId: null, chatLinkError: null });
      persistActiveCompanyId(null);
      return;
    }
    if (!get().companiesLoaded) {
      set({ chatLinkError: "Couldn't check this chat link. Retry when your connection is available." });
      return;
    }
    if (!get().companies.some((company) => company.id === link.id)) {
      set({ chatLinkError: "This chat is unavailable. It may have been deleted, or this account may not have access." });
      return;
    }
    if (get().activeCompanyId === link.id && !confirmChatSwitch(get(), true)) {
      writeChatLink(link.id, "replace");
      return;
    }
    const navigation = get().selectCompany(link.id, "none");
    const admitted = get().activeCompanyId === link.id;
    if (admitted) get().closeScheduled();
    else writeChatLink(get().activeCompanyId, "replace");
    await navigation;
  },

  dismissChatLinkError() {
    writeChatLink(get().activeCompanyId, "replace");
    set({ chatLinkError: null, ...(!get().activeCompanyId ? { sidebarOpen: true } : {}) });
  },

  async startSession() {
    const id = get().activeCompanyId;
    if (!id) return;
    set({ sessionStatus: { ...get().sessionStatus, state: "spawning", companyId: id } });
    try {
      const status = await spawnSession(id);
      set({ sessionStatus: status });
    } catch (err: any) {
      set({
        sessionStatus: {
          companyId: id,
          pid: null,
          state: "errored",
          errorMessage: err.message,
        },
      });
      get().setToast(`Spawn failed: ${err.message}`, "error");
    }
  },

  async stopSession() {
    markStopped(get().activeCompanyId);
    /* "Restart session" — what the user calls it via the overflow menu.
     * Kills the live claude process AND deletes the per-company .jsonl
     * on disk. Next message auto-spawns a fresh claude for THE SAME
     * company / project — fresh context, no API-error/corrupted-state
     * baggage. SQLite chat history + agent notes survive (user still
     * sees the conversation, agent still has its persistent memory).
     *
     * This is the resilience hatch for "agent suddenly fails" — Julian
     * can tap Restart and immediately recover without losing the
     * client's context. */
    const cid = get().activeCompanyId;
    if (!cid) {
      // Nothing to restart — fall back to plain kill.
      try {
        await killActiveSession();
      } catch { /* ignore */ }
      return;
    }
    set((s) => ({ sessionStatus: { ...s.sessionStatus, state: "killing" } }));
    abortMockStream();
    try {
      await restartCompanySession(cid);
      set({
        sessionStatus: { companyId: null, pid: null, state: "idle" },
        streamingMessageId: null,
      });
      get().setToast(
        "Session restarted — next message starts with fresh context.",
        "success",
      );
    } catch (err: any) {
      get().setToast(`Restart failed: ${err.message}`, "error");
      // Best-effort fallback: at least kill the process even if jsonl
      // wipe failed.
      try {
        const status = await killActiveSession();
        set({ sessionStatus: status, streamingMessageId: null });
      } catch { /* swallow */ }
    }
  },

  async reconcileSettledHistory(cid) {
    // Read-only repair, never resend. A missed SSE reply is not evidence that
    // the work failed. Preserve newer sends, queue edits and replay events.
    if (historyRecoveryInFlight.has(cid)) return false;
    historyRecoveryInFlight.add(cid);
    const controller = typeof AbortController === "undefined" ? null : new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const before = get();
    const messages = before.messagesByCompany[cid];
    const queued = before.queueByCompany[cid];
    const selection = chatSelectionVersion;
    const lastUser = messages?.findLast((message) => message.role === "user");
    try {
      const { messages: rawHistory, inFlight } = await Promise.race([
        getMessageHistory(cid, controller?.signal),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => { controller?.abort(); reject(new Error("History recovery timed out")); }, 12_000);
        }),
      ]);
      const history = nativeInputMessages(rawHistory, parseNativeInputSnapshot(inFlight?.nativeInput));
      if (!inFlight || inFlight.busy || (inFlight.queueLength ?? 0) > 0) return false;
      // The server must have observed our latest accepted/optimistic turn.
      // Never erase an in-flight POST simply because history is still behind.
      if (lastUser && !history.some((message) => message.id === lastUser.id)) return false;
      const completedUsers = new Set<string>();
      let userId: string | null = null;
      for (const message of history) {
        if (message.role === "user") userId = message.id;
        else if (message.role === "assistant" && userId) completedUsers.add(userId);
      }
      const completed = lastUser ? completedUsers.has(lastUser.id) : history.at(-1)?.role === "assistant";
      let applied = false;
      set((s) => {
        if (chatSelectionVersion !== selection || s.activeCompanyId !== before.activeCompanyId ||
            s.messagesByCompany[cid] !== messages || s.queueByCompany[cid] !== queued ||
            s.streamingMessageId !== before.streamingMessageId ||
            s.pendingByCompany[cid] !== before.pendingByCompany[cid] ||
            s.lastAppliedSeqByCompany[cid] !== before.lastAppliedSeqByCompany[cid] ||
            s.currentEpochByCompany[cid] !== before.currentEpochByCompany[cid]) return s;
        // Polls replace equivalent status objects every 6s. Compare lifecycle
        // fields so a slower history read can finish, but a new turn/process
        // transition cannot be overwritten by its older snapshot.
        const statusBefore = before.sessionStatusByCompany[cid];
        const statusNow = s.sessionStatusByCompany[cid];
        if ((["companyId", "state", "busy", "pid", "spawnedAt", "lastDoneAt"] as const)
          .some((key) => statusNow?.[key] !== statusBefore?.[key])) return s;
        // Nothing new on the server and no reply yet (e.g. a stopped turn): keep the local
        // rows (a stopped turn's tool steps) instead of rebuilding the list every poll.
        const localLast = messages?.at(-1);
        if (!completed && localLast?.role === "assistant" && !localLast.isStreaming && !!localLast.tools?.length &&
            !localLast.content?.trim()) {
          const localIds = new Set((messages ?? []).map((message) => message.id));
          if (history.every((message) => localIds.has(message.id))) return s;
        }
        applied = true;
        const retained = mergeFailedLocalMessages(history, messages ?? [], s.failedUserMsgIds, s.unconfirmedUserMsgIds);
        const next: Partial<SessionState> = {
          messagesByCompany: { ...s.messagesByCompany, [cid]: retained.messages },
          failedUserMsgIds: retained.failedUserMsgIds,
        };
        if (completed) {
          if (cid === s.activeCompanyId) next.streamingMessageId = null;
          const pending = { ...s.pendingByCompany };
          delete pending[cid];
          next.pendingByCompany = pending;
          persistPendingByCompany(pending);
          // Remove only queue rows whose own durable user+reply pair is proven.
          // Unaccepted/new queued prompts are never dropped or sent here.
          if (queued) next.queueByCompany = {
            ...s.queueByCompany,
            [cid]: queued.filter((message) => !message.userMsgId || !completedUsers.has(message.userMsgId)),
          };
          const status = s.sessionStatusByCompany[cid];
          if (status) next.sessionStatusByCompany = {
            ...s.sessionStatusByCompany,
            [cid]: { ...status, busy: false, lastDoneAt: Date.now() },
          };
        }
        return nativeSnapshotPatch(s, cid, inFlight?.nativeInput, next);
      });
      return applied;
    } catch { return false; } // An unavailable history is not proof of failure.
    finally {
      if (timeout !== undefined) clearTimeout(timeout);
      controller?.abort();
      historyRecoveryInFlight.delete(cid);
    }
  },

  async pollAllStatuses() {
    // Samples are renamed/re-keyed by the agent server-side after /cloud-control
    // spins up the demo. The company list otherwise only loads at bootstrap, so
    // refetch it while any sample is still unfinalized (no apiBase yet) → the new
    // business name + key surface within ~6s, no manual reload. Self-stops once
    // finalized. Decoupled from the streaming/status core below.
    if (
      get().companies.some(
        (c) =>
          c.kind === "sample" &&
          !c.apiBase &&
          // Only while the demo is plausibly still being built — caps the
          // refetch so an abandoned, never-finalized sample doesn't poll forever.
          Date.now() - new Date(c.createdAt).getTime() < 30 * 60_000,
      )
    ) {
      void get().loadCompanies();
    }
    try {
      const myGen = ++_resyncGen;
      // Wave 8 race fix (2026-05-28): capture fetch start time so we can
      // detect when polled data is stale relative to a more-recent local
      // optimistic update (specifically: busy=false set by the done-event
      // handler in applyStreamEvent). Without this, the Stop button stayed
      // visible for up to 6 seconds after claude finished responding:
      //   1. pollAllStatuses fires XHR at T (server busy=true at moment of read)
      //   2. Done event arrives at T+0.5s → optimistic busy=false
      //   3. Poll response arrives at T+1s → set() OVERWRITES busy=true (stale)
      //   4. Stop button stuck until next poll tick (6s later) finds busy=false
      // The fix: any local entry whose lastDoneAt > pollFetchAt is newer
      // than this poll's snapshot — keep local busy/lastDoneAt, take
      // everything else from poll (state, errorMessage, spawnedAt).
      const pollFetchAt = Date.now();
      // Runs we showed as compacting BEFORE this snapshot was requested: if the
      // server no longer reports them, it lost them (restart). Keyed on the
      // server's own startedAt, never compared with this device's clock.
      const compactionBefore = get().compactionByCompany;
      const compactingBefore = Object.entries(compactionBefore)
        .filter(([, c]) => c?.state === "compacting").map(([cid, c]) => [cid, c.startedAt] as const);
      const statuses = await getAllSessionStatuses();
      if (myGen !== _resyncGen) return; // a newer poll superseded this one
      {
        // Unread replies: the chat on screen is being read, so a reply landing there
        // is seen (only while the page is actually visible).
        const unread = typeof lastPolledUnread === "function" ? lastPolledUnread() : null;
        if (unread) {
          const next = { ...unread };
          // Marked unread by hand: a dot only (never a new time, so never a new place).
          const manual = typeof lastPolledManualUnread === "function" ? lastPolledManualUnread() ?? {} : {};
          for (const mid of Object.keys(manual)) next[mid] = next[mid] || MANUAL_UNREAD;
          // A Mark as unread still on its way to the server survives a poll that started before it.
          for (const [mid, mat] of _manualUnread) {
            if (next[mid]) _manualUnread.delete(mid);
            else if (Date.now() - mat < 15_000) next[mid] = MANUAL_UNREAD;
            else _manualUnread.delete(mid);
          }
          const active = get().activeCompanyId;
          if (active && next[active] && active !== get().unreadHoldId && typeof document !== "undefined" && document.visibilityState === "visible") {
            delete next[active];
            void Promise.resolve(apiMarkChatRead?.(active)).catch(() => undefined);
          }
          set({ unreadByCompany: next });
        }
      }
      for (const [cid, polled] of Object.entries(statuses)) {
        const current = get().compactionByCompany[cid];
        if (current?.state === "compacting" && current !== compactionBefore[cid]) continue;
        get().applyCompaction(cid, polled?.compaction);
      }
      for (const [cid, startedAt] of compactingBefore) {
        const polled = statuses[cid];
        if (polled?.compaction || polled?.compacting === true) continue;
        if (get().compactionByCompany[cid]?.state === "compacting" && get().compactionByCompany[cid]?.startedAt === startedAt) {
          get().applyCompaction(cid, { state: "idle" });
        }
      }

      // Repair missing replies while the process is warm-but-idle too. Before
      // declaring a dead session, require fresh durable history for that turn.
      const snapshot = get();
      const candidates = new Set(Object.keys(snapshot.pendingByCompany).filter((cid) => snapshot.pendingByCompany[cid]));
      if (snapshot.activeCompanyId && (snapshot.streamingMessageId ||
          snapshot.messagesByCompany[snapshot.activeCompanyId]?.some((message) => message.isStreaming) ||
          snapshot.messagesByCompany[snapshot.activeCompanyId]?.at(-1)?.role === "user" ||
          lastReplyEmpty(snapshot.messagesByCompany[snapshot.activeCompanyId]))) candidates.add(snapshot.activeCompanyId);
      const confirmedHistories = new Map<string, UIMessage[] | undefined>();
      await Promise.all([...candidates].map(async (cid) => {
        const status = statuses[cid];
        if (status?.busy || status?.state === "spawning" || status?.state === "killing") return;
        if (await get().reconcileSettledHistory(cid)) confirmedHistories.set(cid, get().messagesByCompany[cid]);
      }));
      if (myGen !== _resyncGen) return;

      set((s) => {
        // Freshness merge — preserve local optimistic state when it's
        // newer than this poll's fetch time. Only affects `busy` and
        // `lastDoneAt`; all other fields are server-authoritative.
        const mergedStatuses: Record<string, SessionStatus> = {};
        for (const [cid, polled] of Object.entries(statuses)) {
          const local = s.sessionStatusByCompany[cid];
          const localIsNewer =
            local?.lastDoneAt !== undefined &&
            local.lastDoneAt > pollFetchAt;
          if (localIsNewer) {
            mergedStatuses[cid] = {
              ...polled,
              busy: local.busy,
              lastDoneAt: local.lastDoneAt,
            };
          } else {
            mergedStatuses[cid] = polled;
          }
        }

        const next: Partial<SessionState> = { sessionStatusByCompany: mergedStatuses };

        // Reconcile orphan in-flight state. The "server-as-truth contract":
        // if the backend doesn't acknowledge a running session for a company
        // where we locally have streaming/pending state, sweep the orphan.
        // This is the sole authority that clears `streamingMessageId` and
        // per-message `isStreaming` flags based on server state.
        const activeCid = s.activeCompanyId;
        let messagesByCompany = s.messagesByCompany;
        let streamingMessageId = s.streamingMessageId;
        let pendingByCompany = s.pendingByCompany;
        let messagesDirty = false;
        let pendingDirty = false;

        const cidsToCheck = new Set<string>();
        if (streamingMessageId && activeCid) cidsToCheck.add(activeCid);
        for (const cid of Object.keys(pendingByCompany)) {
          if (pendingByCompany[cid]) cidsToCheck.add(cid);
        }

        for (const cid of cidsToCheck) {
          const serverState = mergedStatuses[cid]?.state;
          const isLive =
            serverState === "running" ||
            serverState === "spawning" ||
            serverState === "killing";
          if (isLive) continue;
          if (!confirmedHistories.has(cid) || confirmedHistories.get(cid) !== messagesByCompany[cid]) continue;

          // Server has no live session for this company → it ended outside
          // a graceful flow (backend restart, claude crash, OOM, mid-Bash
          // SIGTERM). Before sweeping ghosts, surface an inline error so
          // the user knows their last turn didn't complete instead of
          // staring at a silently-cleared spinner. The marker is local-only
          // (not persisted to SQLite) — PWA reload makes it disappear,
          // and the user naturally sees the dangling user message with no
          // reply and re-sends. Duplicate-guard: once inserted, the cleanup
          // below clears streamingMessageId + pending, so cidsToCheck won't
          // include this cid on the next poll → no re-insert.
          const msgs = messagesByCompany[cid] ?? [];
          const lastFew = msgs.slice(-3);
          const alreadyNotified = lastFew.some(
            (m) =>
              m.role === "assistant" &&
              typeof m.id === "string" &&
              m.id.startsWith("err-session-ended-"),
          );
          // Only notify if there's actually an orphaned turn worth flagging:
          // either an active streaming placeholder for THIS company, or the
          // last message is a user message awaiting a reply.
          const lastMsg = msgs[msgs.length - 1];
          const hasOrphan =
            (!!streamingMessageId && activeCid === cid) ||
            lastMsg?.role === "user";
          if (!alreadyNotified && hasOrphan) {
            if (!messagesDirty) {
              messagesByCompany = { ...messagesByCompany };
              messagesDirty = true;
            }
            const errMsg: UIMessage = {
              id: `err-session-ended-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: "assistant",
              content:
                "⚠️ This session ended without a saved reply. Check its work before retrying; some actions may already have completed.",
              isStreaming: false,
              createdAt: nowIso(),
            };
            messagesByCompany[cid] = [...(messagesByCompany[cid] ?? []), errMsg];
          }

          // Sweep ghosts: drop empty assistant placeholders, unset isStreaming
          // on any remaining (keeps the new error message intact — it has
          // non-empty content).
          const after = messagesByCompany[cid];
          if (after && after.length > 0) {
            const cleaned = after
              .filter((m) => {
                if (m.role !== "assistant") return true;
                const hasContent =
                  (m.content ?? "").length > 0 ||
                  (m.tools?.length ?? 0) > 0 ||
                  (m.files?.length ?? 0) > 0;
                return hasContent;
              })
              .map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m));
            const changed =
              cleaned.length !== after.length ||
              cleaned.some((m, i) => m !== after[i]);
            if (changed) {
              if (!messagesDirty) {
                messagesByCompany = { ...messagesByCompany };
                messagesDirty = true;
              }
              messagesByCompany[cid] = cleaned;
            }
          }
          if (activeCid === cid && streamingMessageId) {
            streamingMessageId = null;
          }
          if (pendingByCompany[cid]) {
            if (!pendingDirty) {
              pendingByCompany = { ...pendingByCompany };
              pendingDirty = true;
            }
            delete pendingByCompany[cid];
          }
        }

        // SELF-HEALING: server says THIS company is busy but the frontend
        // has NO streaming placeholder for it → recreate one. Catches the
        // "lost placeholder after mid-stream reload" race where selectCompany
        // synthesizes a placeholder but it gets ghost-swept or never receives
        // SSE content before being lost. Without this, the user sees the
        // "Working" pill but no progress chip or response — bewildering and
        // they may double-send thinking it failed. With this, within 6s (one
        // poll tick) the placeholder reappears and SSE replay backfills its
        // content. Idempotent: if a placeholder already exists, no-op.
        for (const [cid, status] of Object.entries(mergedStatuses)) {
          if (!status?.busy) continue;
          const msgs = messagesByCompany[cid] ?? [];
          const lastMsg = msgs[msgs.length - 1];
          const hasStreamingPlaceholder = msgs.some(
            (m) => m.role === "assistant" && m.isStreaming,
          );
          // Only inject if last message is user's (claude's reply is the
          // expected next message) and we lost the placeholder somehow.
          if (!hasStreamingPlaceholder && lastMsg?.role === "user") {
            const placeholderId = `recover-${cid}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            if (!messagesDirty) {
              messagesByCompany = { ...messagesByCompany };
              messagesDirty = true;
            }
            const placeholder: UIMessage = {
              id: placeholderId,
              role: "assistant",
              content: "",
              tools: [],
              isStreaming: true,
              createdAt: nowIso(),
            };
            messagesByCompany[cid] = [...msgs, placeholder];
            // Wire streamingMessageId only if this is the active company AND
            // we don't already have one (avoid stomping a fresh local turn).
            if (cid === activeCid && !streamingMessageId) {
              streamingMessageId = placeholderId;
            }
          }
        }

        if (messagesDirty) next.messagesByCompany = messagesByCompany;
        if (pendingDirty) {
          next.pendingByCompany = pendingByCompany;
          persistPendingByCompany(pendingByCompany);
        }
        if (streamingMessageId !== s.streamingMessageId) {
          next.streamingMessageId = streamingMessageId;
        }
        return next;
      });
    } catch { /* silent — informational poll */ }
  },

  async resyncActive() {
    const cid = get().activeCompanyId;
    if (!cid) {
      // Nothing to resync; still refresh status so RAM chip + sidebar dots
      // update on focus regain.
      void get().pollAllStatuses();
      return;
    }
    // This fetch races the freshly reconnected stream. Keep object identities
    // for scheduled attempts so an older snapshot cannot erase an admission
    // (or its reply) that arrives while the history request is in flight.
    const historyBefore = get();
    const historySelection = chatSelectionVersion;
    const scheduledBeforeFetch = new Map((get().messagesByCompany[cid] ?? [])
      .filter((message) => message.scheduledTurn)
      .map((message) => [message.scheduledTurn!.attempt, message]));
    // Tear down + re-establish SSE. New connection gets a fresh Mode-2
    // replay path on the server (now includes the `done` event via the
    // A1 fix) and a fresh auth token.
    if (_streamSub) {
      _streamSub.close();
      _streamSub = null;
    }
    _streamSub = connectStream(
      cid,
      (e) => get().applyStreamEvent(e),
      getCurrentIdToken,
    );

    // Re-fetch history. If server says inFlight.busy and the last persisted
    // message is the user's, synthesize a placeholder so incoming SSE events
    // have a render target. If local has a stale placeholder but server says
    // !busy, pollAllStatuses (called below) will sweep it.
    //
    // P1-F2 + P1-F3 audit fixes (2026-05-27):
    //   - F2: queue reconciliation (mirror of selectCompany — server is
    //     truth, trim local to match, preserve dispatching head). Without
    //     this, PWA background-then-resume after a queued msg drained leaves
    //     a stale "⏳ queued" chip forever (until next selectCompany).
    //   - F3: gate placeholder synthesis on !s.streamingMessageId. The
    //     earlier code unconditionally inserted a new placeholder + stomped
    //     streamingMessageId even if a live placeholder already existed,
    //     creating a 0-6s window with TWO progress chips after focus regain
    //     mid-stream.
    try {
      const { messages: rawHistory, inFlight } = await getMessageHistory(cid);
      const history = nativeInputMessages(rawHistory, parseNativeInputSnapshot(inFlight?.nativeInput));
      const last = history[history.length - 1];
      const shouldSynthesize = !!inFlight?.busy && last?.role === "user";
      const serverQueueLength = inFlight?.queueLength ?? 0;
      let droppedChips: QueuedMessage[] = [];
      set((s) => {
        if (s.activeCompanyId !== cid) return s;
        if (chatSelectionVersion !== historySelection ||
            s.messagesByCompany[cid] !== historyBefore.messagesByCompany[cid] ||
            s.queueByCompany[cid] !== historyBefore.queueByCompany[cid] ||
            s.lastAppliedSeqByCompany[cid] !== historyBefore.lastAppliedSeqByCompany[cid] ||
            s.currentEpochByCompany[cid] !== historyBefore.currentEpochByCompany[cid]) return s;
        if ((s.messagesByCompany[cid] ?? []).some((message) => message.scheduledTurn &&
            scheduledBeforeFetch.get(message.scheduledTurn.attempt) !== message)) return s;
        const next: Partial<SessionState> = {};
        let finalMessages: UIMessage[] = history;
        if (shouldSynthesize && !s.streamingMessageId) {
          // P1-F3: only synthesize if we don't already have a live placeholder.
          const placeholderId = `resync-${cid}-${Date.now()}`;
          const placeholder: UIMessage = {
            id: placeholderId,
            role: "assistant",
            content: "",
            tools: [],
            isStreaming: true,
            createdAt: new Date().toISOString(),
          };
          finalMessages = [...history, placeholder];
          next.streamingMessageId = placeholderId;
        } else if (shouldSynthesize && s.streamingMessageId) {
          // Wave 5 P0 audit fix: same dead-code bug as selectCompany —
          // history.some(m => m.id === s.streamingMessageId) always
          // returned false (local placeholder ids never match server-
          // generated ids). Replaced with count-based detection: if
          // server-side persisted assistant count grew vs what we had
          // locally (excluding our placeholder), turn completed while
          // we were backgrounded — discard stale placeholder + synthesize
          // fresh one for the NEW in-flight turn.
          const localMsgs = s.messagesByCompany[cid] ?? [];
          const localPersistedReplies = localMsgs.filter(
            (m) => m.role === "assistant" && !m.isStreaming,
          ).length;
          const serverPersistedReplies = history.filter(
            (m) => m.role === "assistant",
          ).length;
          const turnCompletedWhileAway = serverPersistedReplies > localPersistedReplies;
          const existing = !turnCompletedWhileAway
            ? localMsgs.find((m) => m.id === s.streamingMessageId)
            : undefined;
          if (existing) {
            // Turn still in-flight — splice live placeholder onto fresh
            // history so SSE events continue to route to its id.
            finalMessages = [...history, existing];
          } else {
            // Either: (a) turn completed while away (count grew) and the
            // NEW in-flight turn needs its own placeholder, or (b) local
            // lost track of the placeholder. Either way, synthesize fresh.
            const placeholderId = `resync-${cid}-${Date.now()}`;
            finalMessages = [...history, {
              id: placeholderId,
              role: "assistant",
              content: "",
              tools: [],
              isStreaming: true,
              createdAt: new Date().toISOString(),
            }];
            next.streamingMessageId = placeholderId;
          }
        }
        const retained = mergeFailedLocalMessages(finalMessages, s.messagesByCompany[cid] ?? [], s.failedUserMsgIds, s.unconfirmedUserMsgIds);
        // The server's queued list wins over a failed marker for the same id.
        const deduped = hideQueuedDuplicates(retained.messages, retained.failedUserMsgIds,
          new Set((inFlight?.queued ?? []).map((q) => q.userMsgId)));
        next.messagesByCompany = { ...s.messagesByCompany, [cid]: deduped.messages };
        next.failedUserMsgIds = deduped.failed;
        // GUARDRAIL: if the server authoritatively says this session is NOT
        // busy, there is no in-flight turn — so there can be no live streaming
        // placeholder. Clear any stale streamingMessageId so the Composer's
        // Stop button can't linger after a reply already landed. Covers the
        // tight race where a turn completed between our history fetch and the
        // SSE replay window (the in-flight reply is now in `history` above, and
        // shouldSynthesize is false, so finalMessages already dropped the empty
        // placeholder). Server is truth; runs on focus/resume, never racing a
        // fresh local send (which only happens on user action, not focus).
        if (!inFlight?.busy) {
          next.streamingMessageId = null;
          // Also clear the per-company serverBusy flag (the Composer's Stop
          // button reads sessionStatusByCompany[cid].busy, NOT streamingMessageId
          // alone). Without this, a stale busy=true — e.g. the optimistic done-
          // clear was missed because `done` arrived while viewing another company
          // — would keep Stop visible until the next 6s poll. Server says not
          // busy here = no in-flight turn, so clear it deterministically. Stamp
          // lastDoneAt so pollAllStatuses' freshness-merge preserves this clear.
          const existingStatus = s.sessionStatusByCompany[cid];
          if (existingStatus && existingStatus.busy) {
            next.sessionStatusByCompany = {
              ...s.sessionStatusByCompany,
              [cid]: { ...existingStatus, busy: false, lastDoneAt: Date.now() },
            };
          }
        }
        // P1-F2: queue reconciliation (same as selectCompany): rebuilt from
        // the server's queued list, or trimmed to its count.
        const rebuiltQueue = reconcileQueue(s.queueByCompany[cid] ?? [], inFlight?.queued, serverQueueLength, queuedFromServer);
        if (rebuiltQueue) {
          next.queueByCompany = { ...s.queueByCompany, [cid]: rebuiltQueue };
          droppedChips = droppedServerQueued(s.queueByCompany[cid] ?? [], rebuiltQueue);
        }
        return nativeSnapshotPatch(s, cid, inFlight?.nativeInput, next);
      });
      if (droppedChips.length > 0) rememberDroppedQueued(get, cid, droppedChips);
    } catch { /* silent */ }
    // Reconnect/resume: settle every send the server has not confirmed yet.
    if (outboxAll().length > 0 || Object.keys(get().unconfirmedUserMsgIds).length > 0) {
      confirmBackoffMs = 2_000;
      scheduleConfirm(get, 300);
    }

    // Trigger reconciliation immediately so the user doesn't wait up to 6s
    // for the next tick when a placeholder needs sweeping.
    void get().pollAllStatuses();
  },

  /** Reveal the bearer key, build a ready-to-paste curl command, and
   *  copy it to the clipboard. Format mirrors what AddCompanyModal
   *  expects so you can paste it back in (e.g. registering the same
   *  company in another tool like the Telegram bot). */
  async copyCompanyCurl(id) {
    try {
      // Start the clipboard operation BEFORE awaiting the authenticated key
      // request. Safari/PWAs can revoke transient user activation across an
      // await, which made this menu item report success without updating the
      // clipboard. ClipboardItem can resolve its text after the click safely.
      const revealed = revealCompanyKey(id);
      const curlText = revealed.then(({ apiBase, agentKey }) => {
        const profileUrl = `${apiBase.replace(/\/+$/, "")}/profile`;
        return `curl -sSf -H "Authorization: Bearer ${agentKey}" ${profileUrl}`;
      });
      const gestureSafeCopy = copyPromisedTextToClipboard(curlText);

      const [{ name }, curl] = await Promise.all([revealed, curlText]);
      let copied = gestureSafeCopy ? await gestureSafeCopy : false;
      // Desktop browsers that do not support promised ClipboardItems often
      // retain activation long enough for the conventional path.
      if (!copied) copied = await copyTextToClipboard(curl);

      get().setToast(
        copied
          ? `${name}: curl copied to clipboard`
          : "Couldn't copy — clipboard blocked. Open Settings to grab the key manually.",
        copied ? "success" : "error",
      );
    } catch (err: any) {
      get().setToast(`Copy curl failed: ${err.message}`, "error");
    }
  },

  /** Open the client's WordPress site root in a new tab — derived from
   *  the api_base origin so it works without storing the bare site URL
   *  separately. */
  visitCompanyWebsite(id) {
    const company = get().companies.find((c) => c.id === id);
    if (!company) {
      get().setToast("Company not found", "error");
      return;
    }
    if (!company.apiBase) {
      get().setToast("No URL on file for this company", "error");
      return;
    }
    try {
      const origin = new URL(company.apiBase).origin;
      window.open(origin, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      get().setToast(`Bad apiBase URL: ${err.message}`, "error");
    }
  },

  /* ---- Revisions: Attention → Approve ---- */
  async fetchAttention() {
    try {
      const attention = await getAttention();
      set({ attentionByCompany: reconcileApprovalProgress(attention, revisionApprovalTransport, recentlyDismissed, Date.now()) });
    } catch {
      /* silent — informational poll */
    }
  },

  async runRevisionForThread(companyId, threadId, opts = {}) {
    const dkey = `${companyId}:${threadId}`;
    if (revisionApprovalTransport.has(dkey)) return;
    revisionApprovalTransport.set(dkey, { at: Date.now(), phase: "sending" });
    set(s => ({ attentionByCompany: reconcileApprovalProgress(s.attentionByCompany, revisionApprovalTransport, recentlyDismissed, Date.now()) }));
    try {
      const res = await apiRunRevision(companyId, threadId, { note: opts.note });
      // No NEW turn actually started (same activity-cursor already dispatched — e.g. a
      // crashed build with no new client activity). Do NOT fake "Approved" or leave it
      // cleared: un-suppress so it reappears, restore any note, and say so honestly.
      if (res.status === "already_dispatched" || res.status === "nothing_pending") {
        revisionApprovalTransport.delete(dkey);
        if (opts.note) set((s) => ({ composerDraft: s.composerDraft.length > 0 ? s.composerDraft : opts.note! }));
        void get().fetchAttention();
        get().setToast("Nothing new to run — this change was already sent. Skip it to close, or wait for the client's reply.", "success");
        return;
      }
      // Light up the sidebar working pulse for this company.
      const pending = { ...get().pendingByCompany, [companyId]: true };
      persistPendingByCompany(pending);
      set({ pendingByCompany: pending });
      revisionApprovalTransport.set(dkey, { at: Date.now(), phase: "sent" });
      get().setToast(opts.note ? "Approval sent with your instructions." : "Approval sent.", "success");
      void get().pollAllStatuses();
    } catch (err: any) {
      // A failed dispatch (e.g. the client is mid-turn) → un-suppress + re-fetch
      // so the item reappears for a retry, and tell the user why.
      revisionApprovalTransport.delete(dkey);
      if (opts.note) set((s) => ({ composerDraft: s.composerDraft.length > 0 ? s.composerDraft : opts.note! }));
      void get().fetchAttention();
      get().setToast(`Couldn't start: ${err.message}`, "error");
    } finally {
      set(s => ({ attentionByCompany: reconcileApprovalProgress(s.attentionByCompany, revisionApprovalTransport, recentlyDismissed, Date.now()) }));
      void get().fetchAttention();
    }
  },

  async dismissRevision(companyId, threadId) {
    const dkey = `${companyId}:${threadId}`;
    recentlyDismissed.set(dkey, Date.now()); // terminal close, separate from approval progress
    // Optimistic: drop it from the dock at once (terminal close).
    set((s) => {
      const cur = s.attentionByCompany[companyId];
      if (!cur) return {} as Partial<SessionState>;
      const items = cur.items.filter((i) => i.threadId !== threadId);
      return {
        attentionByCompany: { ...s.attentionByCompany, [companyId]: { ...cur, items, count: items.length } },
      };
    });
    try {
      await apiDismissRevision(companyId, threadId);
      get().setToast("Closed — it won't ask again.", "success");
    } catch (err: any) {
      // Couldn't close → un-suppress + re-fetch so it reappears (not silently lost).
      recentlyDismissed.delete(dkey);
      void get().fetchAttention();
      get().setToast(`Couldn't close: ${err.message}`, "error");
    }
  },

  async setRevisionsEnabled(companyId, enabled) {
    try {
      const next = await apiSetRevisionsEnabled(companyId, enabled);
      set((s) => ({
        companies: s.companies.map((c) => (c.id === companyId ? keepFlags(c, next) : c)),
      }));
      get().setToast(
        enabled ? "Revisions watch ON for this client." : "Revisions watch OFF.",
        "success",
      );
      if (enabled) void get().fetchAttention();
    } catch (err: any) {
      get().setToast(`Couldn't update: ${err.message}`, "error");
    }
  },

  async runAllRevisions(companyId, opts = {}) {
    const excludeGated = opts.excludeGated === true;
    const cur = get().attentionByCompany[companyId];
    const all = cur?.items ?? [];
    // In gate-safe mode (the Auto-approve ON sweep) only in-scope items are
    // dispatched; gated/claimActive builds stay visible + waiting for Julian.
    const items = all.filter((it) => it.kind !== 'feature' && !revisionApprovalTransport.has(`${companyId}:${it.threadId}`) && (!excludeGated || (!it.awaitingOwner && !it.claimActive)));
    if (items.length === 0) return;
    items.forEach(it => revisionApprovalTransport.set(`${companyId}:${it.threadId}`, { at: Date.now(), phase: "sending" }));
    set(s => ({ attentionByCompany: reconcileApprovalProgress(s.attentionByCompany, revisionApprovalTransport, recentlyDismissed, Date.now()) }));
    // Draft is owned HERE (like sendMessage): clear it only now that we've committed to
    // dispatching — past the empty-list early-return above — so a render→click race that
    // empties the list can never wipe the note with no dispatch. The catch restores on failure.
    if (opts.note && !excludeGated) set({ composerDraft: "" });
    try {
      // note only on a manual approve-all (never on the gate-safe auto sweep).
      const res = await apiRunRevision(companyId, undefined, { excludeGated, note: excludeGated ? undefined : opts.note }); // no threadId ⇒ work ALL pending
      // No NEW turn started (already-dispatched cursor / nothing pending) → be honest,
      // restore the note, and re-show the items rather than faking success.
      if (res.status === "already_dispatched" || res.status === "nothing_pending") {
        items.forEach(it => revisionApprovalTransport.delete(`${companyId}:${it.threadId}`));
        if (opts.note) set((s) => ({ composerDraft: s.composerDraft.length > 0 ? s.composerDraft : opts.note! }));
        void get().fetchAttention();
        get().setToast("Nothing new to run — already sent. Skip to close, or wait for the client's reply.", "success");
        return;
      }
      const pending = { ...get().pendingByCompany, [companyId]: true };
      persistPendingByCompany(pending);
      set({ pendingByCompany: pending });
      items.forEach(it => revisionApprovalTransport.set(`${companyId}:${it.threadId}`, { at: Date.now(), phase: "sent" }));
      get().setToast(
        excludeGated
          ? "Auto-approve ON. In-scope backlog submitted."
          : opts.note
            ? "Approvals sent with your instructions."
            : "Approvals sent.",
        "success",
      );
      void get().pollAllStatuses();
    } catch (err: any) {
      items.forEach((it) => revisionApprovalTransport.delete(`${companyId}:${it.threadId}`));
      // Restore the typed note if it was consumed (mirror sendMessage) so a rejected
      // approve never destroys Julian's text.
      if (opts.note) set((s) => ({ composerDraft: s.composerDraft.length > 0 ? s.composerDraft : opts.note! }));
      void get().fetchAttention();
      get().setToast(`Couldn't start: ${err.message}`, "error");
    } finally {
      set(s => ({ attentionByCompany: reconcileApprovalProgress(s.attentionByCompany, revisionApprovalTransport, recentlyDismissed, Date.now()) }));
      void get().fetchAttention();
    }
  },

  async setAutoRevision(companyId, enabled) {
    try {
      const next = await apiSetAutoRevision(companyId, enabled);
      set((s) => ({ companies: s.companies.map((c) => (c.id === companyId ? keepFlags(c, next) : c)) }));
      get().setToast(enabled ? "Auto-approve ON for this client." : "Auto-approve OFF.", "success");
    } catch (err: any) {
      get().setToast(`Couldn't update: ${err.message}`, "error");
    }
  },

  async setAutomationFreeze(companyId, frozen, reviewedPending = false) {
    try {
      const next = await apiSetAutomationFreeze(companyId, frozen, reviewedPending);
      set((s) => ({ companies: s.companies.map((c) => c.id === companyId ? keepFlags(c, next) : c) }));
      await get().fetchAttention();
      get().setToast(frozen ? 'Automation frozen. Use Stop to end a running turn.' : 'Automation enabled; existing Stop pause is unchanged.', 'success');
    } catch (err: any) { get().setToast(`Could not update automation: ${err.message}`, 'error'); }
  },

  async toggleAutoApprove(companyId) {
    if (autoToggleInFlight.has(companyId)) return; // re-entrancy guard: ignore rapid re-clicks
    const company = get().companies.find((c) => c.id === companyId);
    if (!company) return;
    const turningOn = company.autoRevision !== true;
    autoToggleInFlight.add(companyId);
    try {
      // OFF path: just disarm auto. Watch stays on (pings + Attention dock remain).
      if (!turningOn) {
        await get().setAutoRevision(companyId, false);
        return;
      }

      // ON path. Surfacing is universal now — there's no separate "watch" to arm —
      // so just flip auto on, confirm it stuck, then clear what's already pending.
      await get().setAutoRevision(companyId, true);
      if (get().companies.find((c) => c.id === companyId)?.autoRevision !== true) return;

      // The /auto endpoint owns the automatic backlog sweep. Never submit a second
      // manual-looking Approve-all request here: it could outlive a later OFF toggle.
      await get().fetchAttention();
    } finally {
      autoToggleInFlight.delete(companyId);
    }
  },

  async compactCompany(id) {
    if (get().compactionByCompany[id]?.state === "compacting") return;
    try {
      const started = await apiCompactSession(id);
      get().applyCompaction(id, started);
    } catch (err: unknown) {
      if (err instanceof CompactRefusedError) {
        // A refusal is guidance, not a failure: nothing was changed.
        get().setToast(err.code === "compact_unavailable_in_operations" ? COMPACT_UNAVAILABLE_TOAST : err.message, "info");
        return;
      }
      // The POST may have reached the server even though the answer did not
      // reach us: read server state before claiming anything failed.
      try {
        const state = await getCompactionState(id);
        if (state) get().applyCompaction(id, state);
        if (state?.state === "compacting") return;
      } catch { /* still offline */ }
      get().setToast("Couldn't start Compact — check your connection and try again.", "error");
    }
  },

  applyCompaction(cid, next) {
    if (!next || typeof next.state !== "string") return;
    const prev = get().compactionByCompany[cid];
    const at = (c?: CompactionState) => (c?.startedAt ? Date.parse(c.startedAt) : NaN);
    // The server no longer knows the run we are showing as compacting: it
    // answered idle (its run registry is in memory, so a backend restart
    // loses it) or reports a NEWER run. Close ours as not confirmed instead of
    // keeping "Compacting…" (and the Stop refusal) until a reload. An OLDER run
    // is only a late snapshot/replay and is ignored by the guard below.
    // (Both times are the server's own startedAt; this device's clock is never used.)
    const newerRun = next.state !== "idle" && Number.isFinite(at(next)) && Number.isFinite(at(prev)) && at(next) > at(prev);
    if (prev?.state === "compacting" && (next.state === "idle" || newerRun)) {
      closeLostCompaction(cid, prev);
      if (next.state === "idle") return;
      // A newer run (e.g. started from another device after the restart): adopt it below.
    }
    // Never step back to an older run's state (a late poll or replay).
    if (prev && Number.isFinite(at(prev)) && Number.isFinite(at(next)) && at(next) < at(prev)) return;
    if (prev && prev.state === next.state && prev.startedAt === next.startedAt && prev.finishedAt === next.finishedAt) return;
    set((s) => ({ compactionByCompany: { ...s.compactionByCompany, [cid]: next } }));
    if (next.state === "compacting") {
      ensureCompactionPoll(cid);
      return;
    }
    const finished = prev?.state === "compacting" && prev.startedAt === next.startedAt &&
      (next.state === "done" || next.state === "failed");
    if (!finished) return;
    const line = next.state === "done"
      ? (next.text || "Context compacted.")
      : (next.error || "Compaction failed. Your chat is unchanged.");
    set((s) => {
      const msgs = s.messagesByCompany[cid];
      if (!msgs || (next.messageId && msgs.some((m) => m.id === next.messageId))) return {};
      const row: UIMessage = { id: next.messageId ?? `sys-compact-local-${next.finishedAt ?? Date.now()}`,
        role: "system", kind: undefined, content: line, createdAt: next.finishedAt ?? nowIso() };
      return { messagesByCompany: { ...s.messagesByCompany, [cid]: [...msgs, row] } };
    });
    // A timeout is "not confirmed", not a failure: the CLI may still finish it.
    if (next.state === "failed") get().setToast(line, next.errorCode === "compact_timeout" ? "info" : "error");
    // Compaction end is a turn boundary: queued messages now drain on the
    // server, so give the next one its bubble + live placeholder. With nothing
    // queued, the Stop button must not linger until the next status poll.
    if ((get().queueByCompany[cid] ?? []).length > 0) {
      if (get().activeCompanyId === cid) get().materializeQueuedHead(cid, { skipIfDispatching: true });
    } else {
      set((s) => {
        const status = s.sessionStatusByCompany[cid];
        if (!status) return {};
        const updated = { ...status, busy: false, compacting: false, lastDoneAt: Date.now() };
        return {
          sessionStatusByCompany: { ...s.sessionStatusByCompany, [cid]: updated },
          ...(s.sessionStatus.companyId === cid ? { sessionStatus: { ...s.sessionStatus, busy: false, lastDoneAt: Date.now() } } : {}),
        };
      });
    }
  },

  /** Soft-reset for one company: wipe local message state + tell the
   *  backend to nuke the SQLite rows + claude session jsonl. Agent
   *  notes/<company>.md is preserved (persistent memory survives). */
  async clearChatHistory(id) {
    sendAdmissionEpoch.set(id, (sendAdmissionEpoch.get(id) ?? 0) + 1);
    const resumeOrderedSends = pauseOrderedSends(id);
    // Other tabs may add to shared storage after the server clears. Only this
    // operation's captured IDs belong to its cleanup, never the later outbox.
    const clearedSendIds = new Set([...sendOrderEntries(id).map(entry => entry.userMsgId),
      ...(get().messagesByCompany[id] ?? []).filter(message => message.role === "user").map(message => message.id),
      ...(get().queueByCompany[id] ?? []).map(input => input.userMsgId).filter((value): value is string => !!value)]);
    try {
      await confirmClearSendOrder(id);
      const { cleared, nativeInput } = await apiClearChatHistory(id);
      // Never recover (re-send) a pre-clear send into the fresh chat: forget
      // every outbox entry and pending/failed marker of this chat.
      forgetChatSends(get, set, id, clearedSendIds);
      // Wipe local state for this company too — backend has authoritative
      // record but we want immediate UI feedback (no flash of stale msgs).
      abortMockStream();
      const timer = compactionPolls.get(id);
      if (timer) { clearInterval(timer); compactionPolls.delete(id); }
      set((s) => ({
        messagesByCompany: { ...s.messagesByCompany, [id]: [] },
        queueByCompany: { ...s.queueByCompany, [id]: [] },
        // The backend forgets the chat's Compact run too: no stale state or line.
        compactionByCompany: Object.fromEntries(Object.entries(s.compactionByCompany).filter(([cid]) => cid !== id)),
        completedScheduledReplayByCompany: { ...s.completedScheduledReplayByCompany, [id]: false },
        // The sidebar must not keep previewing a message that was just wiped.
        chatActivity: withoutKey(s.chatActivity, id),
        streamingMessageId: null,
        sessionStatus:
          s.sessionStatus.companyId === id
            ? { companyId: null, pid: null, state: "idle" }
            : s.sessionStatus,
      }));
      // Fence stale receipts after the wipe, while preserving a newer snapshot
      // from another tab that already started fresh work after the server clear.
      if (nativeInput) get().applyNativeInputs(id, nativeInput);
      // The server bumped its /activity cache on the delete: this read is fresh.
      void get().refreshChatActivity();
      get().setToast(
        cleared > 0
          ? `Chat history cleared (${cleared} messages). Agent notes preserved.`
          : "Chat history already empty. Agent notes preserved.",
        "success",
      );
      if (get().nativeInputsByCompany[id]) {
        void getMessageHistory(id).then(({ inFlight }) => get().applyNativeInputs(id, inFlight?.nativeInput)).catch(() => { /* Keep uncertainty until a server snapshot arrives. */ });
      }
    } catch (err: any) {
      get().setToast(`Clear failed: ${err.message}`, "error");
      throw err;
    } finally {
      resumeOrderedSends();
    }
  },

  /** Cancel ONE still-waiting queued message (the ✕ on a pending bubble).
   *  Additive + isolated: only touches queueByCompany (+ optionally restores
   *  the text to the composer). Does NOT touch messagesByCompany, the drain,
   *  streamingMessageId, or Stop — so it cannot affect an in-flight turn. */
  applyNativeInputs(companyId, raw, speculativeAssistantId, nativeHistory) {
    if (!parseNativeInputSnapshot(raw)) return false;
    set(s => nativeSnapshotPatch(s, companyId, raw, {}, speculativeAssistantId, nativeHistory));
    return true;
  },

  async sendNativeInputNow(companyId, inputId) {
    const input = get().nativeInputsByCompany[companyId]?.inputs.find(item => item.userMsgId === inputId);
    if (!input?.canSendNow) return;
    const response = await sendNativeInputNow(companyId, inputId);
    if (!get().applyNativeInputs(companyId, response)) throw new Error("Missing input status");
  },

  async cancelNativeInput(companyId, inputId) {
    const input = get().nativeInputsByCompany[companyId]?.inputs.find(item => item.userMsgId === inputId);
    if (!input?.canCancel) return;
    const wasUnsent = input.state === "queued";
    const response = await cancelQueuedMessage(companyId, inputId);
    if (!get().applyNativeInputs(companyId, response)) throw new Error("Missing input status");
    if (!response.cancelled) { get().setToast("Already sent to the agent, so it can't be removed. Send a correction instead.", "error"); return; }
    if ((response as any).mayHaveRead || !wasUnsent) { get().setToast(MAY_HAVE_READ_TOAST, "info"); return; }
    if (!get().restoreToComposer(companyId, input.text, input.files, input.queuedAt)) { get().setToast(REMOVED_TOAST, "info"); return; }
    get().setToast(TAKEN_BACK_TOAST, "info");
  },

  async editQueued(companyId, inputId, text) {
    const native = get().nativeInputsByCompany[companyId]?.inputs.find(item => item.userMsgId === inputId);
    const local = (get().queueByCompany[companyId] ?? []).find(q => (q.userMsgId ?? q.id) === inputId);
    if (!native && !local?.userMsgId) return "error";
    let result: Awaited<ReturnType<typeof editQueuedMessage>>;
    try { result = await editQueuedMessage(companyId, inputId, text); }
    catch { get().setToast("Couldn't save the change. Check your connection and try again.", "error"); return "error"; }
    if (native || result.body.nativeInput) get().applyNativeInputs(companyId, result.body);
    if (result.status === 200 && result.body.edited) {
      if (!native) {
        set(s => ({ queueByCompany: { ...s.queueByCompany,
          [companyId]: (s.queueByCompany[companyId] ?? []).map(q => (q.userMsgId ?? q.id) === inputId ? { ...q, text } : q) } }));
      }
      // A later same-id resend (outbox) carries the edited words.
      const entry = outboxFind(inputId);
      if (entry) outboxSave({ ...entry, text });
      get().setToast("Saved.", "info");
      return "edited";
    }
    if (result.status === 409) {
      const removed = result.body.error === "removed";
      get().setToast(removed ? "This message was already removed." : "Already sent to the agent, so it can't be changed. Send a correction instead.", "error");
      return removed ? "removed" : "already_sent";
    }
    get().setToast(result.body.detail || "Couldn't save the change. Try again.", "error");
    return result.status >= 400 && result.status < 500 ? "refused" : "error";
  },

  holdQueuedForEdit(companyId, inputId, editing) {
    if (!get().nativeInputsByCompany[companyId]?.inputs.some(item => item.userMsgId === inputId)) return;
    void holdQueuedMessage(companyId, inputId, editing)
      .then(response => { get().applyNativeInputs(companyId, response); })
      .catch(() => { /* the edit itself still answers 409 if it was handed over */ });
  },

  restoreToComposer(companyId, text, files, order) {
    const s = get();
    if (s.activeCompanyId !== companyId) return false;
    const draft = stackTakenBack(companyId, s.composerDraft, text ?? "", order);
    const have = new Set(s.attachments.map(a => a.serverPath).filter(Boolean));
    const added = (files ?? []).filter(path => path && !have.has(path)).map(path => ({ id: newId("att"),
      filename: (path.split("/").pop() || "Attachment").replace(/^\d{13}-(?=.)/, ""),
      status: "ready" as const, serverPath: path, kind: attachKindFor(path) }));
    set({ composerDraft: draft, attachments: [...s.attachments, ...added] });
    // Focus is cosmetic: it can never throw into (and so undo) a confirmed take-back.
    try { if (typeof window !== "undefined" && typeof CustomEvent === "function") window.dispatchEvent(new CustomEvent("composer-focus")); } catch { /* no focus */ }
    return true;
  },

  async undoRemoveQueued(companyId, text, files) {
    const s = get();
    if (s.activeCompanyId !== companyId) { get().setToast("Open that chat to send it again.", "info"); return; }
    if (s.composerDraft.trim() || s.attachments.length) { get().setToast("Finish or clear the message you are writing first.", "info"); return; }
    set({ composerDraft: text, attachments: files.map(path => ({ id: newId("att"), filename: path.split("/").pop() || "Attachment",
      status: "ready" as const, serverPath: path, kind: attachKindFor(path) })) });
    await get().sendMessage();
  },

  async cancelQueued(companyId, queuedId) {
    const arr0 = get().queueByCompany[companyId] ?? [];
    const idx0 = arr0.findIndex((m) => m.id === queuedId);
    if (idx0 === -1) return;
    const entry = arr0[idx0]!;
    // Never cancel the one already dispatching (it's left the server queue and
    // is being sent to claude) — the UI hides ✕ on it, but guard anyway.
    if (entry.dispatching) {
      get().setToast("That message already started — can't cancel it now.", "info");
      return;
    }
    let prevOutbox: OutboxEntry | undefined;
    let prevUnconfirmed: SessionState["unconfirmedUserMsgIds"][string] | undefined;
    // Optimistic remove + restore text to the composer (only if this company is
    // active AND the composer is empty) so the user can edit & resend in a tap.
    set((s) => ({
      queueByCompany: {
        ...s.queueByCompany,
        [companyId]: (s.queueByCompany[companyId] ?? []).filter((m) => m.id !== queuedId),
      },
    }));
    // Next-turn messages come after every current-turn (native) one; ties keep click order.
    const takeBack = () => {
      let restored = false;
      try { restored = get().restoreToComposer(companyId, entry.text, entry.attachmentPaths ?? [], 1e15 + idx0); } catch { /* still cancelled */ }
      get().setToast(restored ? TAKEN_BACK_TOAST : REMOVED_TOAST, "info");
    };
    try {
      const userMsgId = entry.userMsgId;
      if (!userMsgId) {
        // No server cancel key (shouldn't happen for a real send) — local-only.
        takeBack();
        return;
      }
      // A cancelled send is never recovered/resent — also an unconfirmed chip
      // whose POST may still arrive (the server tombstones its id).
      prevOutbox = outboxFind(userMsgId);
      prevUnconfirmed = get().unconfirmedUserMsgIds[userMsgId];
      forgetRemovedSends(set, [userMsgId]);
      if (prevOutbox?.waitingForSend) { takeBack(); return; }
      const { cancelled, removed } = await cancelQueuedMessage(companyId, userMsgId);
      if (!cancelled && !removed) {
        // Already delivered / being sent: it is not removed, so let the normal
        // confirmation settle it (its bubble comes from history).
        locallyRemoved.delete(userMsgId);
      }
      // Only a confirmed cancel goes back to the box, so it can never be both sent and restored.
      if (cancelled || removed) takeBack();
      else get().setToast("Already sent to the agent, so it can't be removed. Send a correction instead.", "error");
    } catch (err: any) {
      // Not cancelled: it may still be queued on the server. Keep tracking it.
      if (entry.userMsgId) {
        locallyRemoved.delete(entry.userMsgId);
        if (prevOutbox) outboxSave(prevOutbox);
        if (prevUnconfirmed) set((s) => ({ unconfirmedUserMsgIds: { ...s.unconfirmedUserMsgIds, [entry.userMsgId!]: prevUnconfirmed! } }));
      }
      // Cancel didn't confirm — re-insert at its original spot so we don't
      // silently send something the user thinks they cancelled. If the backend
      // DID remove it, the queue-length reconciliation on next focus trims the
      // stale local entry. Re-add is the safe direction.
      set((s) => {
        const cur = [...(s.queueByCompany[companyId] ?? [])];
        if (!cur.some((m) => m.id === entry.id)) {
          cur.splice(Math.min(idx0, cur.length), 0, entry);
        }
        return { queueByCompany: { ...s.queueByCompany, [companyId]: cur } };
      });
      get().setToast(`Couldn't cancel — kept it queued: ${err.message}`, "error");
    }
  },

  /* ---- Composer ---- */
  setComposerDraft(s) {
    set({ composerDraft: s });
  },

  async addAttachmentFromFile(file: File) {
    const id = newId("att");
    const mimeType = file.type || "application/octet-stream";
    const isImage = mimeType.startsWith("image/");
    const isPdf = mimeType === "application/pdf";
    const kind: AttachItem["kind"] = isImage ? "image" : isPdf ? "pdf" : "file";

    const initial: AttachItem = {
      id,
      filename: file.name,
      mimeType,
      sizeBytes: file.size,
      status: "compressing",
      kind,
    };
    set((s) => ({ attachments: [...s.attachments, initial] }));

    try {
      let blob: Blob = file;
      let filename = file.name;
      let blobUrl: string | undefined;
      if (isImage) {
        const compressed = await compressImage(file);
        blob = compressed.blob;
        // Force JPEG extension since worker always outputs jpeg.
        filename = filename.replace(/\.[^.]+$/, "") + ".jpg";
        blobUrl = URL.createObjectURL(blob);
      } else {
        // Local preview for non-image files: not shown, but blobUrl can be used
        // for download. We skip it to avoid leaking object URLs.
      }
      const upload = await uploadFile(blob, filename, get().activeCompanyId ?? undefined);
      set((s) => ({
        attachments: s.attachments.map((a) =>
          a.id === id
            ? {
                ...a,
                status: "ready",
                blobUrl,
                serverPath: upload.path,
                kind: upload.kind,
                filename,
                sizeBytes: upload.sizeBytes,
              }
            : a,
        ),
      }));
    } catch (err: any) {
      set((s) => ({
        attachments: s.attachments.map((a) =>
          a.id === id ? { ...a, status: "error", error: err.message } : a,
        ),
      }));
      get().setToast(`Upload failed: ${err.message}`, "error");
    }
  },

  async replaceAttachmentImage(id: string, file: File) {
    const original = get().attachments.find((a) => a.id === id);
    if (!original || original.status !== "ready") return;
    // Spinner over the old thumbnail while the edited copy compresses and uploads.
    set((s) => ({
      attachments: s.attachments.map((a) => (a.id === id ? { ...a, status: "compressing" } : a)),
    }));
    let blobUrl: string | undefined;
    try {
      const compressed = await compressImage(file);
      const filename = file.name.replace(/\.[^.]+$/, "") + ".jpg";
      blobUrl = URL.createObjectURL(compressed.blob);
      const upload = await uploadFile(compressed.blob, filename, get().activeCompanyId ?? undefined);
      let oldUrl: string | undefined;
      let applied = false;
      set((s) => ({
        attachments: s.attachments.map((a) => {
          if (a.id !== id) return a;
          applied = true;
          oldUrl = a.blobUrl;
          return {
            ...a,
            status: "ready",
            blobUrl,
            serverPath: upload.path,
            kind: upload.kind,
            filename,
            sizeBytes: upload.sizeBytes,
          };
        }),
      }));
      // Removed (or the chat was sent) meanwhile: drop the new preview instead.
      if (!applied) URL.revokeObjectURL(blobUrl);
      else if (oldUrl && oldUrl !== blobUrl) URL.revokeObjectURL(oldUrl);
    } catch {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      set((s) => ({
        attachments: s.attachments.map((a) => (a.id === id ? { ...a, status: "ready" } : a)),
      }));
      get().setToast("Couldn't save your drawing. The original image is kept.", "error");
    }
  },

  removeAttachment(id) {
    set((s) => ({
      attachments: s.attachments.filter((a) => {
        if (a.id === id && a.blobUrl) URL.revokeObjectURL(a.blobUrl);
        return a.id !== id;
      }),
    }));
  },

  clearAttachments() {
    set((s) => {
      for (const a of s.attachments) if (a.blobUrl) URL.revokeObjectURL(a.blobUrl);
      return { attachments: [] };
    });
  },

  setThinkingLevel(l) {
    // Per-CHAT effort: persist on the active company (clients.json effort_level).
    // Optimistic update of the company row; revert + toast on API failure.
    const s = get();
    const id = s.activeCompanyId;
    if (!id) return;
    const company = s.companies.find((c) => c.id === id);
    const prev = (company?.effortLevel ?? DEFAULT_THINKING) as ThinkingLevel;
    if (prev === l) return;
    set({ companies: s.companies.map((c) => (c.id === id ? { ...c, effortLevel: l } : c)) });
    apiSetEffort(id, l).catch((err: any) => {
      set({ companies: get().companies.map((c) => (c.id === id ? { ...c, effortLevel: prev } : c)) });
      get().setToast(`Couldn't set thinking level: ${err?.message ?? err}`, "error");
    });
  },
  cycleThinkingLevel() {
    const s = get();
    const id = s.activeCompanyId;
    if (!id) return;
    const company = s.companies.find((c) => c.id === id);
    const cur = (company?.effortLevel ?? DEFAULT_THINKING) as ThinkingLevel;
    // ULTRA (ultracode) is offered ONLY on samples — a client cycles the
    // Medium→Max ladder, a sample gets the extra Ultra step. A stored "low" (not in
    // either ladder) steps to the first entry, Medium. The backend refuses ultra for a client
    // too, so this is UI convenience on top of a hard server-side rule.
    const order = company?.kind === "sample" ? THINKING_ORDER_SAMPLE : THINKING_ORDER;
    const idx = order.indexOf(cur);
    const next = order[(idx < 0 ? 0 : idx + 1) % order.length]!;
    get().setThinkingLevel(next);
  },
  setNotifyOnDoneArmed(b) {
    set({ notifyOnDoneArmed: b });
  },
  clearFailedMarker(msgId) {
    set((s) => {
      if (!(msgId in s.failedUserMsgIds)) return {} as Partial<SessionState>;
      const next = { ...s.failedUserMsgIds };
      delete next[msgId];
      return { failedUserMsgIds: next };
    });
  },

  async sendMessage() {
    const state = get();
    const submittedDraft = state.composerDraft;
    const text = submittedDraft.trim();
    const cid = state.activeCompanyId;
    // Compute ready attachments BEFORE the guard: an attachment-only send
    // (image with no caption) is valid — it's the natural mobile gesture and
    // the Composer enables Send for it (canSend allows hasReadyAttachment).
    // The old guard bailed on empty text, so a caption-less image send lit up
    // the button but silently did nothing. Now we only bail when there is
    // neither text NOR a ready attachment.
    const readyAttachments = state.attachments.filter(
      (a) => a.status === "ready" && a.serverPath,
    );
    const attachmentPaths = readyAttachments.map((a) => a.serverPath!);
    if (!cid || (!text && readyAttachments.length === 0)) return;
    if (orderedSendPauses.has(cid)) {
      get().setToast("Wait for Stop or Clear to finish. Your draft has been kept.", "info");
      return;
    }
    const pendingBeforeSend = state.pendingByCompany[cid] === true;

    // Typed "/compact" is a control, not a message: it runs native Compact
    // (never a user bubble, never sent to the agent). With extra text it is
    // refused and the draft is kept. /clear etc. unaffected.
    if (/^\/compact(?:\s|$)/i.test(text) && readyAttachments.length === 0) {
      if (text.toLowerCase() !== "/compact") {
        get().setToast("Use Compact without extra text.", "info");
        return;
      }
      set({ composerDraft: "" });
      void get().compactCompany(cid);
      return;
    }

    // PER-CHAT SEND GUARD (ported from Build's sendAdmissionByCompany). A second
    // tap while this send is still being admitted — e.g. during a cold start,
    // when the draft is still in the composer — must not POST it again.
    if (sendAdmissionByCompany.has(cid)) {
      get().setToast("Still sending your last message…", "info");
      return;
    }
    sendAdmissionByCompany.add(cid);
    const admissionEpoch = sendAdmissionEpoch.get(cid) ?? 0;
    // Own only admission of this draft, not its network round trip. Once its
    // snapshot is in the outbox, a different draft may be admitted immediately.
    // The older finally must never release a newer send's admission guard.
    let admissionHeld = true;
    const releaseAdmission = () => {
      if (!admissionHeld) return;
      admissionHeld = false;
      sendAdmissionByCompany.delete(cid);
    };
    // A cold send marks this chat "spawning" locally (the POST starts it on the server). Settle
    // that marker from the POST's answer unless the server's own status event already did.
    let coldMarked = false;
    const settleColdStatus = (status: SessionStatus) => {
      if (!coldMarked) return;
      coldMarked = false;
      const cur = get().sessionStatus;
      if (cur.companyId === cid && cur.state === "spawning") set({ sessionStatus: status });
    };
    try {
    // Recency: optimistically float this company to the top of the sidebar
    // (the backend also persists last_active_at on the POST). The sidebar sorts
    // by lastActiveAt, so this re-orders instantly without waiting for a refetch.
    {
      const nowIso = new Date().toISOString();
      set((s) => ({
        companies: s.companies.map((c) =>
          c.id === cid ? { ...c, lastActiveAt: nowIso } : c,
        ),
      }));
    }

    // Cold chat (not running here). Julian (16 Oct 2026): the bubble shows and the box clears
    // on tap, so Send never looks dead. Julian (20 Oct 2026): tap Send, close the app, and the
    // message must still go. It used to await startSession (10-60 s) before POSTing, so closing
    // the app in that window lost it. The POST now goes at once: the server's message route
    // starts the chat itself (ensureLive, under the chat's own lock, like /spawn), saves/queues
    // the message, delivers it whether or not this phone is still connected, dedupes by
    // userMsgId, and answers capacity / waitingForSlot / "removed" on the POST. The pill shows
    // Starting locally until the server's status event, or the POST's answer, settles it.
    let shown: UIMessage | null = null;
    const cold = state.sessionStatus.state !== "running" || state.sessionStatus.companyId !== cid;
    if (cold) {
      // Only when it will go straight through: a send that will queue behind a turn
      // shows as a queued chip instead (the bubble would flash and vanish).
      const willQueue = !!state.streamingMessageId || state.sessionStatusByCompany[cid]?.busy === true ||
        state.compactionByCompany[cid]?.state === "compacting" || !!captureSendOrder(cid).predecessorId;
      if (!willQueue) {
        shown = {
          id: newId("u"),
          role: "user",
          content: text,
          createdAt: nowIso(),
          userAttachments: readyAttachments.map((a) => ({
            serverPath: a.serverPath!,
            filename: a.filename,
            mimeType: a.mimeType,
            sizeBytes: a.sizeBytes,
            kind: a.kind ?? "file",
            blobUrl: a.blobUrl,
          })),
        };
        const bubble = shown;
        set((s) => ({
          messagesByCompany: { ...s.messagesByCompany, [cid]: [...(s.messagesByCompany[cid] ?? []), bubble] },
          composerDraft: s.composerDraft === submittedDraft ? "" : s.composerDraft,
          attachments: s.attachments.filter(a => !readyAttachments.some(sent => sent.id === a.id)),
        }));
      }
      // No toast: the header capsule and the request footer already say "Starting".
      set({ sessionStatus: { ...get().sessionStatus, state: "spawning", companyId: cid } });
      coldMarked = true;
    }
    // The bubble is already showing: both paths below reuse it (same id) and
    // never touch the box again, so anything typed since stays put.
    const shownBubble = shown;
    // A send that never reached the agent (refused, or removed by this tab's own Stop /
    // Clear) gives back its draft, files and /notify — only on this chat's own screen:
    // another chat's composer must never receive it (context-corruption rule).
    const giveBack = (notify: boolean): boolean => {
      if (get().activeCompanyId !== cid) return false;
      set((s) => ({
        composerDraft: s.composerDraft.length > 0 ? s.composerDraft : submittedDraft,
        attachments: [...readyAttachments.filter((a) => !s.attachments.some((b) => b.id === a.id)), ...s.attachments],
        notifyOnDoneArmed: notify || s.notifyOnDoneArmed,
      }));
      return true;
    };

    // If currently streaming → POST to backend immediately AND track
    // locally for the visual queue chip. The backend server-side-enqueues
    // because queue.isBusy() is true; the message is now DURABLE (lives
    // on Hetzner, survives PWA close + WiFi drop). When the current turn
    // finishes, the backend auto-drains and processes this message — the
    // local drain logic (in the `done` SSE handler) just synthesizes a
    // placeholder so the incoming SSE events have somewhere to render.
    // BUSY = a turn is in flight for this company. TWO independent sources:
    //   • streamingMessageId — a turn THIS tab started (it owns the local
    //     streaming placeholder).
    //   • sessionStatusByCompany[cid].busy — the AUTHORITATIVE server flag
    //     (queue.isBusy). It is ALSO true for turns this tab never started: an
    //     auto-approve revision, an Attention-dock Approve, or a send from
    //     another device/phone.
    // Gating on streamingMessageId alone meant that while an AUTO-REVISION was
    // running we fell through to the immediate-send path: we inserted an
    // optimistic bubble, the backend (correctly) enqueued the message, and the
    // orphan bubble then evaporated on the next history poll — because a queued
    // message isn't persisted until drain. The message LOOKED lost though it was
    // safely queued, and no "N queued" chip ever appeared.
    // A native Compact holds the server's busy fence too: queue behind it.
    const serverBusy = state.sessionStatusByCompany[cid]?.busy === true ||
      state.compactionByCompany[cid]?.state === "compacting";
    if (state.streamingMessageId || serverBusy || captureSendOrder(cid).predecessorId) {
      // DEFERRED-BUBBLE QUEUE (Pain-A fix). We do NOT insert the user bubble
      // into the transcript now. If we did, it would sit there WHILE the
      // previous reply is still streaming — i.e. appear "before the response
      // is finished" — and on any history reload the backend's old
      // persist-at-POST timestamp made it render ABOVE the reply entirely.
      // Instead we only track it in queueByCompany (the composer shows an
      // "N queued" chip) and POST it. The bubble is materialized in the `done`
      // drain handler — AFTER the previous reply lands — immediately followed
      // by its own reply placeholder. Result: basic chat order, you → reply1
      // → you → reply2, both live and after reload.
      const userMsgId = shownBubble?.id ?? newId("u");
      const sendOrder = captureSendOrder(cid);
      const userAttachments = readyAttachments.map((a) => ({
        serverPath: a.serverPath!,
        filename: a.filename,
        mimeType: a.mimeType,
        sizeBytes: a.sizeBytes,
        kind: a.kind ?? "file",
        blobUrl: a.blobUrl,
      }));
      const qmsg: QueuedMessage = {
        waitingForSend: sendOrder.waitingForSend,
        id: newId("queued"),
        text,
        attachmentPaths,
        userAttachments,
        serverQueued: false, // flip to true after POST succeeds
        userMsgId, // becomes the bubble's id when materialized at drain
      };
      set((s) => ({
        queueByCompany: {
          ...s.queueByCompany,
          [cid]: [...(s.queueByCompany[cid] ?? []), qmsg],
        },
        // Became a queued chip after all: the early bubble goes (the drain shows it in order).
        ...(shownBubble ? {
          messagesByCompany: { ...s.messagesByCompany, [cid]: (s.messagesByCompany[cid] ?? []).filter((m) => m.id !== shownBubble.id) },
        } : {
          composerDraft: s.composerDraft === submittedDraft ? "" : s.composerDraft,
          attachments: s.attachments.filter(a => !readyAttachments.some(sent => sent.id === a.id)),
        }),
      }));
      // Consume the notify flag — one-shot. Picking /notify mid-stream
      // attaches to the QUEUED turn, not the in-flight one.
      const notifyForThisSend = state.notifyOnDoneArmed;
      if (notifyForThisSend) set({ notifyOnDoneArmed: false });
      // OUTBOX: saved until the server confirms, so a reload/app kill mid-POST
      // is recovered with this same id (the server dedupes it).
      outboxSave({ cid, userMsgId, text, attachmentPaths, userAttachments, notify: notifyForThisSend,
        createdAt: Date.now(), attempts: 1, state: "sending", ...sendOrder });
      const dispatchQueued = async () => { try {
        // Effort is applied server-side at spawn (per-chat CLAUDE_CODE_EFFORT_LEVEL);
        // the message text goes over the wire verbatim.
        const wireText = text;
        // Pass userMsgId so the backend persists the row (at DRAIN time) with
        // the SAME id we'll give the bubble — keeps optimistic ↔ persisted ids
        // aligned across history reloads.
        releaseAdmission();
        if (!(await awaitSendOrder(cid, userMsgId))) return;
        postingIds.add(userMsgId);
        let queuedRes;
        try {
          queuedRes = await postMessage(cid, wireText, attachmentPaths, notifyForThisSend, userMsgId);
        } finally {
          postingIds.delete(userMsgId);
          orderedSendOwners.delete(userMsgId);
        }
        if (((sendAdmissionEpoch.get(cid) ?? 0) !== admissionEpoch || sendOrderEntry(cid, userMsgId)?.orderStopPending) && queuedRes?.state !== "removed") return;
        if (queuedRes?.duplicate && queuedRes.state === "pending") throw Object.assign(new Error("Admission not confirmed yet"), { name: "SendUnconfirmedError" });
        settleColdStatus(queuedRes?.waitingForSlot
          ? { companyId: cid, pid: null, state: "idle", waitingForSlot: true }
          : queuedRes?.state === "removed" ? { companyId: cid, pid: null, state: "idle" } : { companyId: cid, pid: null, state: "running" });
        // A cold send removed by this tab's own Stop / Clear (its POST went at once): the
        // draft, files and /notify come back, as when nothing was posted before the start.
        if (cold && queuedRes?.state === "removed" && (sendAdmissionEpoch.get(cid) ?? 0) !== admissionEpoch) giveBack(notifyForThisSend);
        if (!queuedRes?.nativeInput) settleSendOrder(cid, userMsgId);
        if (queuedRes?.nativeInput && get().applyNativeInputs(cid, confirmedNativeResponse(queuedRes))) return;
        if (locallyRemoved.has(userMsgId)) {
          // Cancelled while its POST was in flight: the cancel owns it now.
          outboxDrop(userMsgId);
          return;
        }
        if (queuedRes?.duplicate && (queuedRes.state === "delivered" || queuedRes.state === "removed")) {
          // Already delivered (an earlier attempt got through), or removed on
          // purpose (Clear/Stop/cancel elsewhere): not queued, nothing to show.
          outboxDrop(userMsgId);
          set((s) => ({
            queueByCompany: { ...s.queueByCompany, [cid]: (s.queueByCompany[cid] ?? []).filter((m) => m.id !== qmsg.id) },
          }));
          return;
        }
        // Confirmed queued: keep the outbox entry (state "queued") until it is
        // delivered, so a queue lost in a backend restart is shown, not dropped.
        if (queuedRes?.queued) outboxMarkQueued(userMsgId, { cid, userMsgId, text, attachmentPaths, userAttachments, notify: notifyForThisSend });
        else outboxDrop(userMsgId);
        // Mark this queued msg as durable on the server so the drain
        // handler knows not to re-POST when the current turn finishes.
        set((s) => ({
          queueByCompany: {
            ...s.queueByCompany,
            [cid]: (s.queueByCompany[cid] ?? []).map((m) =>
              m.id === qmsg.id ? { ...m, serverQueued: true } : m,
            ),
          },
        }));
        get().setToast(
          queuedRes?.waitingForSlot
            ? slotWaitToast(notifyForThisSend)
            : notifyForThisSend
              ? "Queued. Telegram ping when this one's done."
              : "Queued — will send when the current reply finishes.",
        );
      } catch (err: any) {
        if (locallyRemoved.has(userMsgId)) { if (!outboxFind(userMsgId)?.orderStopPending) outboxDrop(userMsgId); return; } // cancelled meanwhile
        if (isSendUnconfirmed(err)) {
          // No answer from the app — the server may well have queued it (a
          // phone that backgrounds mid-POST gets a 499 while the server goes
          // on). Keep the chip, keep the outbox entry, don't restore the
          // composer (that invited a duplicate resend) and ask the server.
          set((s) => ({
            unconfirmedUserMsgIds: { ...s.unconfirmedUserMsgIds, [userMsgId]: { cid, since: Date.now() } },
          }));
          scheduleConfirm(get, 1_500);
          return;
        }
        outboxDrop(userMsgId);
        settleColdStatus({ companyId: cid, pid: null, state: "errored", errorMessage: err.message });
        // POST failed — the message never reached the server queue. Drop it
        // from the local queue and restore the text (and files, /notify) to the
        // composer so the user can retry (there's no bubble to mark, by design).
        // Don't clobber anything the user has since started typing, and never
        // put it in another chat's composer.
        set((s) => ({
          queueByCompany: {
            ...s.queueByCompany,
            [cid]: (s.queueByCompany[cid] ?? []).filter((m) => m.id !== qmsg.id),
          },
        }));
        const restored = giveBack(notifyForThisSend);
        if (isStartRefused(err) && err.capacity) get().setToast(restored ? "Capacity is busy — request restored." : "Capacity is busy — not sent.", "error");
        else get().setToast(restored ? `Couldn't queue — restored to composer: ${err.message}` : `Couldn't queue: ${err.message}`, "error");
      }
      };
      if (sendOrder.waitingForSend) { void dispatchQueued(); scheduleConfirm(get, 1_500); return; }
      await dispatchQueued();
      return;
    }

    // Append user message immediately
    const sendOrder = captureSendOrder(cid);
    const userMsg: UIMessage = shownBubble ?? {
      id: newId("u"),
      role: "user",
      content: text,
      createdAt: nowIso(),
      userAttachments: readyAttachments.map((a) => ({
        serverPath: a.serverPath!,
        filename: a.filename,
        mimeType: a.mimeType,
        sizeBytes: a.sizeBytes,
        kind: a.kind ?? "file",
        blobUrl: a.blobUrl,
      })),
    };
    set((s) => {
      const list = s.messagesByCompany[cid] ?? [];
      // Early bubble: keep it (or put it back if a history refresh dropped it), last in the chat.
      if (shownBubble) return {
        messagesByCompany: { ...s.messagesByCompany, [cid]: [...list.filter((m) => m.id !== userMsg.id), userMsg] },
      };
      return {
        messagesByCompany: { ...s.messagesByCompany, [cid]: [...list, userMsg] },
        composerDraft: s.composerDraft === submittedDraft ? "" : s.composerDraft,
        attachments: s.attachments.filter(a => !readyAttachments.some(sent => sent.id === a.id)),
      };
    });

    // Create assistant placeholder
    const assistantId = newId("a");
    const placeholder: UIMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      thinking: "",
      isStreaming: true,
      createdAt: nowIso(),
    };
    set((s) => ({
      messagesByCompany: {
        ...s.messagesByCompany,
        [cid]: [...(s.messagesByCompany[cid] ?? []), placeholder],
      },
      streamingMessageId: assistantId,
      thinkingExpandedByMsgId: {
        ...s.thinkingExpandedByMsgId,
        [assistantId]: true, // auto-expand thinking while streaming
      },
    }));

    // Persist to mock backend store (so reload would see it in 1A)
    if (__mock) {
      __mock.appendMessage(cid, userMsg);
      __mock.appendMessage(cid, placeholder);
    }

    // Hit the backend. Effort is applied server-side at spawn (per-chat
    // CLAUDE_CODE_EFFORT_LEVEL); the message text goes over the wire verbatim.
    const wireText = text;
    // Mark this company as having pending agent work — drives the
    // oscillating-dot indicator on the sidebar row if the user later
    // switches away or closes the tab.
    {
      const pending = { ...get().pendingByCompany, [cid]: true };
      persistPendingByCompany(pending);
      set({ pendingByCompany: pending });
    }
    // Consume the notify flag — one-shot. Cleared up-front so the
    // setState before await postMessage doesn't carry it into a second send.
    const notifyForThisSend = state.notifyOnDoneArmed;
    if (notifyForThisSend) set({ notifyOnDoneArmed: false });
    outboxSave({ cid, userMsgId: userMsg.id, text, attachmentPaths, userAttachments: userMsg.userAttachments,
      notify: notifyForThisSend, createdAt: Date.now(), attempts: 1, state: "sending", ...sendOrder });
    try {
      // Pass userMsg.id so backend persists with our optimistic id (see
      // matching comment in busy path above for rationale).
      releaseAdmission();
      if (!(await awaitSendOrder(cid, userMsg.id))) return;
      postingIds.add(userMsg.id);
      let res;
      try {
        res = await postMessage(cid, wireText, attachmentPaths, notifyForThisSend, userMsg.id);
      } finally {
        postingIds.delete(userMsg.id);
        orderedSendOwners.delete(userMsg.id);
      }
      if (((sendAdmissionEpoch.get(cid) ?? 0) !== admissionEpoch || sendOrderEntry(cid, userMsg.id)?.orderStopPending) && res?.state !== "removed") return;
      if (res?.duplicate && res.state === "pending") throw Object.assign(new Error("Admission not confirmed yet"), { name: "SendUnconfirmedError" });
      settleColdStatus(res?.waitingForSlot
        ? { companyId: cid, pid: null, state: "idle", waitingForSlot: true }
        : res?.state === "removed" ? { companyId: cid, pid: null, state: "idle" } : { companyId: cid, pid: null, state: "running" });
      if (!res?.nativeInput) settleSendOrder(cid, userMsg.id);
      if (res?.nativeInput && get().applyNativeInputs(cid, confirmedNativeResponse(res), assistantId)) {
        if (get().activeCompanyId === cid) void get().resyncActive();
        return;
      }
      outboxDrop(userMsg.id);
      if (res?.duplicate && res.state === "removed") {
        // Julian removed it (a Stop / Clear here or on another device) before it
        // arrived: never delivered. Drop the bubble and its placeholder.
        locallyRemoved.add(userMsg.id);
        set((s) => {
          const pending = { ...s.pendingByCompany };
          if (pendingBeforeSend) pending[cid] = true;
          else delete pending[cid];
          persistPendingByCompany(pending);
          return {
            messagesByCompany: { ...s.messagesByCompany,
              [cid]: (s.messagesByCompany[cid] ?? []).filter((m) => m.id !== userMsg.id && m.id !== assistantId) },
            streamingMessageId: s.streamingMessageId === assistantId ? null : s.streamingMessageId,
            pendingByCompany: pending,
          };
        });
        // This tab's own Stop / Clear during a cold start (the POST went at once and the
        // server removed it): as before, the draft, files and /notify come back to the box.
        if (shownBubble && (sendAdmissionEpoch.get(cid) ?? 0) !== admissionEpoch) giveBack(notifyForThisSend);
      } else if (res?.duplicate && res.state === "pending") {
        // Another attempt with this id is still being handled by the server.
        outboxSave({ cid, userMsgId: userMsg.id, text, attachmentPaths, userAttachments: userMsg.userAttachments,
          notify: notifyForThisSend, createdAt: Date.now(), attempts: 1, state: "sending" });
        set((s) => ({ unconfirmedUserMsgIds: { ...s.unconfirmedUserMsgIds, [userMsg.id]: { cid, since: Date.now() } } }));
        scheduleConfirm(get, 2_000);
      } else if (res?.queued) {
        // RACE BACKSTOP. The server was busy after all — its `busy` flag is polled
        // every ~6s, so it can flip between our last poll and this POST (e.g. an
        // auto-revision dispatched in that window). The backend ENQUEUED rather
        // than ran this message. Undo the optimistic bubble + streaming
        // placeholder: a queued message is not persisted until drain, so leaving
        // them would show a bubble that evaporates on the next history poll and a
        // placeholder that never streams. Re-register it as a queued message so the
        // "N queued" chip appears and the `done` drain materializes the bubble in
        // correct chat order.
        outboxMarkQueued(userMsg.id, { cid, userMsgId: userMsg.id, text, attachmentPaths,
          userAttachments: userMsg.userAttachments, notify: notifyForThisSend });
        set((s) => ({
          messagesByCompany: {
            ...s.messagesByCompany,
            [cid]: (s.messagesByCompany[cid] ?? []).filter(
              (m) => m.id !== userMsg.id && m.id !== assistantId,
            ),
          },
          streamingMessageId: s.streamingMessageId === assistantId ? null : s.streamingMessageId,
          queueByCompany: {
            ...s.queueByCompany,
            [cid]: [
              ...(s.queueByCompany[cid] ?? []),
              {
                id: newId("queued"),
                text,
                attachmentPaths,
                serverQueued: true,
                userMsgId: userMsg.id,
                userAttachments: userMsg.userAttachments,
              },
            ],
          },
        }));
        get().setToast(
          res.waitingForSlot
            ? slotWaitToast(notifyForThisSend)
            : notifyForThisSend
              ? "Queued. Telegram ping when this one's done."
              : "Queued — will send when the current run finishes.",
        );
      } else if (notifyForThisSend) {
        get().setToast("Sent. I'll ping you on Telegram when done.");
      }
    } catch (err: any) {
      if (isSendUnconfirmed(err)) {
        // HONEST STATE: no answer ≠ failure. The server keeps going after a
        // phone aborts the POST (proven 499s that were delivered and answered).
        // Neutral "Not confirmed yet"; the server is asked about this id on
        // reconnect/online/visibility, and only resent (same id) if absent.
        set((s) => ({
          unconfirmedUserMsgIds: { ...s.unconfirmedUserMsgIds, [userMsg.id]: { cid, since: Date.now() } },
        }));
        scheduleConfirm(get, 1_500);
        return;
      }
      outboxDrop(userMsg.id);
      // A cold start that failed (capacity / spawn_failed) must not leave the pill on Starting.
      settleColdStatus({ companyId: cid, pid: null, state: "errored", errorMessage: err.message });
      // Refused before anything was saved (every slot busy / the start failed): on this chat's
      // own screen the bubble and placeholder go and the draft, files and /notify come back.
      // From another chat's screen the failed bubble below keeps the text with tap-to-retry.
      if (isStartRefused(err) && giveBack(notifyForThisSend)) {
        set((s) => {
          const pending = { ...s.pendingByCompany };
          if (pendingBeforeSend) pending[cid] = true;
          else delete pending[cid];
          persistPendingByCompany(pending);
          return {
            messagesByCompany: { ...s.messagesByCompany,
              [cid]: (s.messagesByCompany[cid] ?? []).filter((m) => m.id !== userMsg.id && m.id !== assistantId) },
            streamingMessageId: s.streamingMessageId === assistantId ? null : s.streamingMessageId,
            pendingByCompany: pending,
          };
        });
        get().setToast(err.capacity ? "Capacity is busy — request restored." : `${err.message} — request restored.`, "error");
        return;
      }
      get().setToast(`Send failed: ${err.message}`, "error");
      // Also mark the user bubble as failed so it gets a persistent red
      // border + tap-to-retry hint after the toast fades. P2-#4 polish:
      // toast is transient (4s) — without a permanent marker, the user
      // bubble looks identical to a successful send and is easy to lose
      // track of in a long chat.
      set((s) => ({
        // Only this send's placeholder: the operator may be on another chat by now.
        streamingMessageId: s.streamingMessageId === assistantId ? null : s.streamingMessageId,
        messagesByCompany: {
          ...s.messagesByCompany,
          [cid]: (s.messagesByCompany[cid] ?? []).map((m) =>
            m.id === assistantId ? { ...m, isStreaming: false, content: m.content || "(failed to send)" } : m,
          ),
        },
        failedUserMsgIds: {
          ...s.failedUserMsgIds,
          [userMsg.id]: { reason: err.message || "send failed", failedAt: Date.now() },
        },
      }));
    }
    // Mock-mode only: simulate streamed reply. Real SSE drives the placeholder
    // updates from connectStream events.
    emitMockReply(assistantId, text);
    } finally {
      releaseAdmission();
    }
  },

  async resendMessage(cid, userMsgId, opts) {
    if (!(await canRecoverSendOrder(cid, userMsgId))) return;
    if (get().nativeInputsByCompany[cid]?.inputs.some(input => input.userMsgId === userMsgId)) return;
    if (resendInFlight.has(userMsgId)) return;
    // Removed on purpose on this device: never sent again automatically.
    if (locallyRemoved.has(userMsgId) && !opts?.manual) return;
    locallyRemoved.delete(userMsgId);
    const st = get();
    const entry = outboxFind(userMsgId);
    const bubble = (st.messagesByCompany[cid] ?? []).find((m) => m.id === userMsgId && m.role === "user");
    if (bubble?.meta?.nativeInput) return;
    const queued = (st.queueByCompany[cid] ?? []).find((q) => q.userMsgId === userMsgId);
    const text = entry?.text ?? bubble?.content ?? queued?.text;
    if (text === undefined) {
      // Nothing left to send (already reconciled away) — just drop markers.
      set((s) => {
        const failed = { ...s.failedUserMsgIds }; delete failed[userMsgId];
        const unconfirmed = { ...s.unconfirmedUserMsgIds }; delete unconfirmed[userMsgId];
        return { failedUserMsgIds: failed, unconfirmedUserMsgIds: unconfirmed };
      });
      outboxDrop(userMsgId);
      return;
    }
    const userAttachments = entry?.userAttachments ?? bubble?.userAttachments ?? queued?.userAttachments
      ?? (bubble?.files?.length ? attachmentsFromPaths(bubble.files) : undefined);
    const attachmentPaths = entry?.attachmentPaths ?? queued?.attachmentPaths
      ?? userAttachments?.map((a) => a.serverPath) ?? [];
    const notify = entry?.notify ?? false;
    // A manual retry uses up the automatic resend: if it also gets no answer it
    // goes straight back to "Not sent — tap to retry" (no resend loop).
    const attempts = opts?.manual ? MAX_AUTO_ATTEMPTS : (entry?.attempts ?? MAX_AUTO_ATTEMPTS) + 1;
    outboxSave({ cid, userMsgId, text, attachmentPaths, userAttachments, notify,
      createdAt: entry?.createdAt ?? Date.now(), attempts, state: "sending" });
    set((s) => {
      const failed = { ...s.failedUserMsgIds }; delete failed[userMsgId];
      return {
        failedUserMsgIds: failed,
        unconfirmedUserMsgIds: { ...s.unconfirmedUserMsgIds, [userMsgId]: { cid, since: Date.now() } },
      };
    });
    resendInFlight.add(userMsgId);
    markSendOrderPosting(cid, userMsgId);
    try {
      const res = await postMessage(cid, text, attachmentPaths, notify, userMsgId);
      if ((!sendOrderEntry(cid, userMsgId) || sendOrderEntry(cid, userMsgId)?.orderStopPending) && res?.state !== "removed") return;
      if (res?.nativeInput && get().applyNativeInputs(cid, confirmedNativeResponse(res))) return;
      if (res?.duplicate && res.state === "pending") {
        scheduleConfirm(get, 2_000);
        return;
      }
      if (res?.duplicate && res.state === "removed") {
        dropRemovedSend(set, cid, userMsgId);
        return;
      }
      if (res?.queued) outboxMarkQueued(userMsgId, { cid, userMsgId, text, attachmentPaths, userAttachments, notify });
      else outboxDrop(userMsgId);
      set((s) => {
        const unconfirmed = { ...s.unconfirmedUserMsgIds }; delete unconfirmed[userMsgId];
        const next: Partial<SessionState> = { unconfirmedUserMsgIds: unconfirmed };
        const msgs = s.messagesByCompany[cid] ?? [];
        const localQueue = s.queueByCompany[cid] ?? [];
        if (res?.queued) {
          // Server queued it: the bubble is materialized at drain, in order.
          next.messagesByCompany = { ...s.messagesByCompany, [cid]: msgs.filter((m) => m.id !== userMsgId) };
          next.queueByCompany = {
            ...s.queueByCompany,
            [cid]: localQueue.some((q) => q.userMsgId === userMsgId)
              ? localQueue.map((q) => q.userMsgId === userMsgId ? { ...q, serverQueued: true } : q)
              : [...localQueue, { id: newId("queued"), text, attachmentPaths, serverQueued: true, userMsgId, userAttachments }],
          };
          return next;
        }
        // Delivered (now, or by an earlier attempt): a normal bubble.
        const inQueue = localQueue.find((q) => q.userMsgId === userMsgId && !q.dispatching);
        if (inQueue) next.queueByCompany = { ...s.queueByCompany, [cid]: localQueue.filter((q) => q !== inQueue) };
        let nextMsgs = msgs.some((m) => m.id === userMsgId)
          ? msgs
          : [...msgs, { id: userMsgId, role: "user" as const, content: text, createdAt: nowIso(), userAttachments }];
        if (!res?.duplicate && s.activeCompanyId === cid && !s.streamingMessageId) {
          // Delivered NOW: give the reply a render target, like a normal send.
          const placeholderId = newId("a");
          nextMsgs = [...nextMsgs, { id: placeholderId, role: "assistant", content: "", thinking: "", isStreaming: true, createdAt: nowIso() }];
          next.streamingMessageId = placeholderId;
          const pending = { ...s.pendingByCompany, [cid]: true };
          persistPendingByCompany(pending);
          next.pendingByCompany = pending;
        }
        if (nextMsgs !== msgs) next.messagesByCompany = { ...s.messagesByCompany, [cid]: nextMsgs };
        return next;
      });
    } catch (err: unknown) {
      if (isSendUnconfirmed(err)) {
        // Still no answer: keep asking the server; planRecovery turns it
        // failed only once the automatic resend is used up.
        scheduleConfirm(get, 3_000);
        return;
      }
      // A real refusal from the app (validation, auth, id conflict).
      outboxDrop(userMsgId);
      markSendFailed(set, cid, userMsgId, text, userAttachments, (err as Error)?.message || "send failed", undefined);
    } finally {
      resendInFlight.delete(userMsgId);
    }
  },

  async confirmPendingSends() {
    if (confirmRunning) { confirmAgain = true; return; }
    confirmRunning = true;
    let wait = false;
    try {
      const targets = new Map<string, string>();
      for (const [id, u] of Object.entries(get().unconfirmedUserMsgIds)) targets.set(id, u.cid);
      for (const e of outboxAll()) targets.set(e.userMsgId, e.cid);
      const nativeChecked = new Set<string>();
      for (const [userMsgId, cid] of targets) {
        if (!(await canRecoverSendOrder(cid, userMsgId))) { wait = true; continue; }
        if (resendInFlight.has(userMsgId)) { wait = true; continue; }
        // Its first POST is still waiting for an answer; that answer settles it.
        if (postingIds.has(userMsgId)) continue;
        if (!nativeChecked.has(cid)) {
          try {
            const history = await getMessageHistory(cid);
            get().applyNativeInputs(cid, history.inFlight?.nativeInput, undefined, history.messages);
            nativeChecked.add(cid);
          } catch { wait = true; continue; }
        }
        if (get().nativeInputsByCompany[cid]?.inputs.some(input => input.userMsgId === userMsgId) || get().messagesByCompany[cid]?.some(message => message.id === userMsgId && message.meta?.nativeInput)) continue;
        let server;
        try {
          server = await getMessageStatus(cid, userMsgId);
        } catch {
          wait = true; // offline / server unreachable: ask again later
          continue;
        }
        if (orderedSendPauses.has(cid) || outboxFind(userMsgId)?.orderStopPending) { wait = true; continue; }
        if (locallyRemoved.has(userMsgId)) { forgetRemovedSends(set, [userMsgId]); continue; }
        const entry = outboxFind(userMsgId);
        const action = planRecovery(entry, server);
        if (action === "wait") { wait = true; continue; }
        if (action === "resend") { await get().resendMessage(cid, userMsgId); continue; }
        if (action === "remove") { dropRemovedSend(set, cid, userMsgId); continue; }
        if (action === "drop" || action === "queued") {
          settleSendOrder(cid, userMsgId);
          if (action === "drop") outboxDrop(userMsgId);
          else {
            // Keep asking at a low rate until it is delivered (or lost).
            wait = true;
            if (entry && entry.state !== "queued") outboxSave({ ...entry, state: "queued" });
          }
          set((s) => {
            const localQueue = s.queueByCompany[cid] ?? [];
            // A steady "still queued" answer changes nothing: no re-render.
            if (server === "queued" && !Object.hasOwn(s.unconfirmedUserMsgIds, userMsgId) && !Object.hasOwn(s.failedUserMsgIds, userMsgId) &&
                localQueue.some((q) => q.userMsgId === userMsgId && q.serverQueued) &&
                !(s.messagesByCompany[cid] ?? []).some((m) => m.id === userMsgId && m.role === "user")) return {};
            const unconfirmed = { ...s.unconfirmedUserMsgIds }; delete unconfirmed[userMsgId];
            const failed = { ...s.failedUserMsgIds }; delete failed[userMsgId];
            const next: Partial<SessionState> = { unconfirmedUserMsgIds: unconfirmed, failedUserMsgIds: failed };
            if (server === "queued") {
              const msgs = s.messagesByCompany[cid] ?? [];
              const bubble = msgs.find((m) => m.id === userMsgId && m.role === "user");
              if (bubble) next.messagesByCompany = { ...s.messagesByCompany, [cid]: msgs.filter((m) => m !== bubble) };
              const chip = localQueue.find((q) => q.userMsgId === userMsgId);
              if (!chip || !chip.serverQueued) {
                next.queueByCompany = {
                  ...s.queueByCompany,
                  [cid]: chip
                    ? localQueue.map((q) => q === chip ? { ...q, serverQueued: true } : q)
                    : [...localQueue, { id: newId("queued"), text: entry?.text ?? bubble?.content ?? "", attachmentPaths: entry?.attachmentPaths ?? [],
                        serverQueued: true, userMsgId, userAttachments: entry?.userAttachments ?? bubble?.userAttachments }],
                };
              }
            } else {
              // Delivered: an unconfirmed chip for it has already drained.
              const stale = localQueue.find((q) => q.userMsgId === userMsgId && !q.dispatching && !q.serverQueued);
              if (stale) next.queueByCompany = { ...s.queueByCompany, [cid]: localQueue.filter((q) => q !== stale) };
              const msgs = s.messagesByCompany[cid] ?? [];
              if (entry && entry.state !== "queued" && !entry.truncated && s.activeCompanyId === cid && !msgs.some((m) => m.id === userMsgId)) {
                next.messagesByCompany = { ...s.messagesByCompany, [cid]: [...msgs,
                  { id: userMsgId, role: "user" as const, content: entry.text, createdAt: new Date(entry.createdAt).toISOString(), userAttachments: entry.userAttachments }] };
              }
            }
            return next;
          });
          continue;
        }
        // fail / lost: never sent automatically — "Not sent — tap to retry"
        // ("Not delivered — tap to resend" for a queued message the server lost).
        const text = entry?.text ?? (get().messagesByCompany[cid] ?? []).find((m) => m.id === userMsgId)?.content
          ?? (get().queueByCompany[cid] ?? []).find((q) => q.userMsgId === userMsgId)?.text;
        if (entry) outboxSave({ ...entry, state: "failed" });
        const reason = action === "lost" ? QUEUE_LOST_REASON
          : server === "failed" ? DELIVERY_FAILED_REASON
          : entry?.truncated ? TOO_LONG_REASON : UNCONFIRMED_REASON;
        if (text !== undefined) markSendFailed(set, cid, userMsgId, text, entry?.userAttachments, reason, entry?.truncated ? undefined : "resend");
        else set((s) => { const u = { ...s.unconfirmedUserMsgIds }; delete u[userMsgId]; return { unconfirmedUserMsgIds: u }; });
      }
    } finally {
      confirmRunning = false;
      if (confirmAgain) { confirmAgain = false; scheduleConfirm(get, 500); }
      else if (wait) { scheduleConfirm(get, confirmBackoffMs); confirmBackoffMs = Math.min(confirmBackoffMs * 2, 30_000); }
      else confirmBackoffMs = 2_000;
    }
  },

  async interruptStream() {
    markStopped(get().activeCompanyId);
    const cid = get().activeCompanyId;
    if (!cid) return;
    // Never kill mid-compaction: it finishes on its own and the queued
    // messages then continue. Leave local state untouched.
    if (get().compactionByCompany[cid]?.state === "compacting") {
      get().setToast("Compacting context — this finishes on its own in a moment.", "info");
      return;
    }
    abortMockStream();
    // Stop cancels this chat's queued follow-ups: never recover/resend them
    sendAdmissionEpoch.set(cid, (sendAdmissionEpoch.get(cid) ?? 0) + 1);
    const resumeOrderedSends = pauseOrderedSends(cid);
    const stoppedOrderEntries = sendOrderEntries(cid);
    // (the server tombstones them). A direct send of this chat still awaiting
    // confirmation is never resent automatically either: it settles as
    // delivered, or "Not sent — tap to retry".
    const stoppedChips = (get().queueByCompany[cid] ?? []).filter((q) => q.userMsgId && !q.dispatching);
    forgetRemovedSends(set, stoppedChips.map((q) => q.userMsgId!));
    // An unconfirmed chip's POST may still arrive after Stop: tombstone its id
    // on the server too (best-effort; the server's Stop covers queued ones).
    for (const e of outboxAll()) {
      if (e.cid === cid && e.state !== "queued") outboxSave({ ...e, attempts: MAX_AUTO_ATTEMPTS });
      // Confirmed queued: Stop dropped it on purpose.
      if (e.cid === cid && e.state === "queued") forgetRemovedSends(set, [e.userMsgId]);
    }
    const sid = get().streamingMessageId;
    // Mark the active streaming message as done AND scrub any orphan
    // empty-content assistant ghosts for this company. Ghosts can sneak in
    // from stray SSE events (e.g., spurious replays from a reconnect after
    // a network blip) — without a hard sweep here they'd accumulate as
    // "(no content)" bubbles + perpetual progress chips, which Julian saw
    // and reported on 25 May. Filtering instead of mapping also removes
    // empty placeholder rows entirely; only legit content survives.
    set((s) => {
      const msgs = s.messagesByCompany[cid] ?? [];
      const cleaned = msgs
        .filter((m) => {
          // Keep all user messages.
          if (m.role !== "assistant") return true;
          // Keep assistant messages with actual content (text, tools, or files).
          const hasContent = (m.content ?? "").length > 0
            || (m.tools?.length ?? 0) > 0
            || (m.files?.length ?? 0) > 0;
          return hasContent;
        })
        .map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m));
      // Also clear the per-company pending pulse — Stop means "I'm done
      // with this turn", which includes the sidebar attention dot. Without
      // this it kept animating until the user navigated away.
      const pending = { ...s.pendingByCompany };
      const pendingChanged = pending[cid];
      if (pendingChanged) delete pending[cid];
      if (pendingChanged) persistPendingByCompany(pending);
      return {
        messagesByCompany: { ...s.messagesByCompany, [cid]: cleaned },
        streamingMessageId: null,
        // Clear local queue — Stop kills the session, so any queued
        // follow-ups won't drain. Chip on queued messages disappears.
        // Backend's finalizeSession also clears its own queue + emits
        // "Session ended mid-turn. Your queued message…was lost" errors,
        // so the user already knows what happened.
        queueByCompany: { ...s.queueByCompany, [cid]: [] },
        thinkingExpandedByMsgId: sid
          ? { ...s.thinkingExpandedByMsgId, [sid]: false }
          : s.thinkingExpandedByMsgId,
        ...(pendingChanged ? { pendingByCompany: pending } : {}),
      };
    });
    try {
      const unresolved = await retireStoppedSendOrder(cid, stoppedOrderEntries, new Set(stoppedChips.map(q => q.userMsgId!)));
      await interruptCurrentTurn(cid);
      // Backend now hard-kills claude on stop (no clean cancel exists).
      // Be explicit so the user knows the next message takes ~10s to
      // cold-spawn a fresh claude — not a regression, just the trade-off
      // for guaranteed token-burn termination.
      get().setToast(unresolved ? "Stopped. Confirming earlier sends before new inputs can continue." : "Stopped. Next message spawns a fresh session.");
    } catch (err: any) {
      if (err instanceof CompactRefusedError) {
        get().setToast(err.message, "info");
        void getCompactionState(cid).then((state) => get().applyCompaction(cid, state)).catch(() => {});
        return;
      }
      get().setToast(`Interrupt failed: ${err.message}`, "error");
    } finally {
      resumeOrderedSends();
    }
  },

  /* ---- Thinking accordion ---- */
  toggleThinking(msgId) {
    set((s) => ({
      thinkingExpandedByMsgId: {
        ...s.thinkingExpandedByMsgId,
        [msgId]: !s.thinkingExpandedByMsgId[msgId],
      },
    }));
  },

  setThinkingExpanded(msgId, expanded) {
    set((s) => ({
      thinkingExpandedByMsgId: {
        ...s.thinkingExpandedByMsgId,
        [msgId]: expanded,
      },
    }));
  },

  materializeQueuedHead(cid, options) {
    if (options?.skipIfDispatching) {
      const head = (get().queueByCompany[cid] ?? [])[0];
      if (!head || head.dispatching || get().streamingMessageId) return;
    }
    const queue = get().queueByCompany[cid] ?? [];
    if (queue.length > 0) {
      const next = queue[0]!;
      // An unacknowledged POST may be a native addition, never a new turn.
      if (!next.serverQueued) { scheduleConfirm(get, 1_500); return; }
      // Mark as dispatching INSTEAD of removing. The chip stays
      // visible (with "⏳ sending…" text) until claude's first content
      // event for the new placeholder lands — that's our real signal
      // claude has started processing. See applyStreamEvent top: any
      // content event dequeues the dispatching item.
      set((s) => ({
        queueByCompany: {
          ...s.queueByCompany,
          [cid]: (s.queueByCompany[cid] ?? []).map((q, i) =>
            i === 0 ? { ...q, dispatching: true } : q,
          ),
        },
      }));
      const preview = next.text.trim().slice(0, 40);
      // Materialize the queued USER bubble NOW — i.e. AFTER the reply that
      // just finished — then its reply placeholder right below it (Pain-A
      // flow: the queued bubble appears after the previous response, never
      // before, immediately followed by its response). The bubble's id ===
      // next.userMsgId so it matches the queue entry (for the brief
      // "⏳ sending…" badge) AND the server row persisted at drain (so a
      // later reload keeps the same order + identity).
      //
      // We do NOT re-POST. The message was already POSTed by sendMessage's
      // busy branch, so the backend has it (or will, once the in-flight
      // POST lands) and auto-drains it. The old `serverQueued===false`
      // branch re-POSTed via a fresh sendMessage() — but a queue entry is
      // only serverQueued=false during the brief window where its POST is
      // STILL IN FLIGHT (a failed POST is removed from the queue in
      // sendMessage's catch). Re-POSTing in that window DUPLICATED the
      // message to claude on a switch-away / slow-uplink race. Always
      // materialize-without-re-POST is correct in every live case. (Mock
      // mode no longer exists post-Phase-1C, so nothing depends on the
      // re-POST.)
      {
        const userBubble: UIMessage = {
          id: next.userMsgId ?? newId("u"),
          role: "user",
          content: next.text,
          createdAt: nowIso(),
          userAttachments: next.userAttachments,
        };
        const newPlaceholderId = newId("a");
        const placeholder: UIMessage = {
          id: newPlaceholderId,
          role: "assistant",
          content: "",
          tools: [],
          isStreaming: true,
          createdAt: nowIso(),
        };
        set((s) => ({
          messagesByCompany: {
            ...s.messagesByCompany,
            [cid]: [...(s.messagesByCompany[cid] ?? []), userBubble, placeholder],
          },
          streamingMessageId: newPlaceholderId,
        }));
        get().setToast(
          `Sending queued: "${preview}${next.text.length > 40 ? "…" : ""}"`,
          "info",
        );
      }
    }
  },

  /* ---- SSE event reducer ----
   * Events may arrive WITHOUT a messageId (real backend events don't
   * carry one — only the frontend knows which assistant placeholder
   * is currently streaming). We substitute streamingMessageId in that case.
   */
  applyStreamEvent(e: StreamEvent) {
    const cid = get().activeCompanyId;
    if (!cid) return;
    // Transport/auth failures are not agent-turn failures. Preserve the draft,
    // queue and transcript; a fresh authenticated read can recover the reply.
    if (e.type === "connection") {
      if (e.data.companyId !== cid) return;
      if (e.data.state === "connected") {
        // ANY successful (re)connect — including a brand-new stream opened by
        // resyncActive, whose first "connected" has no resolvedMessage —
        // cancels a pending notice and clears one on screen. Never clears an
        // unrelated send/schedule toast.
        liveNotice(get).up();
        if (e.data.resolvedMessage && get().toast?.text === e.data.resolvedMessage) get().clearToast();
        if (e.data.reconnected) void get().reconcileSettledHistory(cid);
      } else if (e.data.state === "auth-required") {
        if (e.data.message) get().setToast(e.data.message, "error");
      } else if (e.data.message) {
        // Debounced: shown only if still down after 8 s while visible.
        liveNotice(get).down(e.data.message);
      }
      return;
    }
    if (e.type === "native_input" && e.data.companyId !== cid) return;
    if ((e.type === "scheduled_turn" || e.type === "automated_turn") && (e.data.companyId !== cid || e.data.message.companyId !== cid)) return;

    // --- Epoch-reset detection (see broadcaster SequencedEvent.epoch) ---
    // The backend stamps a monotonic per-company epoch on every event. When it
    // INCREASES vs what we've tracked, a new backend epoch has begun — a fresh
    // spawn, a session respawn, OR a whole backend restart (e.g. the L4
    // watchdog restarting it with this page open, or mobile backgrounding →
    // SSE reconnect landing on a fresh epoch). The per-company seq counter
    // restarted at the backend, so we MUST clear our seq-dedup tracker;
    // otherwise the stale-high lastAppliedSeq drops the new epoch's events and
    // the turn renders as a stuck "Working/Refining…" with no reply (the
    // lingering-Stop / dropped-reply bug). A straggler from an OLDER epoch
    // (late delivery after a reset) is dropped. Replaces the old fragile
    // "seq gap > 5 = restart" guess.
    const eventEpoch = Number((e.data as any)?.__epoch ?? 0);
    if (eventEpoch > 0) {
      const curEpoch = get().currentEpochByCompany[cid] ?? 0;
      if (eventEpoch > curEpoch) {
        set((s) => ({
          currentEpochByCompany: { ...s.currentEpochByCompany, [cid]: eventEpoch },
          lastAppliedSeqByCompany: { ...s.lastAppliedSeqByCompany, [cid]: 0 },
          completedScheduledReplayByCompany: { ...s.completedScheduledReplayByCompany, [cid]: false },
        }));
        // A new backend epoch (restart / respawn) may have lost a running Compact.
        if (curEpoch > 0) refreshCompaction(cid);
      } else if (eventEpoch < curEpoch) {
        return; // stale event from a previous epoch
      }
      // eventEpoch === curEpoch → same epoch; fall through to seq dedup below.
    }

    // Dedup by SSE seq WITHIN the current epoch. Mode-2 replay (no
    // Last-Event-ID, post-resyncActive) re-delivers every event since
    // lastDoneSeq; without this, content/thinking deltas append TWICE. Backend
    // seq is monotonic per epoch; epoch CHANGES are handled explicitly above,
    // so within an epoch a seq <= lastSeq is always a replay duplicate → drop.
    const eventSeq = Number((e.data as any)?.__seq ?? 0);
    if (eventSeq > 0) {
      const lastSeq = get().lastAppliedSeqByCompany[cid] ?? 0;
      if (eventSeq <= lastSeq) {
        // Legacy fallback ONLY when NO epoch is present (an old backend mid-
        // deploy): tolerate a large gap as a restart signal. With epoch
        // tagging this branch is dead (eventEpoch > 0) — which is correct, the
        // old gap heuristic mis-fired on large Mode-2 replays, double-applying
        // events.
        if (eventEpoch === 0 && lastSeq - eventSeq > 5) {
          set((s) => ({
            lastAppliedSeqByCompany: { ...s.lastAppliedSeqByCompany, [cid]: eventSeq },
          }));
        } else {
          return; // duplicate (any real epoch reset was handled above)
        }
      } else {
        set((s) => ({
          lastAppliedSeqByCompany: { ...s.lastAppliedSeqByCompany, [cid]: eventSeq },
        }));
      }
    }

    if (e.type === "native_input") {
      get().applyNativeInputs(cid, e.data);
      return;
    }

    // Native Compact lifecycle: server-owned state, never a turn event.
    if (e.type === "compact") {
      get().applyCompaction(cid, e.data);
      return;
    }

    // A fresh history load may already contain a scheduled attempt's reply.
    // Its replayed thinking/tools/done belong to THAT completed turn, never
    // a newer human placeholder. Fence the whole explicit segment before
    // content can consume a local human queue chip; active replay stays live.
    if (get().completedScheduledReplayByCompany[cid] &&
        (e.type === "thinking" || e.type === "message_delta" || e.type === "tool_start" ||
         e.type === "tool_update" || e.type === "error" || e.type === "done")) {
      if (e.type === "done") {
        set((s) => ({ completedScheduledReplayByCompany: { ...s.completedScheduledReplayByCompany, [cid]: false } }));
      }
      return;
    }

    // A reconnect can load the durable reply before replaying its event.
    // Ignore it before any queue-chip mutation, not just before text routing.
    // This also dedups an out-of-turn reply (below) that is already on screen.
    if (e.type === "message_delta" && e.data.persistedMessageId &&
        e.data.persistedMessageId !== (e.data.messageId || get().streamingMessageId) &&
        get().messagesByCompany[cid]?.some((message) => message.id === e.data.persistedMessageId)) return;

    // A reply with no turn to land in (no explicit id, no streaming
    // placeholder) is the CLI speaking on its own — a background subagent /
    // Bash / Monitor wake-up after `done`. It is not the queued message's turn.
    // The backend also flags it (outOfTurn) when it admitted the reply on a
    // completed turn's token: that holds even if this tab already opened a
    // placeholder for a message the owner just sent.
    const flaggedOutOfTurn = e.type === "message_delta" && e.data.outOfTurn === true;
    const outOfTurnReply = e.type === "message_delta" &&
      (flaggedOutOfTurn || (!e.data.messageId && !get().streamingMessageId));

    // Dispatching-queue dequeue: any content event (thinking/message_delta
    // /tool_start/tool_update) for the new placeholder means claude has
    // actually started processing the previously-queued message. Drop the
    // chip now — before this it stayed visible from the moment `done`
    // fired, even before the new turn had any output. An out-of-turn reply
    // never counts: the queued message is still waiting for its own turn.
    if (
      e.type === "thinking" ||
      (e.type === "message_delta" && !outOfTurnReply) ||
      e.type === "tool_start" ||
      e.type === "tool_update"
    ) {
      const q = get().queueByCompany[cid];
      if (q && q.length > 0 && q[0]!.dispatching) {
        set((s) => ({
          queueByCompany: {
            ...s.queueByCompany,
            [cid]: (s.queueByCompany[cid] ?? []).slice(1),
          },
        }));
      }
    }

    // Resolve target messageId — incoming or current streaming.
    //
    // Strict: if no explicit messageId and no streamingMessageId, the event
    // has no home and is DROPPED. We previously tried auto-synthesizing a
    // placeholder bubble here as a safety net for server-side queue drains,
    // but it created ghost "(no content)" bubbles + a perpetual progress
    // chip on every spurious SSE replay (each PWA reconnect would
    // re-trigger it). The refresh-mid-turn case is handled cleanly in
    // selectCompany (synthesizes ONE placeholder + sets streamingMessageId
    // BEFORE the SSE subscription opens). Queue-drain edge case: events for
    // turn N+1 may briefly drop after turn N's `done`, but the final reply
    // is still persisted via /reply and visible on next history load.
    //
    // ONE narrow exception, in `message_delta` below: an out-of-turn reply
    // that carries a persistedMessageId AND non-empty text or files. The
    // backend only emits that id after writing the row, so it is durable
    // content a reload would show anyway, not a ghost. It is appended as a
    // FINISHED bubble (no placeholder, no progress chip, no Stop/queue/turn
    // state), exactly once — the reconnect guard above drops any replay of an
    // id already on screen. Without it, a reply sent after an async subagent
    // finished stayed invisible until reload ("Waiting for the subagent…").
    // Only while the server is idle with nothing queued (the CLI's own
    // wake-up): a busy/queued server means a server-admitted turn this tab has
    // no placeholder for (drained queue, another device), whose prompt is not
    // on screen — that reply is dropped and history re-read after its `done`.
    const targetMsgId = (() => {
      if (e.type === "status" || e.type === "queue" || e.type === "error") return null;
      const explicit = (e.data as any)?.messageId;
      if (explicit) return explicit as string;
      return get().streamingMessageId;
    })();

    switch (e.type) {
      case "automated_turn":
      case "scheduled_turn": {
        set((s) => {
          const queuedHead = s.queueByCompany[cid]?.[0];
          const admitted = admitScheduledTurn(s.messagesByCompany[cid] ?? [], s.streamingMessageId,
            queuedHead?.dispatching ? queuedHead.userMsgId : undefined, e.data, newId("a"));
          if (admitted && "completed" in admitted) return {
            completedScheduledReplayByCompany: { ...s.completedScheduledReplayByCompany, [cid]: true },
          };
          if (!admitted) return {
            completedScheduledReplayByCompany: { ...s.completedScheduledReplayByCompany, [cid]: false },
          };
          return {
            completedScheduledReplayByCompany: { ...s.completedScheduledReplayByCompany, [cid]: false },
            messagesByCompany: { ...s.messagesByCompany, [cid]: admitted.messages },
            streamingMessageId: admitted.streamingMessageId,
            queueByCompany: admitted.unstagedQueuedHead ? {
              ...s.queueByCompany,
              [cid]: (s.queueByCompany[cid] ?? []).map((queued, index) => index === 0 ? { ...queued, dispatching: false } : queued),
            } : s.queueByCompany,
          };
        });
        break;
      }

      case "status": {
        // Mirror the live status into BOTH the singular active mirror AND the
        // per-company map. The Composer's `serverBusy` and the topbar pill read
        // from sessionStatusByCompany[cid] — previously only the 6s /all-status
        // poll (and the done-handler's optimistic clear) wrote there, so a live
        // busy=true (emitted by queue.ts tryDrain the moment a queued message
        // drains) took up to 6s to surface as "working". Writing it here makes
        // serverBusy the real-time backstop the architecture already intends
        // ("sent but not working" fix). Safe re: lingering-Stop: the ONLY
        // status event that carries busy=true is the drain emit, and every
        // drained turn is always followed by a `done` (optimistic busy=false),
        // so busy can never stick true after a turn truly ends.
        set((s) => {
          // A stream status event carries only the live lifecycle fields; the helper
          // counts come from the /all-status poll. Keep them until the next poll so
          // the "N helpers working" chip/note does not blink off on every event.
          const status = keepPolledHelperFields(e.data, s.sessionStatusByCompany[cid]);
          return {
            sessionStatus: status,
            sessionStatusByCompany: { ...s.sessionStatusByCompany, [cid]: status },
          };
        });
        get().applyCompaction(cid, e.data?.compaction);
        break;
      }
      case "thinking": {
        if (!targetMsgId) return;
        const { delta } = e.data;
        const startedAt = Date.now();
        set((s) => ({
          messagesByCompany: {
            ...s.messagesByCompany,
            [cid]: (s.messagesByCompany[cid] ?? []).map((m) => {
              if (m.id !== targetMsgId) return m;
              const prevThinking = m.thinking ?? "";
              return {
                ...m,
                thinking: prevThinking + delta,
                thinkingSeconds: m.thinkingSeconds
                  ? m.thinkingSeconds
                  : Math.max(1, Math.round((startedAt - new Date(m.createdAt).getTime()) / 1000)),
              };
            }),
          },
          thinkingExpandedByMsgId: {
            ...s.thinkingExpandedByMsgId,
            [targetMsgId]:
              s.thinkingExpandedByMsgId[targetMsgId] === undefined
                ? true
                : s.thinkingExpandedByMsgId[targetMsgId],
          },
        }));
        break;
      }
      case "message_delta": {
        if (!targetMsgId || flaggedOutOfTurn) {
          // Out-of-turn durable reply (see the exception above targetMsgId).
          // Already-present ids were dropped by the reconnect guard.
          const { delta, persistedMessageId, files, meta } = e.data;
          const hasFiles = Array.isArray(files) && files.length > 0;
          if (!outOfTurnReply || typeof persistedMessageId !== "string" || !persistedMessageId ||
              (!delta.trim() && !hasFiles)) return;
          const now = get();
          // The helper-results answer is in: drop the "reading results" state now rather
          // than at the next 6 s status poll.
          if (flaggedOutOfTurn && now.sessionStatusByCompany[cid]?.autoTurn) {
            set((s) => ({ sessionStatusByCompany: { ...s.sessionStatusByCompany,
              [cid]: { ...s.sessionStatusByCompany[cid]!, autoTurn: undefined } } }));
          }
          // A flagged reply racing this tab's own new turn: it precedes that
          // turn durably, so show it before the owner's new message and leave
          // the streaming placeholder (and all turn state) untouched.
          const liveMsgs = now.messagesByCompany[cid] ?? [];
          const placeholderAt = targetMsgId ? liveMsgs.findIndex((m) => m.id === targetMsgId) : -1;
          // A RECOVERY placeholder (restored-/resync-/recover-) is not a turn
          // this tab just sent: the backend only flags a reply out-of-turn when
          // no turn is running, so that placeholder's turn already ended. Fill
          // its empty slot in place (server order) and close the stale stream.
          if (flaggedOutOfTurn && placeholderAt >= 0 && /^(recover|restored|resync)-/.test(targetMsgId ?? "") &&
              now.sessionStatusByCompany[cid]?.busy !== true) {
            const reply: UIMessage = { id: persistedMessageId, role: "assistant", content: delta, tools: [],
              files: hasFiles ? files : undefined, meta, createdAt: nowIso() };
            set((s) => {
              const msgs = s.messagesByCompany[cid] ?? [];
              const at = msgs.findIndex((m) => m.id === targetMsgId);
              if (at < 0) return {};
              const stale = msgs[at]!;
              const empty = !stale.content && !stale.tools?.length && !stale.thinking && !stale.files?.length;
              const next = empty ? [...msgs.slice(0, at), reply, ...msgs.slice(at + 1)]
                : [...msgs.slice(0, at), { ...stale, isStreaming: false }, ...msgs.slice(at + 1), reply];
              return { messagesByCompany: { ...s.messagesByCompany, [cid]: next },
                streamingMessageId: s.streamingMessageId === targetMsgId ? null : s.streamingMessageId };
            });
            break;
          }
          if (flaggedOutOfTurn && placeholderAt >= 0) {
            const at = placeholderAt > 0 && liveMsgs[placeholderAt - 1]!.role === "user" ? placeholderAt - 1 : placeholderAt;
            const reply: UIMessage = { id: persistedMessageId, role: "assistant", content: delta, tools: [],
              files: hasFiles ? files : undefined, meta, createdAt: nowIso() };
            set((s) => ({ messagesByCompany: { ...s.messagesByCompany,
              [cid]: [...liveMsgs.slice(0, at), reply, ...liveMsgs.slice(at)] } }));
            break;
          }
          // A flagged helper-results answer is final and saved: show it now even while
          // another turn (e.g. sent from the phone) runs, rather than after that turn's done.
          if (!flaggedOutOfTurn &&
              (now.sessionStatusByCompany[cid]?.busy === true || (now.queueByCompany[cid]?.length ?? 0) > 0)) {
            settleHistoryAfterDone.add(cid);
            return;
          }
          const reply: UIMessage = {
            id: persistedMessageId,
            role: "assistant",
            content: delta,
            tools: [],
            files: hasFiles ? files : undefined,
            meta,
            createdAt: nowIso(),
          };
          set((s) => {
            // No turn is streaming here (streamingMessageId is null), so any
            // isStreaming bubble is a stale leftover (failed history read,
            // Stop). An empty one IS this reply's slot: replace it in place.
            // Finish the rest, so selectCompany's reply-count check cannot
            // restore one as a ghost "Working" bubble below this reply.
            const msgs = s.messagesByCompany[cid] ?? [];
            const slot = msgs.findLastIndex((m) => m.role === "assistant" && m.isStreaming &&
              !m.content && !m.tools?.length && !m.thinking && !m.files?.length);
            const next = msgs.map((m, i) => i === slot ? reply
              : m.role === "assistant" && m.isStreaming ? { ...m, isStreaming: false } : m);
            return { messagesByCompany: { ...s.messagesByCompany, [cid]: slot >= 0 ? next : [...next, reply] } };
          });
          break;
        }
        const { delta } = e.data;
        const persistedMessageId = e.data.persistedMessageId;
        // Reply attachments (the `reply` tool's files[]) now ride on the
        // message event so they render INLINE live. Merge (deduped) onto the
        // placeholder; this also keeps a files-only / empty-text reply from
        // counting as "empty" in the done-handler's skip guard (which would
        // otherwise strand Stop + stall the queue). Dedup guards a Mode-2
        // replay from re-adding the same path.
        const replyFiles = (e.data as { files?: string[] }).files;
        set((s) => {
          const nextMessageId = persistedMessageId || targetMsgId;
          const thinkingExpandedByMsgId = { ...s.thinkingExpandedByMsgId };
          if (persistedMessageId && persistedMessageId !== targetMsgId) {
            const expanded = thinkingExpandedByMsgId[targetMsgId];
            delete thinkingExpandedByMsgId[targetMsgId];
            if (expanded !== undefined) thinkingExpandedByMsgId[persistedMessageId] = expanded;
          }
          return {
            messagesByCompany: {
              ...s.messagesByCompany,
              [cid]: (s.messagesByCompany[cid] ?? []).map((m) => {
                if (m.id !== targetMsgId) return m;
                const nextFiles =
                  replyFiles && replyFiles.length > 0
                    ? Array.from(new Set([...(m.files ?? []), ...replyFiles]))
                    : m.files;
                // The backend `message` event carries the COMPLETE reply text
                // (channel.ts /reply + replyWatcher both emit one whole-text
                // message per turn), NOT an incremental delta — sse.ts maps the
                // whole `text` into `delta`. So this is idempotent: a re-delivery
                // (the epoch-reset replay window, where seq-dedup was just zeroed)
                // must NOT append a 2nd copy. Replace-with-longest: empty
                // placeholder+first delivery → content=delta; exact re-delivery →
                // unchanged; never truncate a longer prev. (Files stay additive +
                // deduped above.)
                const prev = m.content ?? "";
                const nextContent = delta.length >= prev.length ? delta : prev;
                // Response footer: stamp meta if this delivery carries it, else keep any
                // existing. A pure sibling of content/files — never gates emptiness (see
                // the done-handler isEmpty guard, which intentionally ignores meta).
                const nextMeta =
                  (e.data as { meta?: import("../lib/types").MessageMeta }).meta ?? m.meta;
                return {
                  ...m,
                  id: nextMessageId,
                  content: nextContent,
                  files: nextFiles,
                  meta: nextMeta,
                };
              }),
            },
            streamingMessageId:
              persistedMessageId && s.streamingMessageId === targetMsgId
                ? persistedMessageId
                : s.streamingMessageId,
            thinkingExpandedByMsgId,
          };
        });
        break;
      }
      case "tool_start": {
        if (!targetMsgId) return;
        const { tool } = e.data;
        set((s) => ({
          messagesByCompany: {
            ...s.messagesByCompany,
            [cid]: (s.messagesByCompany[cid] ?? []).map((m) =>
              m.id === targetMsgId
                ? { ...m, tools: [...(m.tools ?? []), tool] }
                : m,
            ),
          },
        }));
        break;
      }
      case "tool_update": {
        if (!targetMsgId) return;
        const { toolId, status, result, publicProgress } = e.data;
        set((s) => ({
          messagesByCompany: {
            ...s.messagesByCompany,
            [cid]: (s.messagesByCompany[cid] ?? []).map((m) => {
              if (m.id !== targetMsgId) return m;
              return {
                ...m,
                tools: (m.tools ?? []).map((t) =>
                  t.id === toolId ? { ...t, status, result, ...(publicProgress ? {publicProgress} : {}) } : t,
                ),
              };
            }),
          },
        }));
        break;
      }
      case "queue": {
        // Server-side queue indicator (multi-tab parity). Phase 1A queue is local-only.
        break;
      }
      case "done": {
        if (!targetMsgId) {
          // No active stream — done is informational only, except that a
          // reply dropped above (busy/queued, no placeholder) is recovered
          // read-only from durable history, prompt and reply in saved order.
          if (settleHistoryAfterDone.delete(cid)) void get().reconcileSettledHistory(cid);
          break;
        }
        // Wave 5 P0 audit: skip "done" if target placeholder has NO
        // content/tools/thinking yet. This is almost certainly a replayed
        // `done` from a PRIOR turn arriving on a freshly synthesized
        // placeholder via Mode-2 SSE replay. If we apply it, we'd flip
        // isStreaming=false and clear streamingMessageId — then the real
        // events for the CURRENT in-flight turn drop as orphans. The
        // self-heal reconciler eventually recreates a placeholder, but
        // user sees a 0-6s "empty Working" gap. Skipping here means the
        // placeholder waits for real content events to set its baseline.
        {
          const msgs0 = get().messagesByCompany[cid] ?? [];
          const target0 = msgs0.find((m) => m.id === targetMsgId);
          const isEmpty = target0 && !(
            (target0.content?.length ?? 0) > 0 ||
            (target0.tools?.length ?? 0) > 0 ||
            (target0.thinking?.length ?? 0) > 0 ||
            (target0.files?.length ?? 0) > 0
          );
          if (isEmpty) break;
        }
        const finishedTs = Date.now();
        set((s) => {
          const msgs = s.messagesByCompany[cid] ?? [];
          const target = msgs.find((m) => m.id === targetMsgId);
          const startMs = target ? new Date(target.createdAt).getTime() : finishedTs;
          const totalSec = Math.max(1, Math.round((finishedTs - startMs) / 1000));
          // UX fix (post-Wave 7): optimistically clear server's busy flag for
          // this cid + stamp lastDoneAt = now. Without this, sessionStatusByCompany
          // only refreshes every 6s via pollAllStatuses — so the composer's
          // Stop button (which is shown whenever `streamingMessageId || serverBusy`)
          // lingers visibly for up to 6 seconds after the reply has fully
          // landed. Julian: "after it fully responds, there's still an option
          // to pause — weird." Server is still truth on the next poll;
          // we're just preventing the visual lag.
          const existingStatus = s.sessionStatusByCompany[cid];
          const updatedStatusByCompany = existingStatus
            ? {
                ...s.sessionStatusByCompany,
                [cid]: { ...existingStatus, busy: false, lastDoneAt: finishedTs },
              }
            : s.sessionStatusByCompany;
          // ALSO update the active sessionStatus mirror if it's for this cid —
          // the topbar pill reads from there. Same optimistic update.
          const updatedActiveStatus =
            s.sessionStatus.companyId === cid
              ? { ...s.sessionStatus, busy: false, lastDoneAt: finishedTs }
              : s.sessionStatus;
          return {
            messagesByCompany: {
              ...s.messagesByCompany,
              [cid]: msgs.map((m) =>
                m.id === targetMsgId
                  ? {
                      ...m,
                      isStreaming: false,
                      thinkingSeconds: m.thinking ? totalSec : m.thinkingSeconds,
                    }
                  : m,
              ),
            },
            streamingMessageId:
              s.streamingMessageId === targetMsgId ? null : s.streamingMessageId,
            sessionStatusByCompany: updatedStatusByCompany,
            sessionStatus: updatedActiveStatus,
            thinkingExpandedByMsgId: {
              ...s.thinkingExpandedByMsgId,
              [targetMsgId]: false,
            },
          };
        });

        // Persist into mock store (no-op if not using mocks)
        if (__mock) {
          const final = get().messagesByCompany[cid]?.find((m) => m.id === targetMsgId);
          if (final) __mock.patchMessage(cid, targetMsgId, final);
        }
        // The turn closed but its reply text never reached this tab (the `message`
        // event was lost across a stream reconnect): fetch it now instead of waiting
        // for the next focus (29 Sep: "Updates saved… then boom the chat loads").
        {
          const closed = get().messagesByCompany[cid]?.find((m) => m.id === targetMsgId);
          if (closed && !closed.content?.trim() && !(closed.files?.length)) {
            // After this handler's own state changes (pending/queue) so the read is not
            // discarded as stale; retried twice if a concurrent poll still invalidated it.
            const recover = (attempt: number) => setTimeout(() => {
              void get().reconcileSettledHistory(cid).then((applied) => {
                const last = get().messagesByCompany[cid]?.at(-1);
                if (!applied && attempt < 2 && last?.role === "assistant" && !last.content?.trim() && !last.files?.length)
                  recover(attempt + 1);
              });
            }, attempt === 0 ? 0 : 1500);
            recover(0);
          }
        }

        // Clear pending/unread for this company IF the user is actively
        // viewing it — they've seen the reply land in real time. If
        // they're on another company, leave pending=true so the
        // sidebar dot keeps drawing their attention to it.
        {
          const active = get().activeCompanyId;
          if (active === cid) {
            const pending = { ...get().pendingByCompany };
            if (pending[cid]) {
              delete pending[cid];
              persistPendingByCompany(pending);
              set({ pendingByCompany: pending });
            }
          }
        }

        // Drain client-side queue. Two branches now:
        //   (a) serverQueued=true (new path, post-#94): the backend already
        //       has this message and will auto-drain it server-side. We just
        //       synthesize the next-turn placeholder so the incoming SSE
        //       events render somewhere visible. NO re-POST (would dupe).
        //   (b) serverQueued=false (legacy fallback / mock mode): re-call
        //       sendMessage so it POSTs as usual.
        get().materializeQueuedHead(cid);
        break;
      }
      case "error": {
        get().setToast(e.data.message, "error");
        // The local queue is cleared below. A chip the server had confirmed
        // queued is kept in the outbox so its status decides: delivered (it
        // drained), failed (flagged by the server), or "Not delivered — tap to
        // resend" (lost). Never dropped silently, never resent automatically.
        rememberDroppedQueued(get, cid, (get().queueByCompany[cid] ?? []).filter((q) => q.serverQueued && !q.dispatching));
        // Durably surface the failure in the TRANSCRIPT, not just a 4-second
        // toast that evaporates for a phone-only / backgrounded operator (this
        // is the stale-busy-watchdog / turn-error "no response" gap). Absorb the
        // error into the empty "Working" placeholder; if it already has partial
        // content (or there's no placeholder), append a standalone bubble.
        // De-dup against the orphan-reconciler's `err-session-ended-` bubble so
        // a session-death never stacks two. Reuses the same UIMessage shape.
        const errText =
          "⚠️ " +
          (e.data.message || "This turn failed.") +
          (/retry|resend|send.*again/i.test(e.data.message ?? "")
            ? ""
            : " — send it again to retry.");
        set((s) => {
          const existing = s.messagesByCompany[cid] ?? [];
          const alreadyFlagged = existing
            .slice(-3)
            .some(
              (m) =>
                m.role === "assistant" &&
                typeof m.id === "string" &&
                (m.id.startsWith("err-session-ended-") ||
                  m.id.startsWith("err-turn-failed-")),
            );
          let absorbed = false;
          const updated = existing.map((m) => {
            if (!m.isStreaming) return m;
            const hasContent = !!(m.content && m.content.trim());
            if (!hasContent && !absorbed) {
              absorbed = true; // the empty "Working" bubble becomes the error
              return { ...m, isStreaming: false, content: errText };
            }
            return { ...m, isStreaming: false };
          });
          const finalMsgs =
            absorbed || alreadyFlagged
              ? updated
              : [
                  ...updated,
                  {
                    id: `err-turn-failed-${Date.now()}-${Math.random()
                      .toString(36)
                      .slice(2, 6)}`,
                    role: "assistant",
                    content: errText,
                    isStreaming: false,
                    createdAt: nowIso(),
                  } as UIMessage,
                ];
          return {
            streamingMessageId: null,
            messagesByCompany: { ...s.messagesByCompany, [cid]: finalMsgs },
            // Clear the local queue — every backend path that emits `error` for
            // an in-flight turn also drops the server-side queue. Mirrors
            // interruptStream's queue clear.
            queueByCompany: { ...s.queueByCompany, [cid]: [] },
          };
        });
        break;
      }
    }
  },
}));

// Read-only snapshot for lib/autoUpdate.ts, which must know whether a reply is
// mid-stream before it reloads the page. Exposed on window rather than imported
// so autoUpdate stays decoupled from this store's shape -- if this ever goes
// missing, autoUpdate treats the state as unknown and shows the pill instead of
// silently reloading.
;(window as unknown as Record<string, unknown>).__RFLY_SESSION_STATE__ = () =>
  useSession.getState();
